{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            Copyright (c) 2018 - 2024                               }
{            Email : info@tmssoftware.com                            }
{            Web : http://www.tmssoftware.com                        }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.Grids;

{$DEFINE NOPP}

interface

{$HINTS OFF}

uses
  Classes, JS, WEBLib.Controls, WEBLib.Graphics, Web, DB, jsdelphisystem,
  WEBLib.Menus, WEBLib.StdCtrls, WEBLib.Mask, WEBLib.Ctrls.SVG, Types;

type
{
  TJSTable = class external name 'Table' (TJSNode)
  private
    FRows : TJSElement; external name 'rows';

  public

  end;
}
  TInitMethod = (imRandom, imLinear);

  TGridRect = record
    Left, Top, Right, Bottom: Longint;
  end;

  TGridCoord = record
    X: Longint;
    Y: Longint;
  end;

  TGridOption = (goFixedVertLine, goFixedHorzLine, goVertLine, goHorzLine,
    goRangeSelect, goDrawFocusSelected, goRowSizing, goColSizing, goRowMoving,
    goColMoving, goEditing, goTabs, goRowSelect, goAlwaysShowEditor,
    goThumbTracking, goFixedColClick, goFixedRowClick, goFixedHotTrack,goFixedRowDefAlign);

  TGridDrawState = set of (gdSelected, gdFocused, gdFixed, gdRowSelected,
    gdHotTrack, gdPressed);

  TGridSortIndicator = (siNone, siAscending, siDescending);

  TGridCellEditor = (geText, geNumber, geDate, geTime, geRange, geColor, geWeek, geMonth, geURL, geEmail, geTel, geMask, geCombo, geMemo, geNone, geCustom);

  TGridOptions = set of TGridOption;

  TGridGetCellDataEvent = procedure(Sender: TObject; ACol,ARow: integer; AField: TField; var AValue: string) of object;

  TGridGetCellClassEvent = procedure(Sender: TObject; ACol,ARow: integer; AField: TField; AValue: string; var AClassName: string) of object;

  TGridGetCellChildrenEvent = procedure(Sender: TObject; ACol,ARow: integer; AField: TField; AValue: string; AElement: TJSHTMLElementRecord) of object;

  TGridGetCellEditorEvent = procedure(Sender: TObject; ACol,ARow: integer; var AEditor: TGridCellEditor) of object;

  TGridRequestEvent = procedure(Sender: TObject; ARequest: TJSXMLHttpRequestRecord) of object;

  TGridSortClickEvent = procedure(Sender: TObject; ACol,ARow: integer; ASortIndicator: TGridSortIndicator) of object;

  TDrawCellEvent = procedure(Sender: TObject; ACol, ARow: Longint; Rect: TRect; State: TGridDrawState) of object;

  TGetEditEvent = procedure(Sender: TObject; ACol, ARow: Longint; var Value: string) of object;
  TSetEditEvent = procedure(Sender: TObject; ACol, ARow: Longint; const Value: string) of object;

  TSetEditControlValueEvent = procedure(Sender: TObject; ACol, ARow: Longint; AControl: TControl; const Value: string) of object;
  TGetEditControlValueEvent = procedure(Sender: TObject; ACol, ARow: Longint; AControl: TControl; var Value: string) of object;

  TCustomGrid = class(TWebCustomControl)
  private
    FRowCount: integer;
    FColCount: integer;
    function GetCells(ACol, ARow: integer): string; virtual;
    procedure SetCells(ACol, ARow: integer; const Value: string); virtual;
    function GetCellsArray: TJSArray;
    procedure SetCellsArray(const Value: TJSArray);
  protected
    function ColOffset: integer; virtual;
    function RowOffset: integer; virtual;
    procedure SetColCount(const Value: integer); virtual;
    procedure SetRowCount(const Value: integer); virtual;
    procedure GridChanged; virtual;
    procedure RowCountChanged(delta: integer); virtual;
    procedure ColCountChanged(delta: integer); virtual;
    property RowCount: integer read FRowCount write SetRowCount;
    property ColCount: integer read FColCount write SetColCount;
  public
    property CellsArray: TJSArray read GetCellsArray write SetCellsArray;
    property Visible;
    procedure Clear; virtual;
    property Cells[ACol,ARow: integer]: string read GetCells write SetCells;
    property BorderColor;
  end;

  // for VCL design-time compatibility
  TGridDrawingStyle = (gdsClassic, gdsThemed, gdsGradient);

  TCellEvent = procedure(Sender: TObject; ACol, ARow: integer) of object;
  TCellCheckEvent = procedure(Sender: TObject; ACol, ARow: integer; Checked: boolean) of object;
  TCellCanEditEvent = procedure(Sender: TObject; ACol,ARow: integer; var CanEdit: boolean) of object;

  TSelectCellEvent = procedure(Sender: TObject; ACol, ARow: integer; var CanSelect: boolean) of object;
  TColumnSizedEvent = procedure(Sender: TObject; ACol: integer) of object;

  TGridRange = class(TPersistent)
  private
    FMax: single;
    FMin: single;
    FStep: single;
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
  published
    property Min: single read FMin write FMin default 0;
    property Max: single read FMax write FMax default 100;
    property Step: single read FStep write FStep default 1;
  end;

  TObjectRow = TList;

  TCustomStringGrid = class(TCustomGrid, ITMSFNCDataBinderGrid)
  private
    FReq: TJSXMLHttpRequest;
    FDataNode: string;
    FOrigVal: string;
    FGrid: TJSElement;
    FFixedColor: TColor;
    FFixedColRow: TJSElement;
    FFixedRow: TJSElement;
    FFixedCol: TJSElement;
    FNormalCells: TJSElement;
    FDefaultColAlignment: TAlignment;
    FDefaultRowHeight: integer;
    FDefaultColWidth: integer;
    FColWidths: TList;
    FRowHeights: TList;
    FColAlignments: TList;
    FSelectedCell: TJSElement;
    FEdit: TJSHTMLElement;
    FFixedCols: integer;
    FFixedRows: integer;
    FEditMode: boolean;
    FBlockFocus: boolean;
    FOnSelectCell: TSelectCellEvent;
    FOnTopLeftChanged: TNotifyEvent;
    FOptions: TGridOptions;
    FDelimiter: char;
    FLoadFixed: boolean;
    FSelection: TGridRect;
    FStartCell: TGridCoord;
    FOnHttpRequestError: TNotifyEvent;
    FOnHttpRequestSuccess: TNotifyEvent;
    FOnGetCellClass: TGridGetCellClassEvent;
    FOnGetCellData: TGridGetCellDataEvent;
    FOnGetCellChildren: TGridGetCellChildrenEvent;
    FScrollBars: TScrollStyle;
    FSelMouseDown: boolean;
    FStyleElements: TStyleElements;
    FDefaultDrawing: boolean;
    FDragCursor: TCursor;
    FDragKind: TDragKind;
    FGradientStartColor: TColor;
    FGradientEndColor: TColor;
    FEditCol: integer;
    FEditRow: integer;
    FEditType: TGridCellEditor;
    FFixedFont: TFont;
    FCtl3D: boolean;
    FParentCtl3D: boolean;
    FOnGetEditText: TGetEditEvent;
    FOnSetEditText: TSetEditEvent;
    FOnHttpRequest: TGridRequestEvent;
    FOnCheckClick: TCellCheckEvent;
    FOnFixedCellClick: TCellEvent;
    FOnValidateEdit: TGetEditEvent;
    FHandleEditBlurPtr: pointer;
    FHandleEditKeypressPtr: pointer;
    FHandleCheckClickPtr: pointer;
    FHandleButtonClickPtr: pointer;
    FHandleMouseDownPtr: pointer;
    FHandleMouseMovePtr: pointer;
    FHandleMouseUpPtr: pointer;
    FHandleFocusPtr: pointer;
    FHandleBlurPtr: pointer;
    FHandleFixedClickPtr: pointer;
    FHandleClickPtr: pointer;
    FDrawingStyle: TGridDrawingStyle;
    FOnCanEditCell: TCellCanEditEvent;
    FOnColumnSized: TColumnSizedEvent;
    FRows: TStringList;
    FRowsIndex: integer;
    FOnGetCellEditor: TGridGetCellEditorEvent;
    FCombo: TWebComboBox;
    FMemo: TWebMemo;
    FMask: TWebMaskEdit;
    FEditMask: TEditMask;
    FComboBoxItems: TStrings;
    FRange: TGridRange;
    FShowSelection: boolean;
    FOnClickCell: TCellEvent;
    FElementTableClassName: string;
    FGridLineColor: TColor;
    FSelectionTextColor: TColor;
    FSelectionColor: TColor;
    FWordWrap: boolean;
    FOnButtonClick: TCellEvent;
    FSortIndex: integer;
    FSortDirection: TGridSortIndicator;
    FOnSortClick: TGridSortClickEvent;
    FHasMergedCells: boolean;
    FObjectRows: TList;
    FEditAdvance: boolean;
    FEditControl: TControl;
    FOrigID: string;
    FOnSetEditControlValue: TSetEditControlValueEvent;
    FOnGetEditControlValue: TGetEditControlValueEvent;
    function GetCells(ACol, ARow: integer): string; override;
    procedure SetCells(ACol, ARow: integer; const Value: string); override;
    procedure SetFixedCols(const Value: integer);
    procedure SetFixedRows(const Value: integer);
    procedure SetColor(AValue: TColor); override;
    function GetColWidths(ACol: integer): integer;
    function GetRowHeights(ARow: integer): integer;
    procedure SetColWidths(ACol: integer; const Value: integer);
    procedure SetRowHeights(ARow: integer; const Value: integer);
    procedure SetDefaultColWidth(const Value: integer);
    procedure SetDefaultRowHeight(const Value: integer);
    function GetCol: integer;
    function GetRow: integer;
    procedure SetCol(const Value: integer);
    procedure SetRow(const Value: integer);
    function GetCellElements(ACol, ARow: integer): TJSHTMLElement;
    function GetCheckElement(ACol, ARow: integer): TJSHTMLElement;
    function GetCanvasElement(ACol, ARow: integer): TJSHTMLElement;
    function GetButtonElement(ACol, ARow: integer): TJSHTMLElement;
    function GetProgressElement(ACol, ARow: integer): TJSHTMLElement;
    function GetSelection: TGridRect;
    procedure SetSelection(const Value: TGridRect);
    function GetLeftCol: integer;
    function GetTopRow: integer;
    procedure SetLeftCol(const Value: integer);
    procedure SetTopRow(const Value: integer);
    procedure SetFixedColor(const Value: TColor);
    procedure SetFixedTextColor(const Value: TColor);
    function GetFixedTextColor: TColor;
    function GetVisibleColCount: integer;
    function GetVisibleRowCount: integer;
    function GetRows(Index: integer): TStrings;
    procedure SetRows(Index: integer; const Value: TStrings);
    procedure RowsChanged(Sender: TObject);
    function GetCheckState(ACol, ARow: integer): boolean;
    procedure SetCheckState(ACol, ARow: integer; const Value: boolean);
    function GetCheckEnabled(ACol, ARow: integer): boolean;
    procedure SetCheckEnabled(ACol, ARow: integer; const Value: boolean);
    procedure SetRange(const Value: TGridRange);
    procedure SetElementTableClassName(const Value: string);
    procedure SetGridLineColor(const Value: TColor);
    procedure SetSelectionColor(const Value: TColor);
    procedure SetSelectionTextColor(const Value: TColor);
    function GetColAlignments(ACol: integer): TAlignment;
    procedure SetColAlignments(ACol: integer; const Value: TAlignment);
    procedure SetAlignAttr(AAlign: TAlignment; AElement: TJSElement);
    procedure SetSortIndex(const Value: integer);
    procedure SetDefaultColAlignment(const Value: TAlignment);
    function GetObjects(ACol, ARow: integer): TObject;
    procedure SetObjects(ACol, ARow: integer; const Value: TObject);
    procedure SetFixedFont(const Value: TFont);
  protected
    // ITMSFNCDataBinderGrid interface
    function GetDataRowCount: Integer;
    procedure ClearData;
    procedure SetDataColumnCount(AValue: Integer);
    procedure SetDataRowCount(AValue: Integer);
    procedure SetDataValue(AColumn, ARow: Integer; AValue: string);
    procedure SetDataHeader(AColumn: Integer; AValue: string);
    procedure DataBeginUpdate;
    procedure DataEndUpdate;
    procedure DataInsertRow(AInsertPosition: Integer);
    procedure FontChanged; override;
    procedure UnbindEvents; override;
    procedure FixedFontChanged(Sender: TObject); virtual;

    function StylePrefix: string;
    function ColOffset: integer; override;
    function RowOffset: integer; override;
    function CellColSpan(ACol,ARow: integer): integer;
    function CellRowSpan(ACol,ARow: integer): integer;
    function CellRealElement(const ACol,ARow: integer): TJSHTMLElement;
    procedure CellRealCoords(AElement: TJSHTMLElement;var ACol: integer; var ARow: integer);
    function GetDirectCellElements(ACol, ARow: integer): TJSHTMLElement;

    procedure MouseMove(Shift: TShiftState; X,Y: Integer); override;
    function HandleClick(Event: TJSEvent): Boolean; virtual;
    function HandleFixedClick(Event: TJSEvent): Boolean; virtual;
    function HandleMouseDown(Event: TJSMouseEvent): Boolean; virtual;
    function HandleMouseMove(Event: TJSMouseEvent): Boolean; virtual;
    function HandleMouseUp(Event: TJSMouseEvent): Boolean; virtual;
    function HandleKeyDown(Event: TJSKeyboardEvent): boolean; virtual;
    function HandleTableFocus(Event: TJSEvent): Boolean; virtual;
    function HandleTableBlur(Event: TJSEvent): Boolean; virtual;
    function HandleScroll(Event: TJSEvent): Boolean; virtual;
    function HandleEditBlur(Event: TJSEvent): Boolean; virtual;
    function HandleEditKeyPress(Event: TJSKeyboardEvent): boolean; virtual;

    procedure HandleComboExit(Sender: TObject);
    procedure HandleComboKeyPress(Sender: TObject; var Key: Char);
    procedure HandleMaskExit(Sender: TObject);
    procedure HandleMaskKeyPress(Sender: TObject; var Key: Char);
    procedure HandleMemoExit(Sender: TObject);
    procedure HandleMemoKeyPress(Sender: TObject; var Key: Char);

    function HandleCheckBoxClick(Event: TJSEvent): Boolean; virtual;
    function HandleButtonClick(Event: TJSEvent): Boolean; virtual;
    function HandleDoMouseUp(Event: TJSMouseEvent): Boolean; override;
    function HandleDoMouseMove(Event: TJSMouseEvent): Boolean; override;
    function HandleDoClick(Event: TJSMouseEvent): Boolean; override;
    function DoHttpLoadJson(Event: TEventListenerEvent): boolean; virtual;
    function DoHttpLoadCsv(Event: TEventListenerEvent): boolean; virtual;
    function DoHttpAbort(Event: TEventListenerEvent): boolean; virtual;
    procedure DoHttpRequest(ARequest: TJSXMLHttpRequest); virtual;
    procedure DoCheckClick(ACol, ARow: Longint; Checked: boolean); virtual;
    procedure DoButtonClick(ACol, ARow: Longint); virtual;
    procedure DoSortClick(ACol, ARow: Longint; ASortIndicator: TGridSortIndicator); virtual;
    function GetEditText(ACol, ARow: Longint): string; virtual;
    procedure SetEditText(ACol, ARow: Longint; const AValue: string); virtual;

    procedure SetEditControlValue(ACol,ARow: Longint; AControl: TControl; AValue: string); virtual;
    procedure GetEditControlValue(ACol,ARow: Longint; AControl: TControl; var AValue: string); virtual;
    procedure DoEditControlExit(Sender: TObject); virtual;

    procedure GetCellData(ACol,ARow: integer; AField: TField; var AValue: string); virtual;
    procedure GetCellClassName(ACol,ARow: integer; AField: TField; AValue: string; var AClassName: string); virtual;
    procedure GetCellChildren(ACol,ARow: integer; AField: TField; AValue: string; AElement: TJSHTMLElement); virtual;
    procedure GetCellEditor(const ACol,ARow: integer; var AEditor: TGridCellEditor); virtual;
    procedure AddSelection(ACell: TJSElement); virtual;
    procedure RemoveSelection(ACell: TJSElement); virtual;
    procedure BindCellChildren(ACol,ARow: integer; AElement: TJSHTMLElement); virtual;
    procedure UnBindCellChildren(ACol,ARow: integer; AElement: TJSHTMLElement); virtual;
    procedure DoBoundsChange; override;
    procedure DoColumnSized(AColumn: integer); virtual;
    procedure LoadFromStringList(AStrings: TStrings);
    function GetGridCSS(id: string): string; virtual;
    procedure RenderGrid;
    procedure ReRenderGrid;
    procedure RenderGridSize;
    procedure RenderGridScript;
    procedure UpdateGridSize;
    procedure UpdateGridAlignment;
    procedure BindEvents; override;
    procedure Resize; override;
    procedure DoEnter; override;
    procedure DoExit; override;
    procedure GridChanged; override;
    function CreateElement: TJSElement; override;
    procedure UpdateElement; override;
    procedure UpdateElementVisual; override;
    function FixedColWidth: integer;
    function FixedRowHeight: integer;
    function NormalColWidth: integer;
    function NormalRowHeight: integer;
    function FocusedCell: TJSElement;
    function CanSelect(ACol, ARow: integer): boolean; virtual;
    function CanRowAdvance: boolean; virtual;
    function HasSelection: boolean;
    procedure UpdateCell(ACol, ARow: integer; var AValue: string); virtual;
    procedure ValidateCell(ACol, ARow: integer; var AValue: string); virtual;
    procedure TopLeftChanged; virtual;
    procedure RowCountChanged(delta: integer); override;
    procedure ColCountChanged(delta: integer); override;
    procedure ClearMethodPointers; override;
    procedure GetMethodPointers; override;
    procedure StartEdit(ch: string); virtual;
    function StopEdit: string; virtual;
    function CanEditCell(ACol,ARow: integer): boolean; virtual;
    procedure ChangeRow; virtual;
    property ColCount;
    property RowCount;
    property OriginalValue: string read FOrigVal;
    property OnHttpRequestError: TNotifyEvent read FOnHttpRequestError write FOnHttpRequestError;
    property OnHttpRequestSuccess: TNotifyEvent read FOnHttpRequestSuccess write FOnHttpRequestSuccess;
    property OnHttpRequest: TGridRequestEvent read FOnHttpRequest write FOnHttpRequest;
    procedure EnableColResize;
    procedure EnableDrag; override;
    procedure DisableDrag; override;
    procedure BindCellButton(ACol,ARow: integer; AElement: TJSHTMLElement); virtual;
    procedure InitCSSLibrary(ALibrary: TCSSLibrary); override;
  public
    constructor Create(AOwner: TComponent); override; overload;
    destructor Destroy; override;
    procedure CreateInitialize; override;
    procedure SetFocus; override;
    procedure ShowEdit; virtual;
    procedure HideEdit(Advance: boolean = false); virtual;
    procedure CancelEdit; virtual;
    procedure Clear; override;
    procedure ClearSelection;
    property Rows[Index: integer]: TStrings read GetRows write SetRows;
    procedure InitSample(AInitMethod: TInitMethod);
    property Ctl3D: boolean read FCtl3D write FCtl3D;
    property ParentCtl3D: boolean read FParentCtl3D write FParentCtl3D;
    property Col: integer read GetCol write SetCol;
    property Row: integer read GetRow write SetRow;
    property Selection: TGridRect read GetSelection write SetSelection;
    property EditMode: boolean read FEditMode;
    property EditControl: TControl read FEditControl write FEditControl;
    property SortIndex: integer read FSortIndex write SetSortIndex;
    property SortDirection: TGridSortIndicator read FSortDirection;
    procedure Sort(AColumn: integer; ADirection: TGridSortIndicator);
    procedure MouseToCell(X,Y: integer; var C, R: integer);
    procedure LoadFromJSON(const AURL: string; ADataNode: string = ''); overload;
    procedure LoadFromJSON(AJSON: TJSObject; ADataNode: string = ''); overload;
    function LoadFromJSONAsync(const AURL: string; ADataNode: string = ''): TJSPromise;
    procedure LoadFromCSV(const AURL: string; Delimiter: char = ';'; LoadFixed: boolean = false);
    function LoadFromCSVAsync(const AURL: string; Delimiter: char = ';'; LoadFixed: boolean = false): TJSPromise;
    procedure LoadFromStrings(AStrings: TStrings; Delimiter: char = ';'; LoadFixed: boolean = false);
    procedure SaveToCSV(const AFileName: string; Delimiter: char = ';'; SaveFixed: boolean = false);
    procedure SaveToStrings(AStrings: TStrings; Delimiter: char = ';'; SaveFixed: boolean = false);
    procedure SelectCell(ACol, ARow: integer); virtual;
    procedure InsertRow(const Index: integer);
    procedure RemoveRow(const Index: integer);
    procedure InsertColumn(const Index: integer);
    procedure RemoveColumn(const Index: integer);
    property CellElements[ACol,ARow: integer]: TJSHTMLElement read GetCellElements;
    property ColAlignments[ACol: integer]: TAlignment read GetColAlignments write SetColAlignments;
    property ColWidths[ACol: integer]: integer read GetColWidths write SetColWidths;
    property RowHeights[ARow: integer]: integer read GetRowHeights write SetRowHeights;
    property FixedColRow: TJSElement read FFixedColRow;
    property FixedCol: TJSElement read FFixedCol;
    property FixedRow: TJSElement read FFixedRow;
    property TopRow: integer read GetTopRow write SetTopRow;
    property LeftCol: integer read GetLeftCol write SetLeftCol;
    property VisibleRowCount: integer read GetVisibleRowCount;
    property VisibleColCount: integer read GetVisibleColCount;

    procedure AddCheckBox(const ACol,ARow: integer; AState: boolean = false);
    procedure RemoveCheckBox(const ACol,ARow: integer);
    function HasCheckBox(const ACol,ARow: integer): boolean;
    procedure AddButton(ACol,ARow: integer; AText: string; AStyle: string = '');
    procedure RemoveButton(const ACol,ARow: integer);
    function HasButton(const ACol,ARow: integer): boolean;

    procedure AddCanvas(const ACol,ARow: integer);
    procedure RemoveCanvas(const ACol,ARow: integer);
    function HasCanvas(const ACol,ARow: integer): boolean;
    function GetCanvas(const ACol,ARow: integer): TJSHTMLCanvasElement;

    procedure AddSortIndicator(const ACol,ARow: integer; AIndicator: TGridSortIndicator);
    procedure SelectSortIndicator(const ACol,ARow: integer; ASelect: boolean);
    procedure SetSortIndicator(const ACol,ARow: integer; AIndicator: TGridSortIndicator);
    function HasSortIndicator(const ACol, ARow: integer): boolean;
    procedure RemoveSortIndicator(const ACol, ARow: integer);

    procedure AddProgress(ACol,ARow: integer; APosition: integer; AStyle: string = '');
    procedure SetProgress(ACol,ARow: integer; APosition: integer);
    procedure RemoveProgress(const ACol,ARow: integer);
    function HasProgress(const ACol,ARow: integer): boolean;

    function HasVertScrollBar: boolean; override;
    function HasHorzScrollBar: boolean; override;
    property CheckState[ACol,ARow: integer]: boolean read GetCheckState write SetCheckState;
    property CheckEnabled[ACol,ARow: integer]: boolean read GetCheckEnabled write SetCheckEnabled;

    procedure MergeCells(const ACol,ARow, NumCol,NumRow: integer);
    procedure SplitCells(const ACol,ARow: integer);
    function IsMergedCell(const ACol,ARow: integer; var NumCol,NumRow: integer): boolean;

    property ComboBoxItems: TStrings read FComboBoxItems;
    property Objects[ACol,ARow: integer]: TObject read GetObjects write SetObjects;

    // for design-time VCL class compatibility
    property DefaultDrawing: boolean read FDefaultDrawing write FDefaultDrawing;
    property DragCursor: TCursor read FDragCursor write FDragCursor;
    property DragKind: TDragKind read FDragKind write FDragKind;
    property DrawingStyle: TGridDrawingStyle read FDrawingStyle write FDrawingStyle;
    property GradientEndColor: TColor read FGradientEndColor write FGradientEndColor;
    property GradientStartColor: TColor read FGradientStartColor write FGradientStartColor;
    property FixedTextColor: TColor read GetFixedTextColor write SetFixedTextColor default clBlack;
  published
    property Align;
    property AlignWithMargins;
    property Anchors;
    property BiDiMode;
    property BorderColor;
    property BorderStyle;
    property Color default clWindow;
    property DragMode;
    property DefaultColAlignment: TAlignment read FDefaultColAlignment write SetDefaultColAlignment default taLeftJustify;
    property DefaultColWidth: integer read FDefaultColWidth write SetDefaultColWidth default 64;
    property DefaultRowHeight: integer read FDefaultRowHeight write SetDefaultRowHeight default 24;
    property EditAdvance: boolean read FEditAdvance write FEditAdvance default false;
    property EditMask: TEditMask read FEditMask write FEditMask;

    property ElementID;
    property ElementClassName;
    property ElementFont;
    property ElementPosition;
    property ElementTableClassName: string read FElementTableClassName write SetElementTableClassName;
    property FixedRows: integer read FFixedRows write SetFixedRows default 1;
    property FixedCols: integer read FFixedCols write SetFixedCols default 1;
    property FixedColor: TColor read FFixedColor write SetFixedColor default clBtnFace;
    property FixedFont: TFont read FFixedFont write SetFixedFont;
    property Font;
    property GridLineColor: TColor read FGridLineColor write SetGridLineColor default clSilver;
    property Height;
    property HeightPercent;
    property HeightStyle;
    property Margins;
    property Options: TGridOptions read FOptions write FOptions;
    property ParentFont;
    property ParentBiDiMode;
    property PopupMenu;
    property RangeEdit: TGridRange read FRange write SetRange;
    property Scrollbars: TScrollStyle read FScrollBars write FScrollBars default ssBoth;
    property SelectionColor: TColor read FSelectionColor write SetSelectionColor default $FF8F5B;
    property SelectionTextColor: TColor read FSelectionTextColor write SetSelectionTextColor default $FFFFFF;
    property ShowSelection: boolean read FShowSelection write FShowSelection default true;
    property StyleElements: TStyleElements read FStyleElements write FStyleElements;
    property TabOrder;

    property Visible;
    property Width;
    property WidthPercent;
    property WidthStyle;
    property WordWrap: boolean read FWordWrap write FWordWrap default false;

    property OnClick;
    property OnClickCell: TCellEvent read FOnClickCell write FOnClickCell;
    property OnDblClick;
    property OnEnter;
    property OnExit;
    property OnMouseWheelDown;
    property OnMouseWheelUp;
    property OnCanEditCell: TCellCanEditEvent read FOnCanEditCell write FOnCanEditCell;
    property OnColumnSized: TColumnSizedEvent read FOnColumnSized write FOnColumnSized;
    property OnGetCellData: TGridGetCellDataEvent read FOnGetCellData write FOnGetCellData;
    property OnGetCellChildren: TGridGetCellChildrenEvent read FOnGetCellChildren write FOnGetCellChildren;
    property OnGetCellClass: TGridGetCellClassEvent read FOnGetCellClass write FOnGetCellClass;
    property OnGetCellEditor: TGridGetCellEditorEvent read FOnGetCellEditor write FOnGetCellEditor;
    property OnButtonClick: TCellEvent read FOnButtonClick write FOnButtonClick;
    property OnCheckClick: TCellCheckEvent read FOnCheckClick write FOnCheckClick;
    property OnFixedCellClick: TCellEvent read FOnFixedCellClick write FOnFixedCellClick;
    property OnGetEditText: TGetEditEvent read FOnGetEditText write FOnGetEditText;
    property OnSetEditText: TSetEditEvent read FOnSetEditText write FOnSetEditText;

    property OnSetEditControlValue: TSetEditControlValueEvent read FOnSetEditControlValue write FOnSetEditControlValue;
    property OnGetEditControlValue: TGetEditCOntrolValueEvent read FOnGetEditControlValue write FOnGetEditControlValue;

    property OnSelectCell: TSelectCellEvent read FOnSelectCell write FOnSelectCell;
    property OnSortClick: TGridSortClickEvent read FOnSortClick write FOnSortClick;
    property OnTopLeftChanged: TNotifyEvent read FOnTopLeftChanged write FOnTopLeftChanged;
    property OnValidateEdit: TGetEditEvent read FOnValidateEdit write FOnValidateEdit;
    property OnDragDrop;
    property OnDragOver;
    property OnEndDrag;
    property OnStartDrag;
  end;

  TStringGrid = class(TCustomStringGrid)
  published
    property ColCount;
    property RowCount;
    property OnHttpRequestError;
    property OnHttpRequestSuccess;
  end;

  TWebStringGrid = class(TStringGrid);

  TCustomTableControl = class;

  TTablePager = (tpNone, tpDropDown, tpLink, tpButton, tpList, tpPrevNext);
  TTablePagerAlign = (paRight, paCenter, paLeft);

  TTableControlHeader = class(TPersistent)
  private
    FPageEventPtr: pointer;
    FFilterEventPtr: pointer;
    FSearchEventPtr: pointer;
    FOwner: TCustomTableControl;
    FElement: TJSHTMLElement;
    FPagerElement: TJSHTMLElement;
    FSearchElement: TJSHTMLElement;
    FFilterElement: TJSHTMLElement;
    FSearchInput: TJSHTMLElement;
    FFilterInput: TJSHTMLElement;
    FVisible: boolean;
    FCaption: string;
    FSearchValue: string;
    FFilterValue: string;
    FOnChange: TNotifyEvent;
    FPager: TTablePager;
    FButtonElementClassName: TElementClassName;
    FLinkElementClassName: TElementClassName;
    FDropDownElementClassName: TElementClassName;
    FListElementClassName: TElementClassName;
    FListItemElementClassName: TElementClassName;
    FInputElementClassName: TElementClassName;
    FFilterColumn: integer;
    FSearch: boolean;
    FFilter: boolean;
    FButtonActiveElementClassName: TElementClassName;
    FLinkActiveElementClassName: TElementClassName;
    FListLinkElementClassName: TElementClassName;
    FPagerAlign: TTablePagerAlign;
    procedure SetCaption(const Value: string);
    procedure SetVisible(const Value: boolean);
    procedure SetPager(const Value: TTablePager);
    procedure SetPagerAlign(const Value: TTablePagerAlign);
  protected
    procedure Changed; virtual;
    procedure Update(PageSize, RowCount, ActivePage: integer);
    function HandlePagerClick(Event: TJSInputEvent): Boolean; virtual;
    function HandleSearchClick(Event: TJSInputEvent): Boolean; virtual;
    function HandleFilterClick(Event: TJSInputEvent): Boolean; virtual;
  public
    constructor Create(AOwner: TCustomTableControl);
    procedure Assign(Source: TPersistent); override;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
    property Element: TJSHTMLElement read FElement;
  published
    property FilterColumn: integer read FFilterColumn write FFilterColumn default 0;
    property Filter: boolean read FFilter write FFilter default false;
    property Search: boolean read FSearch write FSearch default false;
    property ButtonActiveElementClassName: TElementClassName read FButtonActiveElementClassName write FButtonActiveElementClassName;
    property ButtonElementClassName: TElementClassName read FButtonElementClassName write FButtonElementClassName;
    property DropDownElementClassName: TElementClassName read FDropDownElementClassName write FDropDownElementClassName;
    property InputElementClassName: TElementClassName read FInputElementClassName write FInputElementClassName;
    property LinkActiveElementClassName: TElementClassName read FLinkActiveElementClassName write FLinkActiveElementClassName;
    property LinkElementClassName: TElementClassName read FLinkElementClassName write FLinkElementClassName;
    property ListElementClassName: TElementClassName read FListElementClassName write FListElementClassName;
    property ListLinkElementClassName: TElementClassName read FListLinkElementClassName write FListLinkElementClassName;
    property ListItemElementClassName: TElementClassName read FListItemElementClassName write FListItemElementClassName;
    property Caption: string read FCaption write SetCaption;
    property Pager: TTablePager read FPager write SetPager;
    property PagerAlign: TTablePagerAlign read FPagerAlign write SetPagerAlign default paRight;
    property Visible: boolean read FVisible write SetVisible default false;
  end;

  TTableControlPaging = class(TPersistent)
  private
    FOwner: TCustomTableControl;
    FSize: integer;
    FIndex: integer;
    FEnabled: boolean;
    FActivePage: integer;
    FPageCount: integer;
    procedure SetIndex(const Value: integer);
    procedure SetSize(const Value: integer);
    procedure SetEnabled(const Value: boolean);
  public
    constructor Create(AOwner: TCustomTableControl);
    procedure Assign(Source: TPersistent); override;
    property ActivePage: integer read FActivePage;
    property PageCount: integer read FPageCount;
  published
    property Enabled: boolean read FEnabled write SetEnabled default false;
    property Index: integer read FActivePage write SetIndex default 0;
    property Size: integer read FSize write SetSize default 10;
  end;

  TTableControlOptions = class(TPersistent)
  private
    FOwner: TCustomTableControl;
    FScrollVertical: boolean;
    FResizeColumns: boolean;
    FAutoCellURL: boolean;
    FAutoCellEmail: boolean;
    FAutoCellImage: boolean;
    FCellBorderColor: TColor;
    FCellBorders: boolean;
    FAutoNumAlign: boolean;
    FImageAlign: TAlignment;
    FImageWidth: integer;
    procedure SetResizeColumns(const Value: boolean);
    procedure SetCellBorderColor(const Value: TColor);
    procedure SetCellBorders(const Value: boolean);
    procedure SetScrollVertical(const Value: boolean);
  public
    constructor Create(AOwner: TCustomTableControl);
    procedure Assign(Source: TPersistent); override;
  published
    property AutoCellEmail: boolean read FAutoCellEmail write FAutoCellEmail default false;
    property AutoCellURL: boolean read FAutoCellURL write FAutoCellURL default false;
    property AutoCellImage: boolean read FAutoCellImage write FAutoCellImage default false;
    property AutoNumAlign: boolean read FAutoNumAlign write FAutoNumAlign default false;
    property CellBorders: boolean read FCellBorders write SetCellBorders default false;
    property CellBorderColor: TColor read FCellBorderColor write SetCellBorderColor default clSilver;
    property ImageAlign: TAlignment read FImageAlign write FImageAlign default taLeftJustify;
    property ImageWidth: integer read FImageWidth write FImageWidth default 0;
    property ScrollVertical: boolean read FScrollVertical write SetScrollVertical default false;
    property ResizeColumns: boolean read FResizeColumns write SetResizeColumns default false;
  end;

  TPageSelectEvent = procedure(Sender: TObject; NewPage: integer; var Allow: boolean; var DefaultSelect: boolean) of object;

  TCustomTableControl = class(TCustomGrid, ITMSFNCDataBinderGrid)
  private
    FReq: TJSXMLHttpRequest;
    FDataNode: string;
    FData: JSValue;
    FUnfiltered: JSValue;
    FTable: TJSElement;
    FBody: TJSElement;
    FRowHeader: boolean;
    FColHeader: boolean;
    FDelimiter: char;
    FLoadFixed: boolean;
    FFindCol, FFindRow: integer;
    FFindCase: boolean;
    FFindCondition: string;
    FElementHeaderClassName: TElementClassName;
    FElementTableClassName: TElementClassName;
    FOnHttpRequestError: TNotifyEvent;
    FOnHttpRequestSuccess: TNotifyEvent;
    FOnDblClickCell: TCellEvent;
    FOnClickCell: TCellEvent;
    FOptions: TTableControlOptions;
    FOnGetCellClass: TGridGetCellClassEvent;
    FOnGetCellData: TGridGetCellDataEvent;
    FOnGetCellChildren: TGridGetCellChildrenEvent;
    FOnHttpRequest: TGridRequestEvent;
    FOnCheckClick: TCellCheckEvent;
    FOnButtonClick: TCellEvent;
    FHandleCheckClickPtr: pointer;
    FHandleButtonClickPtr: pointer;
    FOnSortClick: TGridSortClickEvent;
    FSortIndex: integer;
    FSortDirection: TGridSortIndicator;
    FElementSelectionClassName: TElementClassName;
    FElementRowSelectClassName: TElementClassName;
    FRowIndex: integer;
    FSelectionTextColor: TColor;
    FSelectionColor: TColor;
    FHeader: TTableControlHeader;
    FFooter: TTableControlHeader;
    FPaging: TTableControlPaging;
    FOnSelectPage: TPageSelectEvent;
    FHiddenCols: TStringList;
    FWordWrap: boolean;
    function GetCells(col, row: integer): string; override;
    procedure SetCells(col, row: integer; const Value: string); override;
    function GetRowClassName(row: integer): TElementClassName;
    procedure SetRowClassName(row: integer; const Value: TElementClassName);
    function GetCellElement(col, row: integer): TJSHTMLElement;
    function GetCheckElement(ACol, ARow: integer): TJSHTMLElement;
    function GetButtonElement(ACol, ARow: integer): TJSHTMLElement;
    function GetProgressElement(ACol, ARow: integer): TJSHTMLElement;
    procedure SetOptions(const Value: TTableControlOptions);
    procedure SetElementHeaderClassName(const Value: TElementClassName);
    procedure SetElementTableClassName(const Value: TElementClassName);
    procedure SetColHeader(const Value: boolean);
    procedure SetRowHeader(const Value: boolean);
    procedure SetSortIndex(const Value: integer);
    procedure SetRowIndex(const Value: integer);
    procedure SetSelectionColor(const Value: TColor);
    procedure SetSelectionTextColor(const Value: TColor);
    procedure SetFooter(const Value: TTableControlHeader);
    procedure SetHeader(const Value: TTableControlHeader);
    procedure SetPaging(const Value: TTableControlPaging);
    procedure SetTableCell(ACol,ARow: integer; ACell: TJSHTMLElement; const Value: string);
    function GetColWidth(const ACol: integer): integer;
    procedure SetColWidth(const ACol: integer; AWidth: integer);
    procedure SetWordWrap(const AValue: boolean);
  protected
    // ITMSFNCDataBinderGrid interface
    function GetDataRowCount: Integer;
    procedure ClearData;
    procedure SetDataColumnCount(AValue: Integer);
    procedure SetDataRowCount(AValue: Integer);
    procedure SetDataValue(AColumn, ARow: Integer; AValue: string);
    procedure SetDataHeader(AColumn: Integer; AValue: string);
    procedure DataBeginUpdate;
    procedure DataEndUpdate;
    procedure DataInsertRow(AInsertPosition: Integer);
    procedure ClearMethodPointers; override;
    procedure GetMethodPointers; override;
    function HandleDoKeyPress(Event: TJSKeyBoardEvent): Boolean; override;

    function ColOffset: integer; override;
    function RowOffset: integer; override;

    function HandleDoClick(Event: TJSMouseEvent): Boolean; override;
    function HandleDoDblClick(Event: TJSMouseEvent): Boolean; override;
    function HandleCheckBoxClick(Event: TJSEvent): Boolean; virtual;
    function HandleButtonClick(Event: TJSEvent): Boolean; virtual;
    function HandleDoMouseUp(Event: TJSMouseEvent): Boolean; override;
    function HandleDoMouseMove(Event: TJSMouseEvent): Boolean; override;
    function HandleDoMouseDown(Event: TJSMouseEvent): Boolean; override;
    procedure DoCheckClick(ACol, ARow: Longint; Checked: boolean); virtual;
    procedure DoButtonClick(ACol, ARow: integer); virtual;
    procedure DoSortClick(ACol, ARow: Longint; ASortIndicator: TGridSortIndicator); virtual;
    function DoHttpLoadJson(Event: TEventListenerEvent): boolean; virtual;
    function DoHttpLoadPageJson(Event: TEventListenerEvent): boolean; virtual;
    function DoHttpLoadCsv(Event: TEventListenerEvent): boolean; virtual;
    function DoHttpAbort(Event: TEventListenerEvent): boolean; virtual;
    procedure DoHttpRequest(ARequest: TJSXMLHttpRequest); virtual;
    procedure GetCellData(ACol,ARow: integer; AField: TField; var AValue: string); virtual;
    procedure GetCellClassName(ACol,ARow: integer; AField: TField; AValue: string; var AClassName: string); virtual;
    procedure GetCellChildren(ACol,ARow: integer; AField: TField; AValue: string; AElement: TJSHTMLElement); virtual;
    procedure BindCellCheckBox(ACol,ARow: integer; AElement: TJSHTMLElement); virtual;
    procedure BindCellButton(ACol,ARow: integer; AElement: TJSHTMLElement); virtual;
    procedure RenderGrid;
    procedure ReRenderGrid;
    procedure RenderGridBorders;
    procedure RenderGridData;
    procedure RenderGridScript;
    procedure RenderHeaderFooter;
    procedure BindEvents; override;
    procedure KeyDown(var Key: Word; Shift: TShiftState); override;
    function CreateElement: TJSElement; override;
    procedure UpdateElement; override;
    procedure SetRowCount(const Value: integer); override;
    procedure SetColCount(const Value: integer); override;
    procedure RowCountChanged(delta: integer); override;
    procedure ColCountChanged(delta: integer); override;
    procedure LoadFromStringList(AStrings: TStrings);
    property OnHttpRequestError: TNotifyEvent read FOnHttpRequestError write FOnHttpRequestError;
    property OnHttpRequestSuccess: TNotifyEvent read FOnHttpRequestSuccess write FOnHttpRequestSuccess;
    property OnHttpRequest: TGridRequestEvent read FOnHttpRequest write FOnHttpRequest;
    property RowCount;
    property ColCount;
    procedure EnableColResize;
    procedure EnableDrag; override;
    procedure DisableDrag; override;
    function FindFrom(ACol,ARow: integer; Condition: string; CaseSensitive: boolean = true; AllCells: boolean = false): TGridCoord;
    function RenderRowCount: integer;
    function InVisiblePage(var ARow: integer): boolean;
    procedure DoSelectPage(AIndex: integer);
    procedure SelectPage(AIndex: integer; DoDefault: boolean = true);
    procedure Loaded; override;
    procedure InitCSSLibrary(ALibrary: TCSSLibrary); override;
  public
    constructor Create(AOwner: TComponent); override; overload;
    procedure CreateInitialize; override;
    destructor Destroy; override;
    procedure Invalidate; override;
    procedure LogData;
    procedure InitSample(AInitMethod: TInitMethod);
    procedure LoadFromJSON(const AURL: string; ADataNode: string = ''); overload;
    procedure LoadFromJSON(AJSON: TJSObject; ADataNode: string = ''); overload;
    function LoadFromJSONAsync(const AURL: string; ADataNode: string = ''): TJSPromise;
    procedure LoadPageFromJSON(const AURL: string; ADataNode: string = ''); overload;
    procedure LoadPageFromJSON(AJSON: TJSObject; ADataNode: string = ''); overload;
    function LoadPageFromJSONAsync(const AURL: string; ADataNode: string = ''): TJSPromise;
    procedure LoadFromCSV(const AURL: string; Delimiter: char = ';'; LoadFixed: boolean = false);
    function LoadFromCSVAsync(const AURL: string; Delimiter: char = ';'; LoadFixed: boolean = false): TJSPromise;
    procedure LoadFromStrings(AStrings: TStrings; Delimiter: char = ';'; LoadFixed: boolean = false);
    procedure SaveToCSV(const AFileName: string; Delimiter: char = ';'; SaveFixed: boolean = false);
    procedure SaveToStrings(AStrings: TStrings; Delimiter: char = ';'; SaveFixed: boolean = false);
    procedure AddCheckBox(ACol, ARow: integer; Checked: boolean = false);
    procedure RemoveCheckBox(ACol, ARow: integer);
    procedure AddCheckBoxColumn(ACol: integer; Checked: boolean = false);
    function HasCheckBox(ACol,ARow: integer): boolean;
    procedure AddButton(ACol,ARow: integer; AText: string; AStyle: string = '');
    procedure AddButtonColumn(ACol: integer; AText: string; AStyle: string = '');
    procedure RemoveButton(ACol,ARow: integer);
    function HasButton(ACol,ARow: integer): boolean;

    procedure AddSortIndicator(const ACol,ARow: integer; AIndicator: TGridSortIndicator);
    procedure SelectSortIndicator(const ACol,ARow: integer; ASelect: boolean);
    procedure SetSortIndicator(const ACol,ARow: integer; AIndicator: TGridSortIndicator);
    function HasSortIndicator(const ACol, ARow: integer): boolean;
    procedure RemoveSortIndicator(const ACol, ARow: integer);

    procedure AddProgress(ACol,ARow: integer; APosition: integer; AStyle: string = '');
    procedure SetProgress(ACol,ARow: integer; APosition: integer);
    procedure RemoveProgress(const ACol,ARow: integer);
    function HasProgress(const ACol,ARow: integer): boolean;

    procedure Clear; override;
    property CellElements[col,row: integer]: TJSHTMLElement read GetCellElement;
    property ColWidths[col: integer]: integer read GetColWidth write SetColWidth;
    property RowClassName[row: integer]: TElementClassName read GetRowClassName write SetRowClassName;
    procedure InsertRow(Index: integer);
    procedure RemoveRow(Index: integer);

    procedure SetFilter(Column: integer; Condition: string; CaseSensitive: boolean = true);
    procedure RemoveFilter;
    procedure ScrollRowInView(ARow: integer);
    procedure SelectCell(ACol,ARow: integer);
    function FindCell(Condition: string; CaseSensitive: boolean = true; AllCells: boolean = false): TGridCoord;
    function FindNext: TGridCoord;

    procedure HideColumn(const Index: integer);
    procedure UnHideColumn(const Index: integer);
    function IsHiddenColumn(const Index: integer): boolean;
    procedure HideRow(const Index: integer);
    procedure UnHideRow(const Index: integer);

    procedure Sort(AColumn: integer; ADirection: TGridSortIndicator);
    property SortIndex: integer read FSortIndex write SetSortIndex;
    property SortDirection: TGridSortIndicator read FSortDirection;
    property RowIndex: integer read FRowIndex write SetRowIndex;
  published
    property Align;
    property AlignWithMargins;
    property Anchors;
    property BorderColor;
    property BorderStyle;
    property Color;
    property DragMode;
    property ElementClassName;
    property ElementFont;
    property ElementID;
    property ElementPosition;
    property ElementHeaderClassName: TElementClassName read FElementHeaderClassName write SetElementHeaderClassName;
    property ElementRowSelectClassName: TElementClassName read FElementRowSelectClassName write FElementRowSelectClassName;
    property ElementSelectionClassName: TElementClassName read FElementSelectionClassName write FElementSelectionClassName;
    property ElementTableClassName: TElementClassName read FElementTableClassName write SetElementTableClassName;
    property Enabled default false;
    property Font;
    property Footer: TTableControlHeader read FFooter write SetFooter;
    property Header: TTableControlHeader read FHeader write SetHeader;
    property Height;
    property HeightPercent;
    property HeightStyle;
    property Margins;
    property RowHeader: boolean read FRowHeader write SetRowHeader default true;
    property ColHeader: boolean read FColHeader write SetColHeader default true;
    property Options: TTableControlOptions read FOptions write SetOptions;
    property Paging: TTableControlPaging read FPaging write SetPaging;
    property ParentBiDiMode;
    property PopupMenu;
    property SelectionColor: TColor read FSelectionColor write SetSelectionColor default $FF8F5B;
    property SelectionTextColor: TColor read FSelectionTextColor write SetSelectionTextColor default clWhite;
    property Visible;
    property Width;
    property WidthPercent;
    property WidthStyle;
    property WordWrap: boolean read FWordWrap write SetWordWrap default false;
    property OnButtonClick: TCellEvent read FOnButtonClick write FOnButtonClick;
    property OnCheckClick: TCellCheckEvent read FOnCheckClick write FOnCheckClick;
    property OnClick;
    property OnClickCell: TCellEvent read FOnClickCell write FOnClickCell;
    property OnDblClickCell: TCellEvent read FOnDblClickCell write FOnDblClickCell;
    property OnDblClick;
    property OnGetCellData: TGridGetCellDataEvent read FOnGetCellData write FOnGetCellData;
    property OnGetCellChildren: TGridGetCellChildrenEvent read FOnGetCellChildren write FOnGetCellChildren;
    property OnGetCellClass: TGridGetCellClassEvent read FOnGetCellClass write FOnGetCellClass;
    property OnSortClick: TGridSortClickEvent read FOnSortClick write FOnSortClick;
    property OnDragDrop;
    property OnDragOver;
    property OnEndDrag;
    property OnSelectPage: TPageSelectEvent read FOnSelectPage write FOnSelectPage;
    property OnStartDrag;
  end;

  TTableControl = class(TCustomTableControl)
  published
    property RowCount;
    property ColCount;
    property OnHttpRequestError;
    property OnHttpRequestSuccess;
  end;

  TWebTableControl = class(TTableControl);

  TResponsiveGridItem = class(TCollectionItem)
  private
    FHTML: string;
    FElementHandle: TJSHTMLElement;
    FTag: integer;
    FItemObject: TObject;
    FVisible: boolean;
    FJSONObject: JSValue;
    FSelected: boolean;
    FRecNo: integer;
    procedure SetHTML(const Value: string);
    procedure SetVisible(const Value: boolean);
    function GetJSONElementValue(id: string): string;
    procedure SetSelected(const Value: boolean);
  protected
    property RecNo: integer read FRecNo write FRecNo;
  public
    constructor Create(ACollection: TCollection); override;
    destructor Destroy; override;
    property ElementHandle: TJSHTMLElement read FElementHandle;
    property ItemObject: TObject read FItemObject write FItemObject;
    property JSONElement: JSValue read FJSONObject write FJSONObject;
    property JSONElementValue[id: string]: string read GetJSONElementValue;
    property Selected: boolean read FSelected write SetSelected;
  published
    property HTML: string read FHTML write SetHTML;
    property Tag: integer read FTag write FTag default 0;
    property Visible: boolean read FVisible write SetVisible default true;
  end;

  TResponsiveGridItemEvent = procedure(Sender: TObject; Index: integer) of object;

  TResponsiveGridItemTemplateEvent = procedure(Sender: TObject; Index: integer; ItemData: string; var ATemplate: string) of object;

  TResponsiveGridItemFieldValueEvent = procedure(Sender: TObject; Index: integer; AFieldName: string; var AValue: string) of object;

  TResponsiveGrid = class;

  TResponsiveGridItems = class(TOwnedCollection)
  private
    FOnItemDelete: TResponsiveGridItemEvent;
    FOnItemUpdate: TResponsiveGridItemEvent;
    FOnItemInsert: TResponsiveGridItemEvent;
    FOnItemAdd: TResponsiveGridItemEvent;
    FOnItemClear: TNotifyEvent;
    FGrid: TResponsiveGrid;
    function GetItem(Index: integer): TResponsiveGridItem;
    procedure SetItem(Index: integer; const Value: TResponsiveGridItem);
  protected
    procedure DoItemClear; virtual;
    procedure DoItemAdd(Index: integer); virtual;
    procedure DoItemDelete(Index: integer); virtual;
    procedure DoItemInsert(Index: integer); virtual;
    procedure DoItemUpdate(Index: integer); virtual;
    property OnItemAdd: TResponsiveGridItemEvent read FOnItemAdd write FOnItemAdd;
    property OnItemUpdate: TResponsiveGridItemEvent read FOnItemUpdate write FOnItemUpdate;
    property OnItemDelete: TResponsiveGridItemEvent read FOnItemDelete write FOnItemDelete;
    property OnItemInsert: TResponsiveGridItemEvent read FOnItemInsert write FOnItemInsert;
    property OnItemClear: TNotifyEvent read FOnItemClear write FOnItemClear;
    procedure Update(Item: TCollectionItem); override;
    property Grid: TResponsiveGrid read FGrid;
  public
    constructor Create(AOwner: TComponent);
    procedure Clear;
    procedure Delete(Index: integer);
    function Add: TResponsiveGridItem; overload;
    function Add(HTML: string): TResponsiveGridItem; overload;
    function Insert(Index: integer): TResponsiveGridItem;
    property Items[Index: integer]: TResponsiveGridItem read GetItem write SetItem; default;
  end;

  TResponsiveGridTemplateOrder = (toColumn, toRow);

  TResponsiveGridOptions = class(TPersistent)
  private
    FItemWidthMin: integer;
    FItemHeight: integer;
    FItemGap: integer;
    FScrollVertical: boolean;
    FItemClassName: string;
    FItemColor: TColor;
    FItemBorderColor: TColor;
    FItemHoverColor: TColor;
    FItemHoverBorderColor: TColor;
    FOnChange: TNotifyEvent;
    FItemPadding: integer;
    FItemTemplate: string;
    FItemSelectedBorderColor: TColor;
    FItemSelectedTextColor: TColor;
    FItemSelectedColor: TColor;
    FMultiSelect: boolean;
    FOrder: TResponsiveGridTemplateOrder;
    procedure SetItemBorderColor(const Value: TColor);
    procedure SetItemColor(const Value: TColor);
    procedure SetItemGap(const Value: integer);
    procedure SetItemHeight(const Value: integer);
    procedure SetItemHoverBorderColor(const Value: TColor);
    procedure SetItemHoverColor(const Value: TColor);
    procedure SetItemWidthMin(const Value: integer);
    procedure SetScrollVertical(const Value: boolean);
    procedure SetItemPadding(const Value: integer);
    procedure SetItemTemplate(const Value: string);
    procedure SetItemSelectedBorderColor(const Value: TColor);
    procedure SetItemSelectedColor(const Value: TColor);
    procedure SetItemSelectedTextColor(const Value: TColor);
  protected
    procedure Changed;
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
  published
    property ItemClassName: string read FItemClassName write FItemClassName;
    property ItemColor: TColor read FItemColor write SetItemColor default clNone;
    property ItemBorderColor: TColor read FItemBorderColor write SetItemBorderColor default clNone;
    property ItemGap: integer read FItemGap write SetItemGap default 10;
    property ItemHeight: integer read FItemHeight write SetItemHeight default 100;
    property ItemHoverColor: TColor read FItemHoverColor write SetItemHoverColor default clNone;
    property ItemHoverBorderColor: TColor read FItemHoverBorderColor write SetItemHoverBorderColor default clNone;
    property ItemSelectedColor: TColor read FItemSelectedColor write SetItemSelectedColor default clNone;
    property ItemSelectedBorderColor: TColor read FItemSelectedBorderColor write SetItemSelectedBorderColor default clNone;
    property ItemSelectedTextColor: TColor read FItemSelectedTextColor write SetItemSelectedTextColor default clNone;
    property ItemPadding: integer read FItemPadding write SetItemPadding default 0;
    property ItemTemplate: string read FItemTemplate write SetItemTemplate;
    property ItemWidthMin: integer read FItemWidthMin write SetItemWidthMin default 100;
    property MultiSelect: boolean read FMultiSelect write FMultiSelect default false;
    property Order: TResponsiveGridTemplateOrder read FOrder write FOrder default toColumn;
    property ScrollVertical: boolean read FScrollVertical write SetScrollVertical default True;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
  end;

  TResponsiveGrid = class(TWebCustomControl)
  private
    FDesignTime: boolean;
    FReq: TJSXMLHttpRequest;
    FDataNode: string;
    FOptions: TResponsiveGridOptions;
    FItems: TResponsiveGridItems;
    FOnItemDblClick: TResponsiveGridItemEvent;
    FOnItemClick: TResponsiveGridItemEvent;
    FOnHttpRequestError: TNotifyEvent;
    FOnHttpRequestSuccess: TNotifyEvent;
    FOnItemCreated: TResponsiveGridItemEvent;
    FOnItemGetFieldValue: TResponsiveGridItemFieldValueEvent;
    FItemIndex: integer;
    FDelimiter: char;
    FOnHttpRequest: TGridRequestEvent;
    FListElement: TJSHTMLElement;
    FOnItemGetTemplate: TResponsiveGridItemTemplateEvent;
    procedure SetOptions(const Value: TResponsiveGridOptions);
    procedure SetItems(const Value: TResponsiveGridItems);
    procedure SetItemIndex(const Value: integer);
  protected
    function DoHttpLoadJson(Event: TEventListenerEvent): boolean; virtual;
    function DoHttpLoadCsv(Event: TEventListenerEvent): boolean; virtual;
    function DoHttpAbort(Event: TEventListenerEvent): boolean; virtual;
    procedure DoHttpRequest(ARequest: TJSXMLHttpRequest); virtual;
    procedure DoItemCreated(Index: integer); virtual;
    procedure DoItemGetFieldValue(Index: integer; AFieldName: string; var AValue: string); virtual;
    procedure DoItemGetTemplate(Index: integer; ItemData: string; var ATemplate: string); virtual;

    procedure HandleDoItemClick(Index: integer); virtual;
    procedure HandleDoItemDblClick(Index: integer); virtual;
    function TargetIsParent(tgt,el: TJSElement): boolean; virtual;
    function HandleDoMouseEnter(Event: TJSMouseEvent): Boolean; override;
    function HandleDoClick(Event: TJSMouseEvent): Boolean; override;
    function HandleDoDblClick(Event: TJSMouseEvent): Boolean; override;
    procedure HandleItemAdd(Sender:  TObject; Index: integer);
    procedure HandleItemUpdate(Sender:  TObject; Index: integer);
    procedure HandleItemDelete(Sender:  TObject; Index: integer);
    procedure HandleItemInsert(Sender:  TObject; Index: integer);
    procedure HandleItemClear(Sender: TObject);
    procedure OptionsChanged(Sender: TObject); virtual;
    procedure FontChanged; override;
    function CreateElement: TJSElement; override;
    procedure CreateControl; override;
    procedure BindElement; override;
    procedure UpdateElement; override;
    procedure RenderGrid;
    procedure Loaded; override;
    function GetStyleName: string;
    procedure InitCSSLibrary(ALibrary: TCSSLibrary); override;
  public
    constructor Create(AOwner: TComponent); override; overload;
    procedure CreateInitialize; override;
    destructor Destroy; override;
    procedure LoadFromJSON(const AURL: string; ADataNode: string = ''); overload;
    procedure LoadFromJSON(ASJON: TJSObject; ADataNode: string = ''); overload;
    procedure LoadFromCSV(const AURL: string; Delimiter: char = ';');
    function ListElementHandle: TJSElement;
    function ItemByTag(ATag: integer): TResponsiveGridItem;
  published
    property Align;
    property AlignWithMargins;
    property Anchors;
    property ElementID;
    property ElementClassName;
    property ElementFont;
    property ElementPosition;
    property Font;
    property Height;
    property HeightPercent;
    property HeightStyle;

    property ItemIndex: integer read FItemIndex write SetItemIndex default -1;
    property Items: TResponsiveGridItems read FItems write SetItems;
    property Margins;
    property Options: TResponsiveGridOptions read FOptions write SetOptions;
    property ParentFont;
    property PopupMenu;
    property Visible;
    property Width;
    property WidthPercent;
    property WidthStyle;
    property OnClick;
    property OnDblClick;
    property OnMouseDown;
    property OnMouseUp;
    property OnMouseMove;
    property OnMouseLeave;
    property OnMouseEnter;
    property OnItemCreated: TResponsiveGridItemEvent read FOnItemCreated write FOnItemCreated;
    property OnItemGetFieldValue: TResponsiveGridItemFieldValueEvent read FOnItemGetFieldValue write FOnItemGetFieldValue;
    property OnItemGetTemplate: TResponsiveGridItemTemplateEvent read FOnItemGetTemplate write FOnItemGetTemplate;
    property OnItemClick: TResponsiveGridItemEvent read FOnItemClick write FOnItemClick;
    property OnItemDblClick: TResponsiveGridItemEvent read FOnItemDblClick write FOnItemDblClick;
    property OnHttpRequestError: TNotifyEvent read FOnHttpRequestError write FOnHttpRequestError;
    property OnHttpRequestSuccess: TNotifyEvent read FOnHttpRequestSuccess write FOnHttpRequestSuccess;
    property OnHttpRequest: TGridRequestEvent read FOnHttpRequest write FOnHttpRequest;
  end;

  TWebResponsiveGrid = class(TResponsiveGrid);

function GridRect(Left,Top,Right,Bottom: integer): TGridRect;

implementation

uses
  SysUtils, Math, WEBLib.Utils, WEBLib.WebTools, WEBLib.Forms;

const
  GlyphAscending = '&#x25B2;';
  GlyphDescending = '&#x25BC;';
  CELLPADDING = 0;

function GridRect(Left,Top,Right,Bottom: integer): TGridRect;
begin
  Result.Left := Left;
  Result.Top := Top;
  Result.Right := Right;
  Result.Bottom := Bottom;
end;

{ TCustomStringGrid }

procedure TCustomStringGrid.AddButton(ACol, ARow: integer; AText,
  AStyle: string);
var
  s, ct: string;
  el: TJSHTMLElement;
begin
  if (ARow >= 0) and (ACol >= 0) and not HasCheckBox(ACol,ARow) then
  begin
    ct := Cells[ACol, ARow];
    if ct <> '' then
      ct := '&nbsp;' + ct;

    s := '<BUTTON type="button" style="vertical-align:middle"';

    if AStyle <> '' then
      s := s + ' class="'+AStyle+'"';

    s := s + '>'+AText+'</BUTTON>';

    Cells[ACol,ARow] := s + ct;

    el := CellElements[ACol, ARow];
    BindCellButton(ACol, ARow, el);
  end;
end;

procedure TCustomStringGrid.AddCheckBox(const ACol, ARow: integer; AState: boolean = false);
var
  chk: TJSElement;
begin
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    if not HasCheckBox(ACol,ARow) then
    begin
      chk := document.createElement('INPUT');
      chk['type'] := 'checkbox';

      asm
      chk.checked = AState;
      end;

      CellElements[ACol,ARow].insertBefore(chk, CellElements[ACol,ARow].firstChild);
      chk.addEventListener('click',FHandleCheckClickPtr);
    end;
  end;
end;

procedure TCustomStringGrid.AddCanvas(const ACol, ARow: integer);
var
  el: TJSHTMLElement;
  w,h: integer;
begin
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    if not HasCanvas(ACol,ARow) then
    begin
      w := ColWidths[ACol];
      h := RowHeights[ARow];
      el := TJSHTMLElement(document.createElement('CANVAS'));
      el['width'] := w.toString+'px';
      el['height'] := (h-1).toString+'px';
      el.style.setProperty('display','block');
      CellElements[ACol,ARow].insertBefore(el, CellElements[ACol,ARow].firstChild);
    end;
  end;

end;

procedure TCustomStringGrid.AddProgress(ACol, ARow, APosition: integer;
  AStyle: string);
var
  prog: TJSElement;
begin
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    if not HasProgress(ACol,ARow) then
    begin
      prog := document.createElement('PROGRESS');
      prog['max'] := '100';
      prog['value'] := APosition.toString;
      TJSHTMLElement(prog).style.setProperty('width','100%');
      if AStyle <> '' then
        prog['class'] := AStyle;
      CellElements[ACol,ARow].insertBefore(prog, CellElements[ACol,ARow].firstChild);
    end;
  end;
end;

procedure TCustomStringGrid.AddSelection(ACell: TJSElement);
begin
  if ShowSelection then
  begin
    ACell.classlist.Add('selected');
  end;
end;

procedure TCustomStringGrid.AddSortIndicator(const ACol, ARow: integer;
  AIndicator: TGridSortIndicator);
var
  s,sid: string;
  sp: integer;
begin
  s := Cells[ACol,ARow];
  sp := pos('<span', s);

  if sp > 0 then
    s := Copy(s, 1, sp - 1);

  sid := Name + 'si'+ACol.ToString+'_'+ARow.ToString;

  case AIndicator of
  siNone: Cells[ACol,ARow] := s;
  siAscending: Cells[ACol,ARow] := s + '<span style="cursor:pointer;float:right" id="' + sid + '" sort="up">'+GlyphAscending+'</span>';
  siDescending: Cells[ACol,ARow] := s + '<span style="cursor:pointer;float:right" id="'+ sid + '" sort="dn">'+GlyphDescending+'</span>';
  end;
end;

procedure TCustomStringGrid.BindCellButton(ACol, ARow: integer;
  AElement: TJSHTMLElement);
var
  el: TJSHTMLElement;
begin
  el := TJSHTMLElement(AElement.firstChild);
  if Assigned(el) and (el.tagName = 'BUTTON') then
  begin
    el.addEventListener('click', FHandleButtonClickPtr);
  end;
end;

procedure TCustomStringGrid.BindCellChildren(ACol, ARow: integer; AElement: TJSHTMLElement);
var
  el: TJSHTMLElement;
begin
  el := TJSHTMLElement(AElement.firstChild);

  if Assigned(el) and (el.tagName = 'INPUT') then
  begin
    if el.hasAttribute('type') and (lowercase(el.getAttribute('TYPE'))= 'checkbox') then
    begin
      el.addEventListener('click', FHandleCheckClickPtr);
    end;
  end;
end;

procedure TCustomStringGrid.UnBindCellChildren(ACol, ARow: integer; AElement: TJSHTMLElement);
var
  el: TJSHTMLElement;
begin
  el := TJSHTMLElement(AElement.firstChild);

  if Assigned(el) and (el.tagName = 'INPUT') then
  begin
    if el.hasAttribute('type') and (lowercase(el.getAttribute('TYPE'))= 'checkbox') then
    begin
      el.removeEventListener('click', FHandleCheckClickPtr);
    end;
  end;
end;


procedure TCustomStringGrid.BindEvents;
begin
  inherited;
  RenderGridScript;
end;

procedure TCustomStringGrid.CancelEdit;
var
  eh: TJSHTMLElement;
begin
  if EditMode then
  begin
    if FEditType = geCombo then
    begin
      eh := FCombo.ElementHandle;
    end
    else
    if FEditType = geMemo then
    begin
      eh := FMemo.ElementHandle;
    end
    else
    if FEditType = geMask then
    begin
      eh := FMask.ElementHandle;
    end
    else
    begin
      eh := FEdit;
    end;

    FEditMode := false;

    ValidateCell(FEditCol, FEditRow, FOrigVal);

    if FSelectedCell.contains(eh) then
      FSelectedCell.removeChild(eh);

    FSelectedCell.innerHTML := FOrigVal;
    UpdateCell(FEditCol, FEditRow, FOrigVal);

    if not (goRowSelect in Options) then
    begin
      AddSelection(FSelectedCell);
    end;

    SetEditText(FEditCol, FEditRow, FOrigVal);

    FBlockFocus := true;

    TJSHTMLElement(FNormalCells).focus();
  end;
end;

function TCustomStringGrid.CanEditCell(ACol, ARow: integer): boolean;
var
  CanEdit: boolean;
begin
  CanEdit := true;
  if Assigned(OnCanEditCell) then
    OnCanEditCell(Self, ACol, ARow, CanEdit);

  Result := CanEdit;
end;

function TCustomStringGrid.CanRowAdvance: boolean;
begin
  Result := true;
end;

function TCustomStringGrid.CanSelect(ACol, ARow: integer): boolean;
begin
  Result := true;
  if Assigned(OnSelectCell) then
    OnSelectCell(Self, ACol, ARow, Result);
end;

function TCustomStringGrid.CellColSpan(ACol, ARow: integer): integer;
var
  el: TJSHTMLElement;
  s: string;
  e: integer;
begin
  Result := 0;

  el := CellRealElement(ACol,ARow);
  if Assigned(el) then
  begin
    s := el['colspan'];
    val(s,Result,e);
  end;
end;


function TCustomStringGrid.CellRealElement(const ACol, ARow: integer):TJSHTMLElement;
var
  el: TJSHTMLElement;
  t: TJSElement;
  c,r: integer;
begin
  if (ACol < FixedCols) and (ARow < FixedRows) then
  begin
    t := FFixedColRow;
    c := ACol;
    r := ARow;
  end
  else
  if (ACol < FixedCols) and (ARow >= FixedRows) then
  begin
    t := FFixedCol;
    c := ACol;
    r := ARow - FixedRows;
  end
  else
  if (ACol >= FixedCols) and (ARow < FixedRows) then
  begin
    t := FFixedRow;
    c := ACol - FixedCols;
    r := ARow;
  end
  else
  begin
    t := FNormalCells;
    c := ACol - FixedCols;
    r := ARow - FixedRows;
  end;

  el := nil;

  if FHasMergedCells then
  begin
    asm
      var getTableCell = function (table, x, y) {
      var m = [], row, cell, xx, tx, ty, xxx, yyy;
      for(yyy = 0; yyy < table.rows.length; yyy++) {
          row = table.rows[yyy];
          for(xxx = 0; xxx < row.cells.length; xxx++) {
              cell = row.cells[xxx];
              xx = xxx;
              for(; m[yyy] && m[yyy][xx]; ++xx) {}
              for(tx = xx; tx < xx + cell.colSpan; ++tx) {
                  for(ty = yyy; ty < yyy + cell.rowSpan; ++ty) {
                      if (!m[ty])
                          m[ty] = [];
                      m[ty][tx] = true;
                  }
              }
              if (xx <= x && x < xx + cell.colSpan && yyy <= y && y < yyy + cell.rowSpan)
                  return cell;
          }
        }
      return null;
      };
      el = getTableCell(t, c, r);
    end;
  end
  else
  begin
    asm
      el = t.rows[r].cells[c];
    end;
  end;

  Result := el;
end;

procedure TCustomStringGrid.CellRealCoords(AElement: TJSHTMLElement;var ACol,ARow: integer);
var
  allcells: array of array of TJSHTMLElement;
  el: TJSHTMLElement;
  i,j: integer;
begin
  ACol := -1;
  ARow := -1;

  SetLength(allcells, ColCount, RowCount);

  for i := 0 to ColCount - 1 do
  begin
    for j := 0 to RowCount - 1 do
    begin
      allcells[i,j] := CellRealElement(i,j);
    end;
  end;

  for i := 0 to ColCount - 1 do
  begin
    for j := 0 to RowCount - 1 do
    begin
      if allcells[i,j] = AElement then
      begin
        ACol := i - FixedCols;
        ARow := j - FixedRows;
        break;
      end;
    end;
  end;
end;

function TCustomStringGrid.CellRowSpan(ACol, ARow: integer): integer;
var
  el: TJSHTMLElement;
  s: string;
  e: integer;
begin
  Result := 0;

  el := CellRealElement(ACol,ARow);
  if Assigned(el) then
  begin
    s := el['rowspan'];
    val(s,Result,e);
  end;
end;

procedure TCustomStringGrid.ChangeRow;
begin
  //
end;

procedure TCustomStringGrid.Clear;
var
  c,r: integer;
begin
  for c := 0 to ColCount - 1 do
    for r := 0 to Rowcount - 1 do
    begin
      if HasButton(c,r) then
        RemoveButton(c,r);
      if HasCheckBox(c,r) then
        RemoveCheckBox(c,r);

      Cells[c,r] := '';
    end;
end;

procedure TCustomStringGrid.ClearData;
begin
  Clear;
  RowCount := FixedRows + 5;
  ColCount := FixedCols + 5;
end;

procedure TCustomStringGrid.ClearMethodPointers;
begin
  inherited;
  FHandleEditBlurPtr := nil;
  FHandleEditKeypressPtr := nil;
  FHandleCheckClickPtr := nil;
  FHandleButtonClickPtr := nil;
  FHandleMouseDownPtr := nil;
  FHandleMouseUpPtr := nil;
  FHandleMouseMovePtr := nil;
  FHandleFixedClickPtr := nil;
  FHandleBlurPtr := nil;
  FHandleFocusPtr := nil;
  FHandleClickPtr := nil;
end;


procedure TCustomStringGrid.ClearSelection;
var
  rselect: boolean;
  tr: TJSElement;
begin
  rselect := (goRowSelect in Options);

  if Assigned(FSelectedCell) and rselect then
  begin
    tr := FSelectedCell.parentElement;
    RemoveSelection(tr);
  end;

  Selection := GridRect(0,0,0,0);

  if Assigned(FSelectedCell) then
  begin
    RemoveSelection(FSelectedCell);
  end;

end;

procedure TCustomStringGrid.ColCountChanged(delta: integer);
var
  c: integer;
begin
  if IsUpdating and (csLoading in ComponentState) then
    Exit;

  if not Assigned(FNormalCells) then
    Exit;

  if delta > 0 then
  begin

    asm
      var i = 0;
      var j = 0;
      var k = 0;
      var tr = undefined;
      var td = undefined;
      var sizediv,div = undefined;

      var l = this.FNormalCells.rows.length;

      for(j = 0; j < l; j++)
      {
        tr = this.FNormalCells.rows[j];

        k = tr.cells.length;

         for(i = 0; i < delta; i++)
         {
           tr.insertCell(k);
         }
      }

      for(j = 0; j < this.FFixedRows; j++)
      {
        k = this.FFixedRow.rows[j].cells.length;

        for(i = 0; i < delta; i++)
        {
          td = this.FFixedRow.rows[j].insertCell(k);
          td.setAttribute('class','fixed');
          div = document.createElement("DIV");
          div.style = 'position:relative;height:100%;width:100%';
          td.appendChild(div);
          sizediv = document.createElement("DIV");
          sizediv.style = 'position:absolute;height:100%;width:5px;margin-right:-5px;left:100%;top:0px;cursor:w-resize;z-index:999;';
          div.appendChild(sizediv);
        }
      }
    end;

    for c := 1 to delta do
    begin
      ColWidths[ColCount - c] := DefaultColWidth;
    end;

    if DragMode = dmAutomatic then
      EnableDrag;
  end;

  if delta < 0 then
  begin

    asm
      var i = 0;
      var j = 0;
      var k = 0;
      var tr = undefined;

      var l = this.FNormalCells.rows.length;

      for(j = 0; j < l; j++)
      {
        tr = this.FNormalCells.rows[j];

        k = tr.cells.length;

         for(i = 0; i < -delta; i++)
         {
           tr.deleteCell(k - i - 1);
         }
      }

      if (this.FFixedRows > 0) {

      k = this.FFixedRow.rows[0].cells.length;

      for(i = 0; i < -delta; i++)
      {
        this.FFixedRow.rows[0].deleteCell(k- i - 1);
      }
      }
    end;

    if ColCount > 0 then
      ColWidths[ColCount - 1] := ColWidths[ColCount - 1];
  end;
end;

function TCustomStringGrid.ColOffset: integer;
begin
  Result := FixedCols;
end;

constructor TCustomStringGrid.Create(AOwner: TComponent);
begin
  inherited;
  DrawingStyle := gdsThemed;
end;

function TCustomStringGrid.CreateElement: TJSElement;
begin
  Result := document.createElement('SPAN');
  FGrid := nil;
end;

procedure TCustomStringGrid.CreateInitialize;
begin
  inherited;
  FEditAdvance := false;
  FColWidths := TList.Create;
  FRowHeights := TList.Create;
  FColAlignments := TList.Create;
  FObjectRows := TList.Create;
  FRows := TStringList.Create;
  FRows.OnChange := RowsChanged;
  FComboBoxItems := TStringList.Create;
  FRange := TGridRange.Create;
  FHasMergedCells := false;
  FDefaultColAlignment := taLeftJustify;
  FStartCell.X := -1;
  FStartCell.Y := -1;
  FFixedColor := clBtnFace;
  FRowCount := 5;
  FColCount := 5;
  FColor := clWindow;
  FFixedCols := 1;
  FFixedRows := 1;
  FDefaultColWidth := 64;
  FDefaultRowHeight := 22;
  FShowSelection := true;
  ElementFont := efProperty;
  ElementPosition := epAbsolute;
  ScrollBars := ssBoth;
  FGridLineColor := clSilver;
  FSelectionColor := $FF8F5B;
  FSelectionTextColor := $FFFFFF;
  Options := [goHorzLine, goVertLine, goRangeSelect];
  FFixedFont := TFont.Create;
  FFixedFont.OnChange := FixedFontChanged;
  if (csDesigning in ComponentState) then
  begin
    Width := 400;
    Height := 300;
  end;
end;

procedure TCustomStringGrid.DataBeginUpdate;
begin
end;

procedure TCustomStringGrid.DataEndUpdate;
begin
end;

procedure TCustomStringGrid.DataInsertRow(AInsertPosition: Integer);
begin
  InsertRow(AInsertPosition);
end;

destructor TCustomStringGrid.Destroy;
var
  i: integer;
  LObjr: TObjectRow;
begin
  FRange.Free;
  FColWidths.Free;
  FColAlignments.Free;
  FRowHeights.Free;
  FRows.Free;
  FFixedFont.Free;

  for i := 0 to FObjectRows.Count - 1 do
  begin
    LObjr := TObjectRow(FObjectRows[i]);
    LObjr.Free;
  end;
  FObjectRows.Clear;

  FObjectRows.Free;
  FComboBoxItems.Free;
  if Assigned(FCombo) then
    FCombo.Free;
  if Assigned(FMask) then
    FMask.Free;
  inherited;
end;

procedure TCustomStringGrid.DisableDrag;
  procedure RemoveDraggableAttribute(AElement: TJSElement);
  var
    I: Integer;
  begin
    for I := 0 to AElement.children.length - 1 do
    begin
      if TJSElement(AElement.children[I]).tagName = 'TD' then
      begin
        TJSElement(AElement.children[I]).removeAttribute('draggable');
        RemoveDraggableAttribute(TJSElement(AElement.children[I]));
      end
      else
        RemoveDraggableAttribute(TJSElement(AElement.children[I]));
    end;
  end;
begin
  if not Assigned(Container) then
    Exit;

  RemoveDraggableAttribute(Container);
end;

function TCustomStringGrid.DoHttpLoadCsv(Event: TEventListenerEvent): boolean;
var
  sl: TStringList;
  req: TJSXMLHttpRequest;
begin
  req := TJSXMLHttpRequest(Event.target);

  if (req.status >= 200) and (req.status <= 208) then
  begin
    sl := TStringList.Create;

    sl.Text := req.responseText;

    try
      LoadFromStringList(sl);
    finally
      sl.Free;
    end;

    if Assigned(OnHttpRequestSuccess) then
      OnHttpRequestSuccess(Self);
  end;

  Result := true;
end;

procedure TCustomStringGrid.LoadFromJSON(AJSON: TJSObject; ADataNode: string = '');
var
  JO: JSValue;
  JA: TJSArray;
  r,c: integer;
  arow: string;
  sl: TStringList;
  ce: TJSHTMLElement;
  cd, cn: string;

begin
  if ADataNode <> '' then
    JA := TJSArray(AJSON.Properties[ADataNode])
  else
    JA := TJSArray(AJSON);

  if Assigned(JA) then
  begin
    sl := TStringList.Create;

    Clear;

    RowCount := JA.length +  FixedRows;

    for r := 0 to JA.length - 1 do
    begin
      JO := JA.Elements[r];

      c := 0;
      arow := '';

      asm
        var x = undefined;
        for (x in JO) {
          arow += "|" + JO[x];
          c++;
        }
      end;

      Delete(arow, 1, 1);
      sl.StrictDelimiter := true;
      sl.Delimiter := '|';
      sl.DelimitedText := arow;
      if ColCount < sl.Count  then
        ColCount := sl.Count + FixedCols;

      for c := 0 to sl.Count - 1 do
      begin
        cd := sl.Strings[c];
        GetCellData(FixedCols + c, FixedRows + r, nil, cd);
        Cells[FixedCols + c, FixedRows + r] := cd;

        cn := '';
        ce := CellElements[FixedCols + c, FixedRows + r];
        GetCellChildren(FixedCols + c, FixedRows + r, nil, cd, ce);
        GetCellClassName(FixedCols + c, FixedRows + r, nil, cd, cn);
        if cn <> '' then
          ce.setAttribute('class',cn);
      end;
    end;

    sl.Free;
  end;

  UpdateGridSize;
  UpdateGridAlignment;
end;

function TCustomStringGrid.LoadFromJSONAsync(const AURL: string; ADataNode: string = ''): TJSPromise;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)

      function DoLoad(Event: TEventListenerEvent): boolean;
      begin
        if DoHttpLoadJson(Event) then
          ASuccess(TJSXMLHttpRequest(Event.target))
        else
          AFailed(TJSXMLHttpRequest(Event.target));
        Result := true;
      end;

      function DoError(Event: TEventListenerEvent): boolean;
      begin
        AFailed(TJSXMLHttpRequest(Event.target));
        Result := true;
      end;

    begin
      FDataNode := ADataNode;
      FReq := TJSXMLHttpRequest.new;
      FReq.addEventListener('load',@DoLoad);
      FReq.addEventListener('abort',@DoError);
      FReq.addEventListener('timeout',@DoError);
      FReq.addEventListener('error',@DoError);

      FReq.open('GET', AURL);
      DoHttpRequest(FReq);
      FReq.send;
    end);
end;

procedure TCustomStringGrid.LoadFromStringList(AStrings: TStrings);
var
  arow: TStringList;
  i,j,maxcol: integer;
  sr,sc: integer;
  cd, cn: string;
  ce: TJSHTMLElement;

begin
  if FLoadFixed then
    RowCount := AStrings.Count
  else
    RowCount := AStrings.Count + FixedRows;

  arow := TStringList.Create;

  sc := FixedCols;
  sr := FixedRows;

  if FLoadFixed then
  begin
    sr := 0;
    sc := 0;
  end;

  maxcol := 0;

  try
    for i := 0 to AStrings.Count - 1 do
      begin
        arow.StrictDelimiter := true;
        arow.Delimiter := FDelimiter;
        arow.DelimitedText := AStrings.Strings[i];

        if arow.Count > maxcol then
          maxcol := arow.Count;

        if arow.Count > ColCount - sc then
          ColCount := arow.Count + sc;

        for j := 0 to arow.Count - 1 do
        begin
          cd := arow.Strings[j];
          GetCellData(j + sc, i + sr, nil, cd);
          Cells[j + sc, i + sr] := cd;

          ce := CellElements[j + sc, i + sr];

          GetCellChildren(j + sc, i + sr, nil, cd, ce);

          cn := '';
          GetCellClassName(j + sc, i + sr, nil, cd, cn);
          if cn <> '' then
            ce.setAttribute('class',cn);
        end;
      end;
  finally
    arow.Free;
  end;

  if maxcol < ColCount - sc then
    ColCount := maxcol + sc;
end;

procedure TCustomStringGrid.LoadFromStrings(AStrings: TStrings; Delimiter: char;
  LoadFixed: boolean);
begin
  FDelimiter := Delimiter;
  FLoadFixed := LoadFixed;

  LoadFromStringList(AStrings);
end;

{$HINTS OFF}
function TCustomStringGrid.DoHttpLoadJson(Event: TEventListenerEvent): boolean;
var
  J: TJSObject;
  req: TJSXMLHttpRequest;
begin
  req := TJSXMLHttpRequest(Event.target);

  if (req.status >= 200) and (req.status <= 208) then
  begin
    J := TJSJSON.parseObject(req.responseText);

    LoadFromJSON(J, FDataNode);

    if Assigned(OnHttpRequestSuccess) then
      OnHttpRequestSuccess(Self);
  end;

  Result := true;
end;


procedure TCustomStringGrid.DoHttpRequest(ARequest: TJSXMLHttpRequest);
var
  LRequestRec: TJSXMLHttpRequestRecord;
begin
  if Assigned(OnHttpRequest) then
  begin
    LRequestRec.req := ARequest;
    OnHttpRequest(Self, LRequestRec);
  end;
end;

procedure TCustomStringGrid.DoSortClick(ACol, ARow: Longint;
  ASortIndicator: TGridSortIndicator);
var
 i: integer;
begin
  FSortIndex := ACol;

  for i := 0 to ColCount - 1 do
  begin
    SelectSortIndicator(i,ARow,i = ACol);
  end;

  FSortDirection := ASortIndicator;

  if Assigned(OnSortClick) then
    OnSortClick(Self, ACol, ARow, ASortIndicator);

  Sort(ACol, ASortIndicator);
end;

procedure TCustomStringGrid.EnableColResize;
var
  TableName: string;
  ResizeEnabled: Boolean;
begin
  if not (csDesigning in ComponentState) then
  begin
    TableName := GetID + 'tblResizeRow';

    ResizeEnabled := (goColSizing in Options);

    asm
      TMSStringGridresizableGrid(TableName, ResizeEnabled);
    end;

    TableName := GetID + 'tblResizeFixedRow';
    ResizeEnabled := (goColSizing in Options);
    asm
      TMSStringGridresizableGrid(TableName, ResizeEnabled);
    end;
  end;
end;

procedure TCustomStringGrid.EnableDrag;
  procedure AddDraggableAttribute(AElement: TJSElement);
  var
    I: Integer;
  begin
    for I := 0 to AElement.children.length - 1 do
    begin
      if TJSElement(AElement.children[I]).tagName = 'TD' then
      begin
        TJSElement(AElement.children[I]).Attrs['draggable'] := 'true';
        AddDraggableAttribute(TJSElement(AElement.children[I]));
      end
      else
        AddDraggableAttribute(TJSElement(AElement.children[I]));
    end;
  end;
begin
  if not Assigned(Container) then
    Exit;

  AddDraggableAttribute(Container);
end;

{$HINTS ON}

procedure TCustomStringGrid.DoBoundsChange;
var
  fw,fh,nwvis,nhvis: integer;

begin
  inherited;

  if Assigned(ElementHandle) then
  begin
    UpdateGridSize;

    fw := FixedColWidth;
    fh := FixedRowHeight;

    nwvis := Width - fw;
    nhvis := Height - fh;

    if Assigned(FNormalCells) then
    begin
      TJSHTMLElement(FNormalCells.parentElement.parentElement).style.setProperty('width',inttostr(nwvis)+'px');
      TJSHTMLElement(FNormalCells.parentElement.parentElement).style.setProperty('height',inttostr(nhvis)+'px');
    end;
  end;
end;

procedure TCustomStringGrid.DoButtonClick(ACol, ARow: Longint);
begin
  if Assigned(OnButtonClick) then
    OnButtonClick(Self, ACol, ARow);
end;

procedure TCustomStringGrid.DoCheckClick(ACol, ARow: Longint; Checked: boolean);
begin
  if Assigned(OnCheckClick) then
    OnCheckClick(Self, ACol, ARow, Checked);
end;

procedure TCustomStringGrid.DoColumnSized(AColumn: integer);
begin
  if Assigned(OnColumnSized) then
    OnColumnSized(Self, AColumn);
end;

procedure TCustomStringGrid.DoEditControlExit(Sender: TObject);
begin
  HideEdit;
end;

procedure TCustomStringGrid.DoEnter;
begin
//  focus is handled at NormalCells level
end;

procedure TCustomStringGrid.DoExit;
begin
//  focus is handled at NormalCells level
end;

function TCustomStringGrid.DoHttpAbort(Event: TEventListenerEvent): boolean;
begin
  if Assigned(OnHttpRequestError) then
    OnHttpRequestError(Self);

  Result := true;
end;

function TCustomStringGrid.FixedColWidth: integer;
var
  i: integer;
begin
  Result := 0;

  for i := 0 to FixedCols - 1 do
    Result := Result + ColWidths[i] - CELLPADDING;
end;

procedure TCustomStringGrid.FixedFontChanged(Sender: TObject);
begin
  AddInstanceStyle(StylePrefix + FOrigID, GetGridCSS(FOrigID + 'tbl'));
  UpdateElementVisual;
end;

function TCustomStringGrid.FixedRowHeight: integer;
var
  i: integer;
begin
  Result := 0;

  for i := 0 to FixedRows - 1 do
    Result := Result + RowHeights[i];
end;

function TCustomStringGrid.FocusedCell: TJSElement;
begin
  Result := FSelectedCell;
end;

procedure TCustomStringGrid.FontChanged;
begin
  inherited;
  UpdateElement;
end;

function TCustomStringGrid.GetButtonElement(ACol,
  ARow: integer): TJSHTMLElement;
var
  el: TJSElement;
begin
  Result := nil;

  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := TJSElement(CellElements[ACol,ARow].firstChild);
    if Assigned(el) and (el is TJSElement) then
    begin
      if (uppercase(el.tagName) = 'BUTTON') then
      begin
        Result := TJSHTMLElement(el);
      end;
    end;
  end;
end;

procedure TCustomStringGrid.GetCellChildren(ACol, ARow: integer; AField: TField;
  AValue: string; AElement: TJSHTMLElement);
var
  LElementRec: TJSHTMLElementRecord;
begin
  if Assigned(OnGetCellChildren) then
  begin
    LElementRec.element := AElement;
    OnGetCellChildren(Self, ACol, ARow, AField, AValue, LElementRec);
  end;
end;

procedure TCustomStringGrid.GetCellClassName(ACol, ARow: integer;
  AField: TField; AValue: string; var AClassName: string);
begin
  if Assigned(OnGetCellClass) then
    OnGetCellClass(Self, ACol, ARow, AField, AValue, AClassName);
end;

procedure TCustomStringGrid.GetCellData(ACol, ARow: integer; AField: TField;
  var AValue: string);
begin
  if Assigned(OnGetCellData) then
    OnGetCellData(Self, ACol, ARow, AField, AValue);
end;

{$HINTS OFF}
procedure TCustomStringGrid.GetCellEditor(const ACol, ARow: integer;
  var AEditor: TGridCellEditor);
begin
  if Assigned(OnGetCellEditor) then
    OnGetCellEditor(Self, ACol, ARow, AEditor);
end;

function TCustomStringGrid.GetCellElements(ACol, ARow: integer): TJSHTMLElement;
(*
var
  rc,rr: integer;
  res: TJSHTMLElement;
*)
begin
  Result := CellRealElement(ACol,ARow);
(*
  Exit;

  res := nil;
  if (ACol < FixedCols) and (ARow >= FixedRows) then
  begin
    rr := ARow - FixedRows;
    rc := ACol;

    asm
      res = this.FFixedCol.rows[rr].cells[rc];
    end;
  end;

  if (ARow < FixedRows) and (ACol >= FixedCols) then
  begin
    rc := ACol - FixedCols;
    rr := ARow;

    asm
      res = this.FFixedRow.rows[rr].cells[rc];
    end;
  end;

  if (ACol < FixedCols) and (ARow < FixedRows) then
  begin
    rr := ARow;
    rc := ACol;
    asm
      res = this.FFixedColRow.rows[rr].cells[rc];
    end;
  end;

  if (ACol >= FixedCols) and (ARow >= FixedRows) and (ARow < RowCount) and (ACol < ColCount) then
  begin
    rc := ACol - FixedCols;
    rr := ARow - FixedRows;
    asm
      res = this.FNormalCells.rows[rr].cells[rc];
    end;
  end;

  Result := res;
*)
end;


function TCustomStringGrid.GetDirectCellElements(ACol, ARow: integer): TJSHTMLElement;
var
  rc,rr: integer;
  res: TJSHTMLElement;
begin
  res := nil;

  if (ACol < FixedCols) and (ARow >= FixedRows) then
  begin
    rr := ARow - FixedRows;
    rc := ACol;

    asm
      res = this.FFixedCol.rows[rr].cells[rc];
    end;
  end;

  if (ARow < FixedRows) and (ACol >= FixedCols) then
  begin
    rc := ACol - FixedCols;
    rr := ARow;

    asm
      res = this.FFixedRow.rows[rr].cells[rc];
    end;
  end;

  if (ACol < FixedCols) and (ARow < FixedRows) then
  begin
    rr := ARow;
    rc := ACol;
    asm
      res = this.FFixedColRow.rows[rr].cells[rc];
    end;
  end;

  if (ACol >= FixedCols) and (ARow >= FixedRows) and (ARow < RowCount) and (ACol < ColCount) then
  begin
    rc := ACol - FixedCols;
    rr := ARow - FixedRows;
    asm
      res = this.FNormalCells.rows[rr].cells[rc];
    end;
  end;

  Result := res;
end;

{$HINTS ON}

function TCustomStringGrid.GetCells(ACol, ARow: integer): string;
var
  td: TJSHTMLElement;
begin
  td := CellRealElement(ACol,ARow);

  if not Assigned(td) then
  begin
    Result := '';
    Exit;
  end;

 if WordWrap then
  begin
    if Assigned(td.firstChild) then
      td := TJSHTMLElement(td.firstChild);
  end;

  Result := td.textContent;
end;

function TCustomStringGrid.GetCheckElement(ACol, ARow: integer): TJSHTMLElement;
var
  el: TJSElement;
begin
  Result := nil;

  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := TJSElement(CellElements[ACol,ARow].firstChild);
    if Assigned(el) and (el is TJSElement) then
    begin
      if el.hasAttribute('type') and (uppercase(el['type']) = 'CHECKBOX') then
      begin
        Result := TJSHTMLElement(el);
      end;
    end;
  end;
end;

function TCustomStringGrid.GetCanvasElement(ACol, ARow: integer): TJSHTMLElement;
var
  el: TJSElement;
begin
  Result := nil;

  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := TJSElement(CellElements[ACol,ARow].firstChild);

    if Assigned(el) and (el is TJSElement) then
    begin
      if Uppercase(el.tagName) = 'CANVAS' then
      begin
        Result := TJSHTMLElement(el);
      end;
    end;
  end;
end;


function TCustomStringGrid.GetCheckState(ACol, ARow: integer): boolean;
var
  el: TJSElement;
  res: boolean;
begin
  Result := false;

  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetCheckElement(ACol,ARow);
    if Assigned(el) then
    begin
      asm
        res = el.checked;
      end;
      Result := res;
    end;
  end;
end;

function TCustomStringGrid.GetCheckEnabled(ACol, ARow: integer): boolean;
var
  el: TJSElement;
  res: boolean;
begin
  Result := false;

  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetCheckElement(ACol,ARow);
    if Assigned(el) then
    begin
      asm
        res = el.disabled;
      end;
      Result := not res;
    end;
  end;
end;


function TCustomStringGrid.GetCol: integer;
var
  res: integer;
begin
  res := -1;
  if  Assigned(FSelectedCell) then
  begin
    asm
      res = this.FSelectedCell.cellIndex;
    end;
    res := res + FixedCols;
  end;

  Result := res;
end;

function TCustomStringGrid.GetColAlignments(ACol: integer): TAlignment;
begin
  if ACol < FColAlignments.Count then
    Result := TAlignment(FColAlignments.Items[ACol])
  else
    Result := DefaultColAlignment;
end;

function TCustomStringGrid.GetColWidths(ACol: integer): integer;
begin
  if ACol < FColWidths.Count then
    Result := integer(FColWidths.Items[ACol])
  else
    Result := DefaultColWidth;
end;

function TCustomStringGrid.GetDataRowCount: Integer;
begin
  Result := RowCount - FixedRows;
end;

procedure TCustomStringGrid.GetEditControlValue(ACol, ARow: Longint;
  AControl: TControl; var AValue: string);
begin
  if Assigned(OnGetEditControlValue) then
    OnGetEditControlValue(Self, ACol, ARow, AControl, AValue);
end;

function TCustomStringGrid.GetEditText(ACol, ARow: Longint): string;
begin
  Result := Cells[ACol,ARow];

  if Assigned(OnGetEditText) then
    OnGetEditText(Self, ACol, ARow, Result);
end;

function TCustomStringGrid.GetFixedTextColor: TColor;
begin
  Result := FFixedFont.Color;
end;

function TCustomStringGrid.GetGridCSS(id: string): string;
var
  ff: string;
begin
  Result := '';

  if ElementTableClassName <> '' then
    Exit;

  if not (goVertLine in Options) then
    Result := 'border-left: 0px; border-right: 0px;';

  if not (goHorzLine in Options) then
    Result := Result + 'border-top: 0px; border-bottom: 0px;';

  if ElementFont = efProperty then
  begin
    Result := Result + CSSFont(Font);
    ff := CSSFont(FixedFont);
  end;

  Result := 'table#' + id + ' td { overflow: hidden; text-overflow: ellipsis; white-space:nowrap; border: solid 1px '+ ColorToHTML(GridLineColor) +'; padding: 0px;' + Result + '} '+#13+
                   'table#'+id+' td.fixed {'+ ff + '} '+#13+
                   'table#'+id+' td.selected { background-color: '+ ColorToHTML(SelectionColor)+ '; color: '+ ColorToHTML(SelectionTextColor)+';} '+#13+
                   'table#'+id+' tr.selected { background-color: '+ ColorToHTML(SelectionColor)+ '; color: '+ ColorToHTML(SelectionTextColor)+';} '+#13+
//                   'table#'+id+'NormalCellTable td { padding:2px;} '+#13+
//                   'table#'+id+'ResizeFixedRowTable td { padding:2px;} '+#13+
//                   'table#'+id+'ResizeRowTable td { padding:2px;} '+#13+
//                   'table#'+id+'ResizeFixedColTable td { padding:2px;} '+#13+
                   'table#'+id+' DIV.cell {word-wrap: normal; white-space: normal; text-overflow: clip; } ';
end;

function TCustomStringGrid.GetLeftCol: integer;
var
  i,w,l: integer;
begin
  Result := FixedCols;

  if Assigned(FNormalCells) then
  begin
    l := FNormalCells.parentElement.parentElement.scrollLeft;
    w := 0;
    for i := FixedCols to ColCount - 1 do
    begin
     if l <= w then
     begin
       Result := i;
       break;
     end;
      w := w + ColWidths[i] + 1 - CELLPADDING;
    end;
  end;
end;

procedure TCustomStringGrid.GetMethodPointers;
begin
  inherited;
  FHandleEditBlurPtr := @HandleEditBlur;
  FHandleEditKeypressPtr := @HandleEditKeypress;
  FHandleCheckClickPtr := @HandleCheckBoxClick;
  FHandleButtonClickPtr := @HandleButtonClick;
  FHandleMouseDownPtr := @HandleMouseDown;
  FHandleMouseUpPtr := @HandleMouseUp;
  FHandleMouseMovePtr := @HandleMouseMove;
  FHandleFixedClickPtr := @HandleFixedClick;
  FHandleFocusPtr := @HandleTableFocus;
  FHandleBlurPtr := @HandleTableBlur;
  FHandleClickPtr := @HandleClick;
end;

function TCustomStringGrid.GetObjects(ACol, ARow: integer): TObject;
var
  LObjr: TObjectRow;
begin
  Result := nil;

  if FObjectRows.Count > ARow then
  begin
    LObjr := TObjectRow(FObjectRows[ARow]);
    if LObjr.Count > ACol then
      Result := TObject(LObjr[ACol]);
  end;
end;

function TCustomStringGrid.GetProgressElement(ACol,
  ARow: integer): TJSHTMLElement;
var
  el: TJSElement;
begin
  Result := nil;

  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := TJSElement(CellElements[ACol,ARow].firstChild);
    if Assigned(el) and (el is TJSElement) then
    begin
      if (uppercase(el.tagName) = 'PROGRESS') then
      begin
        Result := TJSHTMLElement(el);
      end;
    end;
  end;
end;

function TCustomStringGrid.GetRow: integer;
var
  res: integer;
begin
  res := -1;
  if Assigned(FSelectedCell) then
  begin
    asm
      res = this.FSelectedCell.parentElement.rowIndex;
    end;
    res := res + FixedRows;
  end;

  Result := res;
end;

function TCustomStringGrid.GetRowHeights(ARow: integer): integer;
begin
  if ARow < FRowHeights.Count then
    Result := integer(FRowHeights.Items[ARow])
  else
    Result := DefaultRowHeight;
end;

function TCustomStringGrid.GetRows(Index: integer): TStrings;
var
  i: integer;
begin
  if (Index >= 0) and (Index < RowCount) then
  begin
    FRowsIndex := Index;
    FRows.Clear;
    for i := 0 to ColCount - 1 do
      FRows.Add(Cells[i,Index]);

    Result := FRows;
  end
  else
    raise Exception.Create('Invalid row index');
end;

function TCustomStringGrid.GetSelection: TGridRect;
begin
  Result := FSelection;
end;

function TCustomStringGrid.GetTopRow: integer;
var
  pixVal: integer;
begin
  Result := FixedRows;

  if Assigned(FNormalCells) and (pixVal >= 0) then
  begin
    pixVal := FNormalCells.parentElement.parentElement.scrollTop;

    Result := FixedRows + pixVal div DefaultRowHeight;
  end;
end;

function TCustomStringGrid.GetVisibleColCount: integer;
var
  i: integer;
  el: TJSHTMLElement;
  cr,gr: TJSDOMRect;
begin
  i := LeftCol;

  gr := ElementHandle.getBoundingClientRect;

  while i < ColCount do
  begin
    el := CellElements[i,FixedRows];
    cr := el.getBoundingClientRect;
    if cr.Right > gr.Right then
      break;
    inc(i);
  end;

  Result := i - LeftCol;
end;

function TCustomStringGrid.GetVisibleRowCount: integer;
var
  i: integer;
  el: TJSHTMLElement;
  cr,gr: TJSDOMRect;
begin
  i := TopRow;

  gr := ElementHandle.getBoundingClientRect;

  while i < RowCount do
  begin
    el := CellElements[FixedCols,i];
    cr := el.getBoundingClientRect;
    if cr.Bottom > gr.Bottom then
      break;
    inc(i);
  end;

  Result := i - TopRow;
end;

procedure TCustomStringGrid.GridChanged;
begin
  inherited;
  FSelectedCell := nil;
  UpdateGridSize;
  UpdateGridAlignment;
end;

{$HINTS OFF}
function TCustomStringGrid.HandleButtonClick(Event: TJSEvent): Boolean;
var
  rindex, cindex: integer;
  chk: boolean;
begin
  rindex := -1;
  cindex := -1;

  asm
    var el = Event.target;
    var d = el.parentElement;
    if (d.tagName.toLowerCase() == 'td')
    {
      cindex = d.cellIndex;
      var tr = d.parentElement;
      rindex = tr.rowIndex;
    }
  end;

  if (rindex <> -1) and (cindex <> -1) then
  begin
    DoButtonClick(cindex + FixedCols, rindex + FixedRows);
  end;
end;

function TCustomStringGrid.HandleCheckBoxClick(Event: TJSEvent): Boolean;
var
  rindex, cindex: integer;
  chk: boolean;
begin
  rindex := -1;
  cindex := -1;

  asm
    var el = Event.target;
    chk = el.checked;
    var d = el.parentElement;
    if (d.tagName.toLowerCase() == 'td')
    {
      cindex = d.cellIndex;
      var tr = d.parentElement;
      rindex = tr.rowIndex;
    }
  end;

  if (rindex <> -1) and (cindex <> -1) then
  begin
    if not CanEditCell(cindex + FixedCols, rindex + FixedRows) then
    // undo checkbox change from click
      asm
        var el = Event.target;
        el.checked = (!el.checked);
      end
    else
      DoCheckClick(cindex + FixedCols, rindex + FixedRows, chk);
  end;
end;

function TCustomStringGrid.HandleFixedClick(Event: TJSEvent): Boolean;
var
  rindex,cindex: integer;
  rselect: boolean;
  tbl: TJSHTMLElement;
  ACol,ARow: integer;
  tgt: TJSHTMLElement;
  tag: string;
  sd: TGridSortIndicator;

begin
  Result := true;

  rselect := (goRowSelect in Options);
  rindex := -1;
  cindex := -1;

  sd := siNone;

  asm
    tgt = Event.target;
  end;

  if tgt.tagName = 'SPAN' then
  begin
    if tgt['sort']= 'up' then
    begin
      sd := siAscending;
    end
    else
    if tgt['sort']= 'dn' then
    begin
      sd := siDescending;
    end;
  end;

  repeat
    asm
      tag = tgt.tagName.toLowerCase();
      if (tag != 'td')
      {
        tgt = tgt.parentElement;
      }
    end;
  until (tag = 'td') or (tgt = nil);


  asm
    if (tag == 'td')
    {
      cindex = tgt.cellIndex;
      var tr = tgt.parentElement;
      rindex = tr.rowIndex;
      tbl = tr.parentElement;
      tbl = tbl.parentElement;
    }
  end;

  if (cindex = -1) or (rindex = -1) then
    Exit;

  ACol := cindex;
  ARow := rindex;

  if tbl = FFixedCol then
  begin
    ARow := rindex + FixedRows;
  end;

  if tbl = FFixedRow then
  begin
    ACol := cindex + FixedCols;
  end;

  if sd = siAscending then
  begin
    DoSortClick(ACol,ARow,siDescending);
    SetSortIndicator(ACol,ARow, siDescending);
  end
  else
  if sd = siDescending then
  begin
    DoSortClick(ACol,ARow, siAscending);
    SetSortIndicator(ACol,ARow, siAscending);
  end;

  if Assigned(OnFixedCellClick) then
    OnFixedCellClick(Self, ACol, ARow);
end;

function TCustomStringGrid.HandleClick(Event: TJSEvent): Boolean;
var
  rindex,cindex: integer;
  rselect: boolean;
  tr: TJSElement;
  tgt: TJSHTMLElement;
  tag,starttag: string;
begin
  Result := true;

  if not Enabled then
    Exit;

  rselect := (goRowSelect in Options);
  rindex := -1;
  cindex := -1;

  asm
    tgt = Event.target;
    starttag = tgt.tagName.toLowerCase();
  end;

  if (starttag = 'tr') or (starttag = 'tbody') then
    Exit;

  repeat
    asm
      tag = tgt.tagName.toLowerCase();
      if (tag != 'td')
      {
        tgt = tgt.parentElement;
      }
    end;
  until (tag = 'td') or (tgt = nil);

  asm
    if (tag == 'td')
    {
      cindex = tgt.cellIndex;
      var tr = tgt.parentElement;
      rindex = tr.rowIndex;
    }
  end;

  if (tag = 'td') and FHasMergedCells then
  begin
    CellRealCoords(tgt, cindex, rindex);
  end;

  if (cindex = -1) or (rindex = -1) then
    Exit;

  if not CanSelect(cindex + FixedCols, rindex + FixedRows) then
  begin
    Exit;
  end;

  SelectCell(cindex + FixedCols, rindex + FixedRows);

  if Assigned(OnClickCell) and Enabled then
  begin
    OnClickCell(Self, cindex + FixedCols, rindex + FixedRows);
  end;

  if Assigned(FSelectedCell) then
  begin
    if FSelMouseDown then
    begin
      if (goEditing in Options) and CanEditCell(Col,Row) then
      begin
        ShowEdit;
        Exit;
      end;
    end
    else
    begin
      if rselect then
      begin
        tr := FSelectedCell.parentElement;
        RemoveSelection(tr);
      end
      else
        RemoveSelection(FSelectedCell);
    end;
  end;

  if (tag = 'td') then
  begin
    tr := tgt.parentElement;
    FSelectedCell := tgt;

    if (rselect) then
      AddSelection(tr)
    else
      AddSelection(tgt);
  end;
end;

procedure TCustomStringGrid.HandleComboExit(Sender: TObject);
begin
  if EditMode then
    HideEdit;
end;

procedure TCustomStringGrid.HandleComboKeyPress(Sender: TObject; var Key: Char);
begin
  if Assigned(OnKeyPress) then
    OnKeyPress(Self, Key);

  if (Ord(Key) = 27) then
    CancelEdit;
  if (Ord(Key) = 13) then
    HideEdit(EditAdvance);
end;

function TCustomStringGrid.HandleDoClick(Event: TJSMouseEvent): Boolean;
begin
  Result := inherited;
  if not EditMode and Assigned(FNormalCells) then
    TJSHTMLElement(FNormalCells).focus();
end;

function TCustomStringGrid.HandleDoMouseMove(Event: TJSMouseEvent): Boolean;
var
  CtrlName: string;
  IsAutoWidth: Boolean;
begin
  inherited;

  CtrlName := GetID + 'tbl';
  IsAutoWidth := (WidthStyle in [ssAuto]);
  asm
    TMSStringGridID = CtrlName;
    TMSStringGridautoWidth = IsAutoWidth;
    GridNormalCells = this.FNormalCells;
    GridFixedCells = this.FFixedRow;
    GridFixedColCells = this.FFixedCol;
    GridFixedColRows = this.FFixedColRow;
    GridColWidths = this.FColWidths;
    GridFixedCols = this.FFixedCols;
    TMSStringGridresizeColumn(Event);
  end;

  Result := True;
end;

function TCustomStringGrid.HandleDoMouseUp(Event: TJSMouseEvent): Boolean;
var
  res: integer;
begin
  inherited;

  asm
    res = TMSStringGridisSizing();
    TMSStringGridresetResize();
  end;

  if res >= 0 then
    DoColumnSized(res);

  Result := True;
end;

{$HINTS ON}

function TCustomStringGrid.HandleEditBlur(Event: TJSEvent): Boolean;
begin
  if FEditMode then
  begin
    HideEdit;
  end;
  Result := true;
end;

function TCustomStringGrid.HandleEditKeyPress(Event: TJSKeyboardEvent): boolean;
var
  c: char;
begin
  Event.cancelBubble := true;
  Event.stopPropagation;
  Result := true;

  if IsKeyCharacter(Event.Key) then
  begin
    c := Chr(GetKeyCode(Event.Key, false));
    if Assigned(OnKeyPress) then
      OnKeyPress(Self, c);
  end;
end;

function TCustomStringGrid.HandleKeyDown(Event: TJSKeyboardEvent): boolean;
var
  k: Word;
  newcol,newrow: integer;
  ss: TShiftState;
  undef: boolean;
  ischar: boolean;
begin
  Result := true;
  if not Enabled then
    Exit;

  Event.cancelBubble := true;

  Event.StopPropagation;

  k := 0;
  ischar := false;

  asm
    undef = (Event.key == undefined);
  end;

  if (Uppercase(Event.key) = 'UNIDENTIFIED') then
    Exit;

  if not undef then
  begin
    k := GetKeyCode(Event.Key);

    ischar := length(Event.Key) = 1;

    ss := GetKeyboardEventShiftState(Event);

    if Assigned(OnKeyDown) then
      OnKeyDown(Self, k, ss);
  end;

  newrow := Row;
  newcol := Col;

  if (newrow = -1) and (newcol = -1) and (ColCount > FixedCols) and (RowCount > FixedRows) then
  begin
    newcol := FixedCols;
    newrow := FixedRows;
    Col := FixedCols;
    Row := FixedRows;
    TJSHTMLElement(FNormalCells).focus;
  end;

  //ss := GetKeyboardEventShiftState(Event);

  // Up, Down, PageUp, PageDn
  if (k in [38, 40, 33, 34]) and not ischar then
  begin
    Event.preventDefault;
    HideEdit;
    ChangeRow;
  end;

  // Home, End, Left, Right should only work when not in edit mode
  if not EditMode and (k in [35, 36, 37, 39]) and not ischar then
  begin
    Event.preventDefault;
    HideEdit;
  end;

  // ESC
  if (k = 27) and not ischar then
  begin
    CancelEdit;
  end;

  if not EditMode and not ischar and Event.shiftKey and not ((k >= 48) and (k <= 122)) and (goRangeSelect in Options) then
  begin
    // End
    if (k = 35) then
    begin
      Row := RowCount - 1;
      Selection := GridRect(Col, newrow, Col, Row);

      if Row >= TopRow + VisibleRowCount then
        TopRow := Row - VisibleRowCount;
    end;

    // Home
    if (k = 36) then
    begin
      Row := FixedRows;
      Selection := GridRect(Col, newrow, Col, Row);

      if Row < TopRow then
        TopRow := FixedRows;
    end;

    // arrow Up
    if (k = 38) and (Row > FixedRows) then
    begin
      if Row = min(Selection.Top, Selection.Bottom) then
        newrow := max(Selection.Top, Selection.Bottom)
      else
        newrow := min(Selection.Top, Selection.Bottom);

      Row := Row - 1;
      Selection := GridRect(Selection.Left, newrow, Selection.Right, Row);

      if Row < TopRow then
        TopRow := TopRow - 1;
    end;

    // arrow Down
    if (k = 40) and (Row < RowCount - 1) then
    begin
      if Row = max(Selection.Top, Selection.Bottom) then
        newrow := min(Selection.Top, Selection.Bottom)
      else
        newrow := max(Selection.Top, Selection.Bottom);

      Row := Row + 1;
      Selection := GridRect(Selection.Left, newrow, Selection.Right, Row);

      if Row > TopRow + VisibleRowCount then
        TopRow := TopRow + 1;
    end;

    // arrow Left
    if (k = 37) and (Col > FixedCols) then
    begin
      if Col = min(Selection.Left, Selection.Right) then
        newcol := max(Selection.Left, Selection.Right)
      else
        newcol := min(Selection.Left, Selection.Right);

      Col := Col - 1;
      Selection := GridRect(newcol, Selection.Top, Col, Selection.Bottom);

      if Col < LeftCol then
        LeftCol := LeftCol - 1;
     end;

    // arrow Right
    if (k = 39) and (Col < ColCount - 1) then
    begin
      if Col = max(Selection.Left, Selection.Right) then
        newcol := min(Selection.Left, Selection.Right)
      else
        newcol := max(Selection.Left, Selection.Right);

      Col := Col + 1;

      Selection := GridRect(newcol, Selection.Top, Col, Selection.Bottom);

      if Col >= LeftCol + VisibleColCount then
        LeftCol := LeftCol + 1;
    end;
  end
  else
  begin
    // End
    if (k = 35) and not ischar then
    begin
      newrow := RowCount - 1;
    end;

    // Home
    if (k = 36) and not ischar then
    begin
      newrow := FixedRows;
    end;

    // PgUp
    if (k = 34) and not ischar then
    begin
      if (Row + 4 <= RowCount - 1) then
      begin
        newrow := Row + 4;
      end
      else
      begin
        newrow := RowCount - 1;
      end;
    end;

    // PgDn
    if (k = 33) and not ischar then
    begin
      if Row - 4 > FixedRows then
      begin
        newrow := Row - 4
      end
      else
      begin
        newrow := FixedRows;
      end;
    end;

    // arrow Up
    if not EditMode and (k = 38) and (Row > FixedRows) and not ischar then
    begin
      newrow := Row - 1;
    end;

    // arrow Down
    if not EditMode and (k = 40) and (Row < RowCount - 1) and not ischar then
    begin
      newrow := Row + 1;
    end;

    // arrow Left
    if not EditMode and (k = 37) and (Col > FixedCols) and not ischar then
    begin
      newcol := Col - 1;
    end;

    // arrow Right
    if not EditMode and (k = 39) and (Col < ColCount - 1) and not ischar then
    begin
      newcol := Col + 1;
    end;

    if not EditMode and CanSelect(newcol,newrow) and not ischar then
    begin
      SelectCell(newcol,newrow);

      if (k = 38) and (Row < TopRow) then
         TopRow := TopRow - 1;

      if (k = 33) and (Row < TopRow) then
         TopRow := Row;

      if (k = 34) and (Row >= TopRow + VisibleRowCount) then
         TopRow := Row;

      if (k = 40) and (Row >= TopRow + VisibleRowCount) then
         TopRow := TopRow + 1;

      if (k = 37) and (Col < LeftCol) then
         LeftCol := LeftCol - 1;

      if (k = 39) and (Col >= LeftCol + VisibleColCount) then
         LeftCol := LeftCol + 1;

      if (k = 35) then
         TopRow := Row - VisibleRowCount;

      if (k = 36) then
         TopRow := FixedRows;
    end;

    // F2
    if (k = 113) and not EditMode and (goEditing in Options) and CanEditCell(Col,Row) then
    begin
      ShowEdit;
    end;

    if ((k >= 32) and (k <= 112)) and ischar and not EditMode and (goEditing in Options) and CanEditCell(Col,Row) then
      StartEdit(Event.Key);

    if (k = 13) and (goEditing in Options) and CanEditCell(Col,Row) then
    begin
      if EditMode then
      begin
        HideEdit(EditAdvance);
      end
      else
        ShowEdit;
    end;
  end;
end;

procedure TCustomStringGrid.HandleMaskExit(Sender: TObject);
begin
  if EditMode then
    HideEdit;
end;

procedure TCustomStringGrid.HandleMemoExit(Sender: TObject);
begin
  if EditMode then
    HideEdit;
end;


procedure TCustomStringGrid.HandleMaskKeyPress(Sender: TObject; var Key: Char);
begin
  if Assigned(OnKeyPress) then
    OnKeyPress(Self, Key);

  if (Ord(Key) = 27) then
    CancelEdit;

  if (Ord(Key) = 13) then
    HideEdit(EditAdvance);
end;

procedure TCustomStringGrid.HandleMemoKeyPress(Sender: TObject; var Key: Char);
begin
  if Assigned(OnKeyPress) then
    OnKeyPress(Self, Key);

  if (Ord(Key) = 27) then
    CancelEdit;
end;

function TCustomStringGrid.HandleMouseDown(Event: TJSMouseEvent): Boolean;
var
  rindex, cindex: integer;
  l, t: Single;
  ss: TShiftState;
  mb: TMouseButton;
  el: TJSHTMLElement;
  ww: boolean;
begin
  Result := true;

  if not Enabled then
    Exit;

  mb := GetMouseEventButton(Event);

  if mb <> mbLeft then
    Exit;

  rindex := -1;
  cindex := -1;

  el := TJSHTMLElement(Event.target);
  ww := WordWrap;

  if ww and (Lowercase(el.tagName)= 'div') then
  begin
    el := TJSHTMLElement(el.parentElement);
  end;

  asm
    if (el.tagName.toLowerCase() == 'td')
    {
      cindex = el.cellIndex;
      var tr = el.parentElement;
      rindex = tr.rowIndex;
    }
  end;

  FSelMouseDown := el = FSelectedCell;

  if Assigned(FSelectedCell) and not FSelMouseDown and (cindex <> -1) and (rindex <> -1) then
  begin
    HideEdit;
  end;

  if not CanSelect(cindex + FixedCols, rindex + FixedRows) then
    Exit;

  if (cindex >= 0) and (rindex >= 0) then
  begin
    if (goRowSelect in Options) then
      Selection := GridRect(FixedCols, rindex + FixedRows, ColCount - 1, rindex + FixedRows)
    else
      Selection := GridRect(cindex + FixedCols, rindex + FixedRows, cindex + FixedCols, rindex + FixedRows);

    if (goRangeSelect in Options) then
    begin
      FStartCell.X := cindex + FixedCols;
      FStartCell.Y := rindex + FixedRows;
    end
    else
    begin
      FStartCell.X := -1;
      FStartCell.Y := -1;
    end;
  end;

  XYToClient(Event.clientX, Event.clientY, l, t);

  ss := GetMouseEventShiftState(Event);

  MouseDown(mb, ss, Trunc(l), Trunc(t));
end;

function TCustomStringGrid.HandleScroll(Event: TJSEvent): Boolean;
begin
  if FixedCols > 0 then
  begin
    FFixedCol.parentElement.scrollTop := FNormalCells.parentElement.parentElement.scrollTop;
  end;

  if FixedRows > 0 then
    FFixedRow.parentElement.parentElement.scrollLeft := FNormalCells.parentElement.parentElement.scrollLeft;

  TopLeftChanged;

  Result := true;
end;

function TCustomStringGrid.HandleTableBlur(Event: TJSEvent): Boolean;
begin
  Result := true;

  if Assigned(OnExit) and not EditMode then
    OnExit(Self);
end;

function TCustomStringGrid.HandleTableFocus(Event: TJSEvent): Boolean;
begin
  Result := true;
  if not Enabled then
    Exit;

  if Assigned(OnEnter) and not FBlockFocus then
    OnEnter(Self);

  FBlockFocus := false;

  if (Col = -1) and (Row = -1) and not HasSelection and (FixedCols < ColCount) and (FixedRows < RowCount) and CanSelect(FixedCols, FixedRows) then
  begin
    SelectCell(FixedCols, FixedRows);
  end;
end;

function TCustomStringGrid.HasButton(const ACol, ARow: integer): boolean;
var
  el: TJSElement;
begin
  Result := false;
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetButtonElement(ACol,ARow);
    Result := Assigned(el);
  end;
end;

function TCustomStringGrid.HasCheckBox(const ACol, ARow: integer): boolean;
var
  el: TJSElement;
begin
  Result := false;
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetCheckElement(ACol,ARow);
    Result := Assigned(el);
  end;
end;

function TCustomStringGrid.HasCanvas(const ACol, ARow: integer): boolean;
var
  el: TJSElement;
begin
  Result := false;
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetCanvasElement(ACol,ARow);
    Result := Assigned(el);
  end;
end;

function TCustomStringGrid.GetCanvas(const ACol,ARow: integer): TJSHTMLCanvasElement;
var
  el: TJSElement;
begin
  Result := nil;
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetCanvasElement(ACol,ARow);
    if Assigned(el) then
      Result := TJSHTMLCanvasElement(el);
  end;
end;

function TCustomStringGrid.HasHorzScrollBar: boolean;
begin
  Result := false;
  if Assigned(FNormalCells.parentElement) then
  begin
    Result :=  FNormalCells.parentElement.parentElement.clientWidth < FNormalCells.parentElement.scrollWidth;
  end;
end;

function TCustomStringGrid.HasProgress(const ACol, ARow: integer): boolean;
var
  el: TJSElement;
begin
  Result := false;
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetProgressElement(ACol,ARow);
    Result := Assigned(el);
  end;
end;

function TCustomStringGrid.HasSelection: boolean;
begin
  Result := not ((Selection.Left = 0) and (Selection.Top = 0) and (Selection.Right = 0) and (Selection.Bottom = 0));
end;

function TCustomStringGrid.HasSortIndicator(const ACol, ARow: integer): boolean;
var
  el: TJSElement;
  sid: string;

begin
  sid := Name + 'si'+ACol.ToString+'_'+ARow.ToString;

  el := document.getElementById(sid);

  Result := Assigned(el);
end;

function TCustomStringGrid.HasVertScrollBar: boolean;
begin
  Result := false;
  if Assigned(FNormalCells.parentElement) then
  begin
    Result :=  FNormalCells.parentElement.parentElement.clientHeight < FNormalCells.parentElement.scrollHeight;
  end;
end;

procedure TCustomStringGrid.HideEdit(Advance: boolean = false);
var
  val,cval: string;
  eh: TJSHTMLElement;
  rh: integer;
begin
  if EditMode then
  begin
    FEditMode := false;

    if FEditType = geCombo then
    begin
      val := FCombo.Text;
      eh := FCombo.ElementHandle;
    end
    else
    if FEditType = geMemo then
    begin
      val := FMemo.Lines.Text;
      eh := FMemo.ElementHandle;
    end
    else
    if FEditType = geMask then
    begin
      val := FMask.Text;
      eh := FMask.ElementHandle;
    end
    else
    if FEditType = geCustom then
    begin
      GetEditControlValue(FEditCol, FEditRow, FEditControl, val);
      eh := FEditControl.ElementHandle;
      TJSHTMLElement(FSelectedCell).style.removeProperty('vertical-align');
    end
    else
    begin
      val := (FEdit as TJSHTMLInputElement).value;
      eh := FEdit;
    end;

    ValidateCell(FEditCol, FEditRow, val);

    if FSelectedCell.contains(eh) then
      FSelectedCell.removeChild(eh);

    cval := val;

    if WordWrap then
    begin
      rh := RowHeights[FEditRow] - 2;
      cval := '<DIV class="cell" style="height:'+rh.ToString+'px;max-height:'+rh.ToString+'px">'+ cval + '</DIV>';
    end;

    FSelectedCell.innerHTML := cval;

    UpdateCell(FEditCol, FEditRow, val);

    if not (goRowSelect in Options) then
      AddSelection(FSelectedCell);

    SetEditText(FEditCol, FEditRow, val);

    FBlockFocus := true;

    TJSHTMLElement(FNormalCells).focus();

    if Advance then
    begin
      RemoveSelection(FSelectedCell);
      if Col < ColCount - 1 then
        Col := Col + 1
      else
      begin
        if CanRowAdvance then
        begin
          if Row < RowCount - 1  then
          begin
            Col := FixedCols;
            Row := Row + 1;
          end
          else
          begin
            Col := FixedCols;
            Row := FixedRows;
          end;
        end
        else
          Exit;
      end;

      if CanEditCell(Col,Row) then
        StartEdit('');
    end;

  end;
end;

procedure TCustomStringGrid.InitCSSLibrary(ALibrary: TCSSLibrary);
begin
  if (ALibrary = cssBootstrap) then
  begin
    ElementFont := efCSS;
  end;
end;

procedure TCustomStringGrid.InitSample(AInitMethod: TInitMethod);
var
  i,j: integer;
begin
  for i := 0 to RowCount - 1 do
  begin
    for j := 0 to ColCount - 1 do
    begin
     case AInitMethod of
     imRandom:
       Cells[j,i] := IntToStr(Random(100));
     imLinear:
       Cells[j,i] := IntToStr(j)+':'+IntToStr(i);
     end;
    end;
  end;
end;

{$HINTS OFF}
procedure TCustomStringGrid.InsertColumn(const Index: integer);
var
  i,fc: integer;
begin
  fc := FixedCols;

  asm
    var tr;
    var td;
  end;

  if Index < FixedCols then
  begin
    if FixedRows > 0 then
    begin
      for i := 0 to FixedRows - 1 do
      begin
        asm
          tr = this.FFixedColRow.rows[i];
          td = tr.insertCell(Index);
        end;
      end;
    end;

    for i := 0 to RowCount - 1 - FixedRows do
    begin
      asm
        tr = this.FFixedCol.rows[i];
        td = tr.insertCell(Index);
      end;
    end;

    FFixedCols := FixedCols + 1;
  end
  else
  begin
    if FixedRows > 0 then
    begin
      for i := 0 to FixedRows - 1 do
      begin
        asm
          tr = this.FFixedRow.rows[i];
          td = tr.insertCell(Index - fc);
        end;
      end;
    end;

    for i := 0 to RowCount - 1 - FixedRows do
    begin
      asm
        tr = this.FNormalCells.rows[i];
        td = tr.insertCell(Index - fc);
      end;
    end;
  end;

  FColCount := FColCount + 1;

  for i := ColCount - 1 downto Index do
  begin
    if Index > 0 then
      ColWidths[i] := ColWidths[i - 1];
  end;

  ColWidths[Index] := DefaultColWidth;
end;

procedure TCustomStringGrid.InsertRow(const Index: integer);
var
  i,fr: integer;

begin
  fr := FixedRows;

  asm
    var tr;
    var td;
  end;

  if Index < FixedRows then
  begin
    if FixedCols > 0 then
    begin
      asm
        tr = this.FFixedColRow.insertRow(Index-fr);
        td = tr.insertCell(i-1);
      end;

      for i := 0 to FixedCols - 1 do
      begin
        asm
          td = tr.insertCell(i-1);
        end;
      end;
    end;

    asm
      tr = this.FFixedRow.insertRow(Index);
    end;
    for i := FixedCols to ColCount - 1 do
    begin
       asm
         tr.insertCell(i-1);
       end;
    end;
    FFixedRows := FFixedRows + 1;
  end
  else
    if Index >= FixedRows then
    begin
      if FixedCols > 0 then
      begin
        asm
          tr = this.FFixedCol.insertRow(Index-fr);
        end;

        for i := 0 to FixedCols - 1 do
        begin
          asm
            td = tr.insertCell(i-1);
          end;
        end;
      end;

      asm
        tr = this.FNormalCells.insertRow(Index-fr);
      end;

      for i := FixedCols to ColCount - 1 do
      begin
         asm
           tr.insertCell(i-1);
         end;
      end;
    end;

  FRowCount := FRowCount + 1;
  RowHeights[Index] := DefaultRowHeight;

  UpdateGridSize;
  UpdateGridAlignment;
end;

function TCustomStringGrid.IsMergedCell(const ACol, ARow: integer; var NumCol,
  NumRow: integer): boolean;
begin
  NumCol := CellColSpan(ACol,ARow);
  NumRow := CellRowSpan(ACol,ARow);

  Result := (NumCol > 1) or (NumRow > 1);
end;

{$HINTS ON}

procedure TCustomStringGrid.MouseToCell(X,Y: integer; var C, R: integer);
var
  DocX,DocY: single;
  el: TJSHTMLElement;
  cindex,rindex: integer;
begin
  cindex := -1;
  rindex := -1;

  ClientToXY(X,Y,DocX,DocY);

  el := TJSHTMLElement(document.elementFromPoint(Round(DocX),Round(DocY)));

  if Assigned(el) then
  begin
    asm
      if (el.tagName.toLowerCase() == 'td')
      {
        cindex = el.cellIndex;
        var tr = el.parentElement;
        rindex = tr.rowIndex;

        if (tr.parentElement.parentElement == this.FNormalCells)
        {
          rindex = rindex + this.FFixedRows;
          cindex = cindex + this.FFixedCols;
        }
        if (tr.parentElement.parentElement == this.FFixedCol)
        {
          rindex = rindex + this.FFixedRows;
        }
        if (tr.parentElement.parentElement == this.FFixedRow)
        {
          cindex = cindex + this.FFixedCols;
        }
      }
    end;

  end;
  R := rindex;
  C := cindex;
end;

procedure TCustomStringGrid.SaveToStrings(AStrings: TStrings; Delimiter: char = ';'; SaveFixed: boolean = false);
var
  r,c: integer;
  fr,fc: integer;
  s,rowstr: string;
begin
  fr := 0;
  fc := 0;
  if not SaveFixed then
    fc := FixedCols;

  if not SaveFixed then
    fr := FixedRows;

  for r := fr to RowCount - 1 do
  begin
    rowstr := '';
    for c := fc to ColCount - 1 do
    begin
      s := Cells[c,r];
      s := stringreplace(s,'"','""',[rfReplaceAll]);

      if (pos(Delimiter,s) > 0) or (pos(#13, s) > 0) then
      begin
        s := '"' +  s +  '"';
      end;

      if s = '' then
        s := '""';

      if rowstr = '' then
        rowstr := s
      else
        rowstr := rowstr + Delimiter + s;
    end;
    AStrings.Add(rowstr);
  end;
end;

procedure TCustomStringGrid.SaveToCSV(const AFileName: string; Delimiter: char = ';'; SaveFixed: boolean = false);
var
  sl: TStringList;
begin
  sl := TStringList.Create;
  try
    SaveToStrings(sl, Delimiter, SaveFixed);
    sl.SaveToFile(AFileName);
  finally
    sl.Free;
  end;
end;


procedure TCustomStringGrid.LoadFromCSV(const AURL: string; Delimiter: char = ';'; LoadFixed: boolean = false);
begin
  FDelimiter := Delimiter;
  FLoadFixed := LoadFixed;

  FReq := TJSXMLHttpRequest.new;
  FReq.addEventListener('load',@DoHttpLoadCsv);
  FReq.addEventListener('abort',@DoHttpAbort);
  FReq.open('GET', AURL);
  DoHttpRequest(FReq);
  FReq.send;
end;

function TCustomStringGrid.LoadFromCSVAsync(const AURL: string; Delimiter: char = ';'; LoadFixed: boolean = false): TJSPromise;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)

      function DoLoad(Event: TEventListenerEvent): boolean;
      begin
        if DoHttpLoadCsv(Event) then
          ASuccess(TJSXMLHttpRequest(Event.target))
        else
          AFailed(TJSXMLHttpRequest(Event.target));
        Result := true;
      end;

      function DoError(Event: TEventListenerEvent): boolean;
      begin
        AFailed(TJSXMLHttpRequest(Event.target));
        Result := true;
      end;

    begin
      FDelimiter := Delimiter;
      FLoadFixed := LoadFixed;
      FReq := TJSXMLHttpRequest.new;
      FReq.addEventListener('load',@DoLoad);
      FReq.addEventListener('abort',@DoError);
      FReq.addEventListener('timeout',@DoError);
      FReq.addEventListener('error',@DoError);

      FReq.open('GET', AURL);
      DoHttpRequest(FReq);
      FReq.send;
    end);

end;

procedure TCustomStringGrid.LoadFromJSON(const AURL: string; ADataNode: string = '');
begin
  FDataNode := ADataNode;
  FReq := TJSXMLHttpRequest.new;
  FReq.addEventListener('load',@DoHttpLoadJson);
  FReq.addEventListener('abort',@DoHttpAbort);
  FReq.open('GET', AURL);
  DoHttpRequest(FReq);
  FReq.send;
end;

{$HINTS OFF}
procedure TCustomStringGrid.MergeCells(const ACol, ARow, NumCol,
  NumRow: integer);
var
  i: integer;
  j: integer;
  el,mel: TJSHTMLElement;
  cw: integer;
begin
  FHasMergedCells := true;
  mel := CellRealElement(ACol,ARow);

  for i := NumCol downto 1 do
  begin
    for j := NumRow downto 1 do
    begin
      if (i>1) or (j>1) then
      begin
        el := CellRealElement(ACol + i - 1,ARow + j - 1);
        asm
          el.remove();
        end;
      end;
    end;
  end;

  if NumCol > 1 then
    mel['colspan'] := NumCol.ToString;
  if NumRow > 1 then
    mel['rowspan'] := NumRow.ToString;

  if ARow in [0,1] then
  begin
    cw := 0;
    for i := ACol to ACol + NumCol - 1 do
    begin
      cw := cw + ColWidths[i] - CELLPADDING;
      if ((ARow = 0) and (goFixedVertLine in Options)) or
         ((ARow = 1) and (goVertLine in Options)) then
        if NumCol >  1 then
          inc(cw);
    end;

    mel.style.setProperty('width', cw.ToString+'px');
    mel.style.setProperty('max-width', cw.ToString+'px');
  end;
end;
{$HINTS ON}

procedure TCustomStringGrid.SplitCells(const ACol, ARow: integer);
var
  el,td,split: TJSElement;
  i,j,NumRow,NumCol: integer;
begin
  if IsMergedCell(ACol,ARow, NumCol,NumRow) then
  begin
    split := CellRealElement(ACol,ARow);

    for i := 1 to Max(1,NumRow) do
    begin
      el := CellRealElement(ACol + NumCol,ARow + i - 1);

      for j := 1 to Max(1,NumCol) do
      begin
        if (j = 1) and (i = 1) then
          Continue;
        td := document.createElement('TD');
        el.parentNode.InsertBefore(td,el);
      end;
    end;

    split['colspan'] := '';
    split['rowspan'] := '';

    if ARow = 0 then
    begin
      for i := ACol to ACol + NumCol - 1 do
        ColWidths[i] := ColWidths[i];
    end;
  end;
end;


procedure TCustomStringGrid.MouseMove(Shift: TShiftState; X, Y: Integer);
begin
  inherited;
  if (FStartCell.X <> -1) and (FStartCell.Y <> -1) then
  begin
    if (Y > Height - 10) then
    begin
      TopRow := TopRow + 1;
      Selection := GridRect(Selection.Left, min(Selection.Top, Selection.Bottom), Selection.Right, max(Selection.Top, Selection.Bottom) + 1);
    end;

    if (FixedRows > 0) and (Y < RowHeights[0]) and (TopRow > FixedRows) then
    begin
      TopRow := TopRow - 1;
      Selection := GridRect(Selection.Left, min(Selection.Top, Selection.Bottom) - 1, Selection.Right, max(Selection.Top, Selection.Bottom));
    end;

    if (X > Width - 10) then
    begin
      LeftCol := LeftCol + 1;
      Selection := GridRect(min(Selection.Left, Selection.Right), Selection.Top, max(Selection.Left, Selection.Right) + 1, Selection.Bottom);
    end;

    if (FixedCols > 0) and (X < ColWidths[0]) and (LeftCol > FixedCols) then
    begin
      LeftCol := LeftCol - 1;
      Selection := GridRect(min(Selection.Left, Selection.Right) - 1, Selection.Top, max(Selection.Left, Selection.Right), Selection.Bottom);
    end;

  end;
end;

function TCustomStringGrid.NormalColWidth: integer;
var
  i: integer;
begin
  Result := 1;

  for i := FixedCols to ColCount - 1 do
    Result := Result + ColWidths[i] + 1 - CELLPADDING;
end;

function TCustomStringGrid.NormalRowHeight: integer;
var
  i: integer;
begin
  Result := 0;

  for i := FixedRows to RowCount - 1 do
    Result := Result + RowHeights[i];
end;


{$HINTS OFF}
procedure TCustomStringGrid.RemoveButton(const ACol, ARow: integer);
var
  el: TJSElement;
begin
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetButtonElement(ACol,ARow);
    if Assigned(el) then
    begin
      el.removeEventListener('click',FHandleCheckClickPtr);
      asm
        el.remove();
      end;
    end;
  end;
end;

procedure TCustomStringGrid.RemoveCanvas(const ACol, ARow: integer);
var
  el: TJSElement;
begin
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetCanvasElement(ACol,ARow);
    if Assigned(el) then
    begin
      asm
        el.remove();
      end;
    end;
  end;
end;


procedure TCustomStringGrid.RemoveCheckBox(const ACol, ARow: integer);
var
  el: TJSElement;
begin
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetCheckElement(ACol,ARow);
    if Assigned(el) then
    begin
      el.removeEventListener('click',FHandleCheckClickPtr);
      asm
        el.remove();
      end;
    end;
  end;
end;

procedure TCustomStringGrid.RemoveColumn(const Index: integer);
var
  i,fc: integer;
begin
  fc := FixedCols;

  asm
    var tr;
    var td;
  end;

  if Index < FixedCols then
  begin
    if FixedRows > 0 then
    begin
      for i := 0 to FixedRows - 1 do
      begin
        asm
          tr = this.FFixedColRow.rows[i];
          td = tr.deleteCell(Index);
        end;
      end;
    end;

    for i := 0 to RowCount - 1 - FixedRows do
    begin
      asm
        tr = this.FFixedCol.rows[i];
        td = tr.deleteCell(Index);
      end;
    end;

    FFixedCols := FixedCols - 1;
  end
  else
  begin
    if FixedRows > 0 then
    begin
      for i := 0 to FixedRows - 1 do
      begin
        asm
          tr = this.FFixedRow.rows[i];
          td = tr.deleteCell(Index - fc);
        end;
      end;
    end;

    for i := 0 to RowCount - 1 - FixedRows do
    begin
      asm
        tr = this.FNormalCells.rows[i];
        td = tr.deleteCell(Index - fc);
      end;
    end;
  end;

  FColCount := FColCount - 1;

  for i := Index to ColCount - 1 do
  begin
    ColWidths[i] := ColWidths[i + 1];
  end;
end;

procedure TCustomStringGrid.RemoveProgress(const ACol, ARow: integer);
var
  el: TJSElement;
begin
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetProgressElement(ACol,ARow);
    asm
      el.remove();
    end;
  end;
end;

procedure TCustomStringGrid.RemoveRow(const Index: integer);
var
  td,tr: TJSHTMLElement;
  fr: integer;
  i: integer;
begin
  if Index >= RowCount then
    raise Exception.Create('Invalid row index');

  fr := FFixedRows;

  if Index < FixedRows then
  begin
    if FixedCols > 0 then
    begin
      asm
        this.FFixedColRow.deleteRow(Index);
      end;
    end;

    asm
      this.FFixedRow.deleteRow(Index);
    end;

  end
  else
  begin
    if FixedCols > 0 then
    begin
      asm
        this.FFixedCol.deleteRow(Index -fr);
      end;
    end;

    asm
      this.FNormalCells.deleteRow(Index - fr);
    end;
  end;

  if FRowHeights.Count = RowCount then
    FRowHeights.Delete(Index);

  FRowCount := FRowCount - 1;

  RowHeights[0] := RowHeights[0];

  UpdateGridSize;
  UpdateGridAlignment;
end;

procedure TCustomStringGrid.RemoveSelection(ACell: TJSElement);
begin
  if ShowSelection then
    ACell.classlist.remove('selected');
end;

procedure TCustomStringGrid.RemoveSortIndicator(const ACol, ARow: integer);
var
  el: TJSElement;
  sid: string;

begin
  sid := Name + 'si'+ACol.ToString+'_'+ARow.ToString;

  el := document.getElementById(sid);

  if Assigned(el) then
     el.parentElement.removeChild(el);
end;

procedure TCustomStringGrid.RenderGrid;
var
  res: TJSHTMLElement;
  i,j,k,fw,fh,nw,nh,nwvis,nhvis,nwvisnoscroll,nhvisnoscroll: integer;
  tr, td, span, cellspan: TJSElement;
  fixcol,fixtxtcol: string;
  dynstyle: string;
  vscroll,hscroll: boolean;
  LAutoSizeW, LAutoSizeH: boolean;
  ControlName: string;
begin
  while FColWidths.Count <= ColCount do
    FColWidths.Add(DefaultColWidth);

  if FOrigID = '' then
  begin
    FOrigID := GetID;
    if FOrigID = '' then
      FOrigID := Name;
  end;

  if not Assigned(FGrid) then
  begin
    if IsLinked and Assigned(ElementHandle.firstElementChild) then
    begin
      res := TJSHTMLElement(ElementHandle.firstElementChild);
    end
    else
      res := TJSHTMLElement(document.createElement('TABLE'));

    res.setAttribute('id', FOrigID + 'tbl');

    if ElementTableClassName = '' then
    begin
      res.setAttribute('class','table.' + StylePrefix + Name);
      res.style.setProperty('border-collapse','collapse');
      res.style.setProperty('border-spacing','0px');
      res.style.setProperty('border','0px');
    end
    else
      res.setAttribute('class',ElementTableClassName);

    FGrid := res;

    SetHTMLElementFont(res, Font, not ((ElementClassName = '') and (ElementFont = efProperty)) );

    AddInstanceStyle(StylePrefix + FOrigID, GetGridCSS(FOrigID + 'tbl'));
  end
  else
    Exit;

  LAutoSizeW := WidthStyle = ssAuto;
  LAutoSizeH := HeightStyle = ssAuto;
  ControlName := FOrigID + 'tbl';

  asm
    var td = undefined;
    var tr = undefined;
    var span = undefined;
    var cellspan = undefined;
    this.FFixedColRow = document.createElement('TABLE');
    this.FFixedColRow.setAttribute('id', ControlName + 'ResizeFixedRowTable');
    this.FFixedColRow.style.setProperty('border-collapse','collapse');
    this.FFixedColRow.style.setProperty('border-spacing','0px');

    this.FFixedCol = document.createElement('TABLE');
    this.FFixedCol.setAttribute('id', ControlName + 'ResizeFixedColTable');
    this.FFixedCol.style.setProperty('border-collapse','collapse');
    this.FFixedCol.style.setProperty('border-spacing','0px');

    this.FFixedRow = document.createElement('TABLE');
    this.FFixedRow.setAttribute('id', ControlName + 'ResizeRowTable');
    this.FFixedRow.style.setProperty('border-collapse','collapse');
    this.FFixedRow.style.setProperty('border-spacing','0px');
    this.FFixedRow.style.setProperty('border','0px');

    this.FNormalCells = document.createElement('TABLE');
    this.FNormalCells.setAttribute('id', ControlName + 'NormalCellTable');
    this.FNormalCells.style.setProperty('border-collapse','collapse');
    this.FNormalCells.style.setProperty('border-spacing','0px');
  end;

  // make normal grid cells focusable
  FNormalCells.setAttribute('tabindex',IntToStr(TabOrder));
  TJSHTMLElement(FNormalCells).style.setProperty('outline','none');
  FNormalCells.addEventListener('click',FHandleClickPtr);
  FNormalCells.addEventListener('mousedown',FHandleMouseDownPtr);
  FNormalCells.addEventListener('mousemove',FHandleMouseMovePtr);
  FNormalCells.addEventListener('mouseup',FHandleMouseUpPtr);

  FFixedCol.addEventListener('click',FHandleFixedClickPtr);
  FFixedRow.addEventListener('click',FHandleFixedClickPtr);
  FFixedColRow.addEventListener('click',FHandleFixedClickPtr);

  ElementHandle.appendChild(res);

  fw := FixedColWidth;
  fh := FixedRowHeight;

  nw := NormalColWidth;
  nh := NormalRowHeight;

  nwvis := Width - FixedColWidth;
  nhvis := Height - FixedRowHeight;

  if BorderStyle = bsSingle then
  begin
    nwvis := nwvis - 2;
    nhvis := nhvis - 2;
  end;

  nwvisnoscroll := nwvis - GetScrollBarWidth;

  if FixedColWidth + NormalColWidth < Width then
    nhvisnoscroll := nhvis
  else
    nhvisnoscroll := nhvis - GetScrollBarHeight;

  fixcol := ColorToHTML(FFixedColor);
  fixtxtcol := ColorToHTML(FixedTextColor);

  // insert the main four grid cells
  asm
    tr = res.insertRow(0);
    td = tr.insertCell(0);
    td.style.border = "0px";
    td = tr.insertCell(1);
    td.style.border = "0px";
    tr = res.insertRow(1);
    td = tr.insertCell(0);
    td.style.border = "0px";
    td = tr.insertCell(1);
    td.style.border = "0px";
  end;

  // fill fixedrow cells
  if FixedRows > 0 then
  begin
    k := 0;

    // fixedcol/fixedrow
    if FixedCols > 0 then
    begin
      k := 1;
      asm
        td = res.rows[0].cells[0];
        span = document.createElement('DIV');
//        span.style.width = fw + "px";
        span.style.height = fh + "px";
        span.style.overflow = "hidden";
        span.style.backgroundColor = fixcol;
        span.style.color = fixtxtcol;
        td.appendChild(span);
        span.appendChild(this.FFixedColRow);
      end;
    end
    else
    begin
      asm
        td = res.rows[0].cells[0];
        td.style.border = "0px";
        td = res.rows[1].cells[0];
        td.style.border = "0px";
      end;

    end;

    //
    asm
      td = res.rows[0].cells[1];
      td.style.backgroundColor = fixcol;
      td.style.color = fixtxtcol;

      span = document.createElement('DIV');
      span.style.height = fh + "px";
      if (!LAutoSizeW) {
        span.style.width = nwvis + "px";
      }

      span.style.overflow = "hidden";
      span.style.backgroundColor = fixcol;
      span.style.color = fixtxtcol;

      td.appendChild(span);

      cellspan = document.createElement('DIV');
      cellspan.style.height = fh + "px";
      if (!LAutoSizeW) {
        cellspan.style.width = nw + "px";
      }

      span.appendChild(cellspan);
      cellspan.appendChild(this.FFixedRow);
    end;

    if (FixedCols > 0) then
    begin
      for i := 0 to FixedRows - 1 do
      begin
        asm
          tr = this.FFixedColRow.insertRow(i);
        end;

        for j := 0 to FixedCols - 1 do
        begin
          asm
            td = tr.insertCell(j);
            td.setAttribute('class','fixed');
          end;

          SetAlignAttr(ColAlignments[j], td);
        end;
      end;
    end;

    // fill top cell
    for i := 0 to FixedRows - 1 do
    begin
      asm
        tr = this.FFixedRow.insertRow(i);
      end;

      for j := FixedCols to ColCount - 1 do
      begin
        k := j - FixedCols;
        asm
          td = tr.insertCell(k);
          td.setAttribute('class','fixed');
        end;

        SetAlignAttr(ColAlignments[j], td);
      end;
    end;
  end;

  k := 0;

  // fill left cell
  if FixedCols > 0 then
  begin
    k := 1;
    asm
      //tr = res.insertRow(res.rows.length);
      //td = tr.insertCell(0);

      td = res.rows[1].cells[0];

      td.style.backgroundColor = fixcol;
      td.style.color = fixtxtcol;
      td.setAttribute('class','fixed');

      span = document.createElement('DIV');
      if (!LAutoSizeW) {
      //span.style.width = fw + "px";
      }
      span.style.height = nhvisnoscroll + "px";
      span.style.overflow = "hidden";
      span.style.backgroundColor = fixcol;
      span.style.color = fixtxtcol;

      td.setAttribute('valign','top');

      td.appendChild(span);
      span.appendChild(this.FFixedCol);

    end;

    SetAlignAttr(ColAlignments[0], td);

    for i := FixedRows to RowCount - 1 do
    begin
      j := i - FixedRows;
      asm
        tr = this.FFixedCol.insertRow(j);
      end;

      for j := 0 to FixedCols - 1 do
      begin
        asm
          td = tr.insertCell(j);
          td.setAttribute('class','fixed');
        end;

        SetAlignAttr(ColAlignments[j], td);

        //TJSHTMLElement(td).innerHTML := inttostr(j)+':'+inttostr(i);
      end;
    end;
  end;

  if FixedCols = 0 then
  begin
    asm
      //tr = res.insertRow(res.rows.length);
      //td = tr.insertCell(0);
      td = res.rows[1].cells[1];
    end
  end
  else
  begin
    asm
      //tr = res.rows[1];
      //td = tr.insertCell(1);
      td = res.rows[1].cells[1];
    end;
  end;

  vscroll := ((ssVertical = ScrollBars) or (ssBoth = ScrollBars));
  hscroll := ((ssHorizontal = ScrollBars) or (ssBoth = ScrollBars));

  asm
    span = document.createElement('DIV');
    // span.style.backgroundColor = "#FFFFFF";

    if (vscroll) {
      span.style.overflowY = "auto";
    }
    else
    {
      span.style.overflowY = "hidden";
    }

    if (hscroll) {
      span.style.overflowX = "auto";
    }
    else {
      span.style.overflowX = "hidden";
    }

    if (!LAutoSizeW) {
      span.style.width = nwvis + "px";
    }

    if (LAutoSizeH) {
      span.style.overflow = "";
    }

    span.style.height = nhvis + "px";
    td.appendChild(span);

    cellspan = document.createElement('DIV');
      if (!LAutoSizeW) {
        cellspan.style.width = nw + "px"; }
    cellspan.style.height = nh + "px";
    span.parentElement.style.verticalAlign = "top";
    span.appendChild(cellspan);
    cellspan.appendChild(this.FNormalCells);
  end;

  span.addEventListener('scroll',@HandleScroll);

  for i := FixedRows to RowCount - 1 do
  begin
    k := i - FixedRows;
    asm
      tr = this.FNormalCells.insertRow(k);
    end;

    for j := FixedCols to ColCount - 1 do
    begin
      k := j - FixedCols;

      asm
        td = tr.insertCell(k);
      end;

      SetAlignAttr(ColAlignments[j], td);
    end;
  end;

  FNormalCells.parentElement.parentElement.addEventListener('keydown',@HandleKeydown);
  FNormalCells.addEventListener('focus',FHandleFocusPtr);
  FNormalCells.addEventListener('blur',FHandleBlurPtr);

  EnableColResize;

  if DragMode = dmAutomatic then
    EnableDrag;
end;

procedure TCustomStringGrid.RenderGridScript;
begin
  AddControlScriptSource(
  'var TMSStringGridpageX,TMSStringGridcurCol,TMSStringGridnxtCol,TMSStringGridcurColWidth,TMSStringGridnxtColWidth,TMSStringGridautoWidth,GridNormalCells,GridFixedCells,GridFixedColCells,GridFixedColRows,GridColWidths,GridFixedCols,TMSStringGridCurID;'+
  'var TMSStringGridTable,TMSStringGridcurTableWidth,TMSStringGridID,TMSStringGridFullWidth;'+
  'TMSStringGridautoWidth = false;'+
  'TMSStringGridCurID = "";'+
  'ResizeMainGrid = true;'+

  'function TMSStringGridupdateColSize(Cells, Col) {'+
  '    if (Cells){'+
  '     for (i = 0; i < Cells.rows.length; i++){'+
  '       td = Cells.rows[i].cells[Col.cellIndex];'+
  '       td.style.position = "relative";'+
  '       td.style.width = Col.style.width;'+
  '       td.style.maxWidth = Col.style.width;'+
  '      }'+
  '    }'+
  '}'+

  'function TMSStringGridresizeColumn(e) {'+
  '   if ((TMSStringGridcurCol) && (TMSStringGridCurID.indexOf(TMSStringGridID) == 0)) {'+

  '    var diffX = e.pageX - TMSStringGridpageX;'+

  '    newColWidth = (TMSStringGridcurColWidth + diffX);'+
  '    if (newColWidth < 10)'+
  '      newColWidth = 10;'+

  '    if (TMSStringGridCurID.indexOf("ResizeRow") > 0) {'+
  //resize normal columns
  '	    TMSStringGridcurCol.style.width = newColWidth + "px";'+
  '	    TMSStringGridcurCol.style.maxWidth = TMSStringGridcurCol.style.width;'+
  '     GridColWidths.Put(TMSStringGridcurCol.cellIndex + GridFixedCols, parseInt(TMSStringGridcurCol.style.width));'+
  '     TMSStringGridupdateColSize(GridFixedCells, TMSStringGridcurCol);'+
  '     TMSStringGridupdateColSize(GridNormalCells, TMSStringGridcurCol);'+

  '     TMSStringGridcurTable.style.width = (TMSStringGridcurTableWidth + diffX) + "px";'+
  '     GridNormalCells.style.width = (TMSStringGridcurTableWidth + diffX) + "px";'+
  '    } else {'+
  //resize fixed columns
  '     TMSStringGridfullWidth = document.getElementById(TMSStringGridID).offsetWidth;'+
  '     newCellsWidth = (TMSStringGridfullWidth - (TMSStringGridcurTableWidth + diffX));'+

  '     if ((newCellsWidth > 10) && (newColWidth > 10)) {'+
  '	     TMSStringGridcurCol.style.width = newColWidth + "px";'+
  '	     TMSStringGridcurCol.style.maxWidth = TMSStringGridcurCol.style.width;'+
  '      TMSStringGridupdateColSize(GridFixedColRows, TMSStringGridcurCol);'+
  '      TMSStringGridupdateColSize(GridFixedColCells, TMSStringGridcurCol);'+
  '      GridColWidths.Put(TMSStringGridcurCol.cellIndex, parseInt(TMSStringGridcurCol.style.width));'+
  '      TMSStringGridcurTable.style.width = (TMSStringGridcurTableWidth + diffX) + "px";'+
  '      GridFixedColCells.style.width = (TMSStringGridcurTableWidth + diffX) + "px";'+

  '      if ((!TMSStringGridautoWidth) && (newColWidth > 10)){' +
  '       GridFixedCells.parentElement.style.width = newCellsWidth + "px";'+
  '       GridFixedCells.parentElement.parentElement.style.width = newCellsWidth + "px";'+

  '       GridNormalCells.parentElement.style.width = newCellsWidth + "px";'+
  '       GridNormalCells.parentElement.parentElement.style.width = newCellsWidth + "px";'+
  '      }'+
  '     }'+
  '    }'+
  '   }'+
  ' }'+

  'function TMSStringGridisSizing() {'+
  '   if ((TMSStringGridcurCol) && (TMSStringGridCurID.indexOf(TMSStringGridID) == 0))'+
  '   {' +
  '     if (TMSStringGridCurID.indexOf("ResizeRow") > 0)'+
  '     {' +
  '       return(TMSStringGridcurCol.cellIndex + GridFixedCols)'+
  '     }' +
  '     else'+
  '     {' +
  '      return(TMSStringGridcurCol.cellIndex)'+
  '     }' +
  '   }'+
  '  else  { return -1; }'+
  '   }' +

  'function TMSStringGridresetResize() {'+
  '   TMSStringGridID = "";'+
  '   TMSStringGridFullWidth = undefined;'+
  '   TMSStringGridCurID = "";'+
  '   TMSStringGridcurCol = undefined;'+
  '   TMSStringGridnxtCol = undefined;'+
  '   TMSStringGridpageX = undefined;'+
  '   TMSStringGridnxtColWidth = undefined;'+
  '   TMSStringGridcurColWidth = undefined;'+
  '   TMSStringGridautoWidth = false;'+
  '   GridFixedCells = undefined;'+
  '   GridFixedColCells = undefined;'+
  '   GridFixedColRows = undefined;'+
  '   GridNormalCells = undefined;'+
  '   GridColWidths = undefined;'+
  '   GridFixedCols = undefined;'+
  ' }'+

  ' function divmousedown(e) {'+
  '   TMSStringGridCurID = e.target.id;'+
  '   TMSStringGridcurCol = e.target.parentElement;'+
  '   TMSStringGridnxtCol = TMSStringGridcurCol.nextElementSibling;'+
  '   TMSStringGridpageX = e.pageX;'+

  '   TMSStringGridcurTable = TMSStringGridcurCol.parentElement.parentElement.parentElement;'+
  '   TMSStringGridcurTableWidth = TMSStringGridcurTable.offsetWidth;'+

  '   TMSStringGridcurColWidth = TMSStringGridcurCol.offsetWidth;'+
  '   if (TMSStringGridnxtCol)'+
  '    TMSStringGridnxtColWidth = TMSStringGridnxtCol.offsetWidth;'+
  ' }'+#13#10+
  ' function divmouseover(e) {'+
  '   e.target.style.borderRight = "2px solid #0000ff";}'+#13#10+

  ' function divmouseout(e) {'+
  '   e.target.style.borderRight = "";}'+#13#10+

  ' function divmouseup(e) {'+
  '   TMSStringGridresetResize();}'+#13#10+

  'function TMSStringGridresizableGrid(tablename, enabled) {'+
  ' var table = document.getElementById(tablename + "Table");'+
  ' if (!table) return;' +
  ' var row = table.getElementsByTagName("tr")[0],'+
  ' cols = row ? row.children : undefined;'+
  ' if (!cols) return;'+
  ' for (var i=0;i<cols.length;i++){'+
  '  var coldiv = document.getElementById(tablename + "Div" + i);'+

  '  if (coldiv) {'+
  '    coldiv.removeEventListener("mousedown",divmousedown);'+
  '    coldiv.removeEventListener("mouseover",divmouseover);'+
  '    coldiv.removeEventListener("mouseout",divmouseout);'+
  '    coldiv.removeEventListener("mouseup",divmouseup);'+
  '    coldiv.remove(); coldiv = null; }'+

  '  if (enabled) {'+
  '    var div = createDiv();'+
  '    cols[i].appendChild(div);'+
  '    cols[i].style.position = "relative";'+
  '    div.id = tablename + "Div" + i;'+
  '    setListeners(div);'+
  '  }'+
  ' }'+

  ' function setListeners(div){'+
  '  div.addEventListener("mousedown", divmousedown);'+
  '  div.addEventListener("mouseover", divmouseover);'+
  '  div.addEventListener("mouseout", divmouseout);'+
  '  div.addEventListener("mouseup", divmouseup);'+

(*
  '  div.addEventListener("mousedown", function (e) {'+
  '   TMSStringGridCurID = e.target.id;'+
  '   TMSStringGridcurCol = e.target.parentElement;'+
  '   TMSStringGridnxtCol = TMSStringGridcurCol.nextElementSibling;'+
  '   TMSStringGridpageX = e.pageX;'+

  '   TMSStringGridcurTable = TMSStringGridcurCol.parentElement.parentElement.parentElement;'+
  '   TMSStringGridcurTableWidth = TMSStringGridcurTable.offsetWidth;'+

  '   TMSStringGridcurColWidth = TMSStringGridcurCol.offsetWidth;'+
  '   if (TMSStringGridnxtCol)'+
  '    TMSStringGridnxtColWidth = TMSStringGridnxtCol.offsetWidth;'+
  '  });'+
  '  div.addEventListener("mouseover", function (e) {'+
  '   e.target.style.borderRight = "2px solid #0000ff";'+
  '  });'+

  '  div.addEventListener("mouseout", function (e) {'+
  '   e.target.style.borderRight = "";'+
  '  });'+

  '  document.addEventListener("mousemove", function (e) {'+
  '   TMSStringGridresizeColumn(e);'+
  '  });'+

  '  document.addEventListener("mouseup", function (e) {'+
  '   TMSStringGridresetResize();'+
  '  });'+
*)
  ' }'+

  ' function createDiv(){'+
  '  var div = document.createElement("div");'+
  '  div.style.top = 0;'+
  '  div.style.right = 0;'+
  '  div.style.width = "10px";'+
  '  div.style.position = "absolute";'+
  '  div.style.cursor = "col-resize";'+
  '  div.style.userSelect = "none";'+
  '  div.style.height = "100%";'+
//  '  div.style.border = "1px solid red";'+
  '  div.innerHTML = "&nbsp;";'+
  '  return div;'+
  ' }'+

  '};');
end;

procedure TCustomStringGrid.RenderGridSize;
var
  fw,fh,nw,nh,nwvis,nhvis,nwvisnoscroll,nhvisnoscroll: integer;

begin
  if not Assigned(FGrid) then
    Exit;

  fw := FixedColWidth;
  fh := FixedRowHeight;

  nw := NormalColWidth;
  nh := NormalRowHeight;

  nwvis := Width - FixedColWidth;
  nhvis := Height - FixedRowHeight;

  if BorderStyle = bsSingle then
  begin
    nwvis := nwvis - 2;
    nhvis := nhvis - 2;
  end;

  nwvisnoscroll := nwvis - GetScrollBarWidth;

  if FixedColWidth + NormalColWidth < Width then
    nhvisnoscroll := nhvis
  else
    nhvisnoscroll := nhvis - GetScrollBarHeight;

  if (FixedRows > 0) and (WidthStyle = ssAbsolute) then
  begin
    asm
      var td = this.FGrid.rows[0].cells[1];
      if (td!=null) {
      var span = td.firstChild;
      span.style.width = nwvisnoscroll + "px";
      span.style.overflow = "hidden";
      }
    end;
  end;

  if (FixedCols > 0) and (HeightStyle = ssAbsolute) then
  begin
    asm
      var td = this.FGrid.rows[1].cells[0];
      if (td!=null) {
      var span = td.firstChild;
      span.style.height = nhvisnoscroll + "px";
      span.style.overflow = "hidden";
      }
    end;
  end;
end;

procedure TCustomStringGrid.Resize;
begin
  inherited;
  RenderGridSize;
end;

procedure TCustomStringGrid.RowCountChanged(delta: integer);
var
  r: integer;
  fr,fc: integer;
  nh: integer;

  procedure SetColAlign(column: integer; td: TJSHTMLElement);
  begin
    SetAlignAttr(ColAlignments[column], td);
  end;

begin
  if IsUpdating and (csLoading in ComponentState) then
    Exit;

  if not Assigned(FNormalCells) then
    Exit;

  fr := FixedRows;
  fc := FixedCols;

  nh := NormalRowHeight;

  if (1 < 0) then  // to avoid issue with pas2js code optimization
    SetColAlign(0,nil);


  if delta > 0 then
  begin

    asm
      var i = 0;
      var j = 0;
      var tr = undefined;
      var td = undefined;
      var l = this.FNormalCells.rows.length;

      this.FNormalCells.parentElement.style.setProperty('height', nh + 'px');

      for(i = 0;i < delta; i++)
      {
         tr = this.FNormalCells.insertRow(l);
         for(j = this.FFixedCols; j < this.FColCount; j++)
         {
            td = tr.insertCell(0);
            SetColAlign(j, td);
         }

         if (fc > 0) {
           tr = this.FFixedCol.insertRow(l);
           for(j = 0; j < this.FFixedCols; j++)
           {
             td = tr.insertCell(0);
             td.setAttribute('class','fixed');
             SetColAlign(j, td);
           }
         }
      }
    end;

    BeginUpdate;

    for r := 1 to delta do
    begin
      RowHeights[RowCount - r] := DefaultRowHeight;
    end;

    if DragMode = dmAutomatic then
      EnableDrag;

    EndUpdate;
  end;

  if delta < 0 then
  begin
    asm
      var i = 0;
      var l = this.FNormalCells.rows.length;

      if (l > 0) {

      for(i = 0;i < -delta; i++)
      {
        this.FNormalCells.deleteRow(l - i - 1);

        if (fc > 0) {
          this.FFixedCol.deleteRow(l - i - 1);
        }
      }
      }
    end;

    if RowCount > 0 then
      RowHeights[RowCount - 1] := RowHeights[RowCount - 1];
  end;
end;

function TCustomStringGrid.RowOffset: integer;
begin
  Result := FixedRows;
end;

procedure TCustomStringGrid.RowsChanged(Sender: TObject);
begin
  SetRows(FRowsIndex, FRows);
end;

procedure TCustomStringGrid.SelectCell(ACol, ARow: integer);
var
  rc,rr: integer;
begin
  Selection := GridRect(ACol, ARow, ACol, ARow);

  rc := ACol - FixedCols;
  rr := ARow - FixedRows;

  if (rc >= 0) and (rr >= 0) then
  begin
    asm
      //if all rows are deleted then no indicator is displayed
      if (this.FNormalCells.rows.length <= 0)
      {
        this.FSelectedCell = null;
        this.UpdateIndicator(this.FFixedRows);
        return;
      }
      this.FSelectedCell = this.FNormalCells.rows[rr].cells[rc];
    end;
  end
  else
  begin
    asm
      this.FSelectedCell = null;
    end;
  end;
end;

procedure TCustomStringGrid.SelectSortIndicator(const ACol, ARow: integer;
  ASelect: boolean);
var
  el: TJSElement;
  sid: string;

begin
  sid := Name + 'si'+ACol.ToString+'_'+ARow.ToString;

  el := document.getElementById(sid);

  if Assigned(el) then
  begin
    if ASelect then
      TJSHTMLElement(el).style.setProperty('color',ColorToHTML(SelectionColor))
    else
      TJSHTMLElement(el).style.removeProperty('color');
  end;
end;

procedure TCustomStringGrid.SetAlignAttr(AAlign: TAlignment;
  AElement: TJSElement);
begin
  case AAlign of
  taLeftJustify: AElement.removeAttribute('align');
  taCenter: AElement['align'] := 'center';
  taRightJustify: AElement['align'] := 'right';
  end;
end;

procedure TCustomStringGrid.SetCells(ACol, ARow: integer; const Value: string);
var
  rc,rr,rh: integer;
  StrVal: string;
  el: TJSHTMLElement;

begin
  if (ARow < 0) or (ACol < 0) then
    Exit;

  StrVal := Value;
  if pos(#13, Value) > 0 then
  begin
    StrVal := StringReplace(StrVal, #13, '<BR>', [rfReplaceAll]);
    StrVal := StringReplace(StrVal, #10, '<BR>', [rfReplaceAll]);
  end
  else
  if pos(#10, Value) > 0 then
  begin
    StrVal := StringReplace(StrVal, #10, '<BR>', [rfReplaceAll]);
  end;

  if WordWrap then
  begin
    rh := RowHeights[ARow] - 2;
    StrVal := '<DIV class="cell" style="height:'+rh.ToString+'px;max-height:' + rh.ToString+'px">' + StrVal +  '</DIV>';
  end;

  el := CellRealElement(ACol,ARow);
  if Assigned(el) then
    el.innerHTML := StrVal;

    (*

  if (ACol < FixedCols) and (ARow >= FixedRows) then
  begin
    rr := ARow - FixedRows;
    rc := ACol;

    asm
      var td = this.FFixedCol.rows[rr].cells[rc];
      td.innerHTML = StrVal;
    end;
  end;

  if (ARow < FixedRows) and (ACol >= FixedCols) then
  begin
    rc := ACol - FixedCols;
    rr := ARow;

    if Assigned(FFixedRow) then
    begin
      asm
        var tr = this.FFixedRow.rows[rr];
        if (tr != undefined) {
          var td = tr.cells[rc];
          td.innerHTML = StrVal;
         }
      end;
    end;
  end;

  if (ACol < FixedCols) and (ARow < FixedRows) then
  begin
    rc := ACol;
    rr := ARow;

    asm
      var td = this.FFixedColRow.rows[rr].cells[rc];
      td.innerHTML = StrVal;
    end;
  end;

  if (ACol >= FixedCols) and (ARow >= FixedRows) then
  begin
    rc := ACol - FixedCols;
    rr := ARow - FixedRows;
    asm
      var td = this.FNormalCells.rows[rr].cells[rc];
      td.innerHTML = StrVal;
    end;
  end;
    *)

  if ARow = 0 then
    EnableColResize;
end;

procedure TCustomStringGrid.SetCheckState(ACol, ARow: integer;
  const Value: boolean);
var
  el: TJSHTMLElement;
begin
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetCheckElement(ACol,ARow);
    asm
      el.checked = Value;
    end;
  end;
end;

procedure TCustomStringGrid.SetCheckEnabled(ACol, ARow: integer;
  const Value: boolean);
var
  el: TJSHTMLElement;
  v: boolean;
begin
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    v := not Value;
    el := GetCheckElement(ACol,ARow);
    asm
      el.disabled = v;
    end;
  end;
end;


procedure TCustomStringGrid.SetCol(const Value: integer);
var
  r,c: integer;
  td: TJSElement;
begin
  if (Value < ColCount) and (Value >= FixedCols) then
  begin
     if Assigned(FSelectedCell) then
     begin
       RemoveSelection(FSelectedCell);
     end;

     c := Value - FixedCols;
     r := Row - FixedRows;
     if r < 0 then
       r := 0;

     asm
       var tr = this.FNormalCells.rows[r];
       td = tr.cells[c];
     end;

     AddSelection(td);
     FSelectedCell := td;

     asm
       var pardiv = this.FNormalCells.parentElement.parentElement;

       if (td.offsetLeft < pardiv.scrollLeft) {
         pardiv.scrollLeft = td.offsetLeft;
       }

       if (td.offsetLeft + td.offsetWidth - pardiv.scrollLeft > pardiv.offsetWidth) {
         pardiv.scrollLeft = pardiv.scrollLeft + td.offsetWidth;
       }
     end;
  end;
end;

procedure TCustomStringGrid.SetColAlignments(ACol: integer;
  const Value: TAlignment);
begin
  while FColAlignments.Count <= ACol do
    FColAlignments.Add(taLeftJustify);
  FColAlignments[ACol] := Value;
  UpdateGridAlignment;
end;

procedure TCustomStringGrid.SetColWidths(ACol: integer; const Value: integer);
var
  fcw: integer;
  nw, nwvis, nwvisnoscroll: integer;
  LAutoSizeW: boolean;
begin
  while FColWidths.Count <= ACol do
    FColWidths.Add(DefaultColWidth);

  FColWidths[ACol] := Value;

  if IsUpdating then
    Exit;

  if not Assigned(FGrid) then
    Exit;

  LAutoSizeW := WidthStyle = ssAuto;
  nw := NormalColWidth;
  nwvis := Width - FixedColWidth;
  nwvisnoscroll := nwvis - GetScrollBarWidth;

  if ACol < FixedCols then
  begin
    fcw := FixedColWidth;
    asm
      var tr = this.FGrid.rows[0];
      if (tr != undefined) {
      var td = tr.cells[0];
      if (td != undefined) {
        td.style.width = fcw + "px";
        if (td.firstChild != undefined) {
        td.firstChild.style.width = fcw + "px"; }
        }
      }

      tr = this.FGrid.rows[1];
      if (tr != undefined) {
      td = tr.cells[0];
      if (td != undefined) {
        td.style.width = fcw + "px";
        if (td.firstChild != undefined) {
        td.firstChild.style.width = fcw + "px"; }
        }

      tr = this.FFixedColRow.rows[0];
      if (tr != undefined) {

      td = tr.cells[0];
      if (td != undefined) {
        td.style.width = fcw + "px";
        }
        }
      }
      tr = this.FFixedCol.rows[0];
      if (tr != undefined) {

      td = tr.cells[0];
      if (td != undefined) {
        td.style.width = fcw + "px";
        }
      }
    end;
  end;

  if ACol >= FixedCols then
  begin
    ACol := ACol - FixedCols;

    asm
      var tr = this.FFixedRow.rows[0];
      if (tr != undefined) {
        var td = tr.cells[ACol];
        td.style.width = Value + "px";
        td.style.maxwidth = Value + "px";

        if (this.FNormalCells.rows.length > 0)
        {
        var td = this.FNormalCells.rows[0].cells[ACol];
        td.style.width = Value + "px";
        td.style.maxwidth = Value + "px";
        }
      }
    end;
  end;

  asm
    var tr = this.FGrid.rows[0];
    if (tr != undefined) {
      var td = tr.cells[1];
      if ((td.firstChild != undefined) && (!LAutoSizeW)) {
      td.firstChild.style.width = nwvisnoscroll + "px";
      td.firstChild.firstChild.style.width = nw + "px";
      }
    }
    tr = this.FGrid.rows[1];
    if (tr != undefined) {
      td = tr.cells[1];
      if ((td.firstChild != undefined) && (!LAutoSizeW)) {
      td.firstChild.style.width = nwvis + "px";
      td.firstChild.firstChild.style.width = nw + "px";
      }
    }
  end;

  UpdateGridSize;
end;
{$HINTS ON}


procedure TCustomStringGrid.SetDataColumnCount(AValue: Integer);
begin
  ColCount := FixedCols + AValue;
end;

procedure TCustomStringGrid.SetDataHeader(AColumn: Integer; AValue: string);
begin
  if FixedRows > 0 then
    Cells[AColumn + FixedCols, FixedRows - 1] := AValue;
end;

procedure TCustomStringGrid.SetDataRowCount(AValue: Integer);
begin
  RowCount := FixedRows + AValue;
end;

procedure TCustomStringGrid.SetDataValue(AColumn, ARow: Integer;
  AValue: string);
begin
  Cells[AColumn + FixedCols, ARow + FixedRows] := AValue;
end;

procedure TCustomStringGrid.SetDefaultColAlignment(const Value: TAlignment);
begin
  if (FDefaultColAlignment <> Value) then
  begin
    FDefaultColAlignment := Value;
    if not (csloading in ComponentState) then
      ReRenderGrid;
  end;
end;

procedure TCustomStringGrid.SetDefaultColWidth(const Value: integer);
var
  i: integer;
begin
  FDefaultColWidth := Value;

  for i := 0 to ColCount - 1 do
    ColWidths[i] := Value;
end;

procedure TCustomStringGrid.SetDefaultRowHeight(const Value: integer);
var
  i: integer;
begin
  FDefaultRowHeight := Value;
  for i := 0 to RowCount - 1 do
    RowHeights[i] := Value;
end;

procedure TCustomStringGrid.SetEditControlValue(ACol, ARow: Longint;
  AControl: TControl; AValue: string);
begin
  if Assigned(OnSetEditControlValue) then
    OnSetEditControlValue(Self, ACol, ARow, AControl, AValue);
end;

procedure TCustomStringGrid.SetEditText(ACol, ARow: Longint;
  const AValue: string);
begin
  if Assigned(OnSetEditText) then
    OnSetEditText(Self, ACol, ARow, AValue);
end;

procedure TCustomStringGrid.SetElementTableClassName(const Value: string);
begin
  if (FElementTableClassName <> Value) then
  begin
    FElementTableClassName := Value;

    if not IsUpdating then
      ReRenderGrid;
  end;
end;

procedure TCustomStringGrid.SetFixedColor(const Value: TColor);
begin
  if (FFixedColor <> Value) then
  begin
    FFixedColor := Value;
    UpdateElementVisual;
  end;
end;

procedure TCustomStringGrid.ReRenderGrid;
begin
  if not IsLinked and Assigned(FGrid) and Assigned(FGrid.firstElementChild) then
  begin
    FGrid.removeChild(FGrid.firstChild);
  end;
  FGrid := nil;
  RenderGrid;
  UpdateGridSize;
  UpdateGridAlignment;
end;

procedure TCustomStringGrid.SetFixedCols(const Value: integer);
begin
  if (FFixedCols <> Value) then
  begin
    FFixedCols := Value;

    if not IsUpdating then
      ReRenderGrid;
  end;
end;

procedure TCustomStringGrid.SetFixedFont(const Value: TFont);
begin
  FFixedFont.Assign(Value);
end;

procedure TCustomStringGrid.SetFixedRows(const Value: integer);
begin
  if (FFixedRows <> Value) then
  begin
    FFixedRows := Value;

    if not IsUpdating then
      ReRenderGrid;
  end;
end;

procedure TCustomStringGrid.SetColor(AValue: TColor);
begin
  if (FColor <> AValue) then
  begin
    FColor := AValue;
    UpdateElement;
  end;
end;

procedure TCustomStringGrid.SetFixedTextColor(const Value: TColor);
begin
  if (FixedFont.Color <> Value) then
  begin
    FixedFont.Color := Value;
    UpdateElementVisual;
  end;
end;

procedure TCustomStringGrid.SetFocus;
begin
  if Assigned(FNormalCells) then
    TJSHTMLElement(FNormalCells).focus();
end;

procedure TCustomStringGrid.SetGridLineColor(const Value: TColor);
begin
  if (FGridLineColor <> Value) then
  begin
    FGridLineColor := Value;
    UpdateElement;
  end;
end;

procedure TCustomStringGrid.SetLeftCol(const Value: integer);
var
  i,w: integer;
begin
  w := 0;
  for i := FixedCols to Value - 1 do
    w := w + ColWidths[i] + 1 - CELLPADDING;

  if Assigned(FNormalCells) then
  begin
    FNormalCells.parentElement.parentElement.scrollLeft := w;
    if FixedCols > 0 then
      FFixedCol.parentElement.parentElement.scrollLeft := w;
  end;
end;

procedure TCustomStringGrid.SetObjects(ACol, ARow: integer;
  const Value: TObject);
var
  LObjr: TObjectRow;
begin
  while FObjectRows.Count <= ARow do
  begin
    LObjr := TObjectRow.Create;
    FObjectRows.Add(LObjr);
  end;

  LObjr := TObjectRow(FObjectRows[ARow]);

  while LObjr.Count <= ACol do
  begin
    LObjr.Add(nil);
  end;

  LObjr[ACol] := Value;
end;

procedure TCustomStringGrid.SetProgress(ACol, ARow, APosition: integer);
var
  el: TJSElement;
begin
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetProgressElement(ACol,ARow);
    if Assigned(el) then
    begin
      el['value'] := APosition.ToString;
    end;
  end;
end;

{$HINTS OFF}

procedure TCustomStringGrid.SetRange(const Value: TGridRange);
begin
  FRange.Assign(Value);
end;

procedure TCustomStringGrid.SetRow(const Value: integer);
var
  r,c,sh: integer;
  rselect: boolean;
  tr,td: TJSElement;
begin
  if (Value < RowCount) and (Value >= FixedRows) then
  begin
    rselect := (goRowSelect in Options);

    if Assigned(FSelectedCell) and rselect then
    begin
      tr := FSelectedCell.parentElement;
      RemoveSelection(tr);
    end;

    if Assigned(FSelectedCell) then
    begin
      RemoveSelection(FSelectedCell);
    end;

    c := Col - FixedCols;
    if c < 0 then
      c := 0;

    r := Value - FixedRows;

    if FixedColWidth + NormalColWidth < Width then
      sh := 0
    else
      sh := GetScrollBarHeight;

    asm
      tr = this.FNormalCells.rows[r];
      td = tr.cells[c];
    end;

    if (rselect) then
    begin
      if not isUndefined(FSelectedCell) then
        RemoveSelection(FSelectedCell);

      AddSelection(tr);
      FSelectedCell := td;
    end
    else
    begin
      FSelectedCell := td;
      AddSelection(FSelectedCell);
    end;

    asm
      var pardiv = this.FNormalCells.parentElement.parentElement;

      if (td.offsetTop < pardiv.scrollTop) {
        pardiv.scrollTop = td.offsetTop;
      }

      if (td.offsetHeight>0) {
        while (td.offsetTop + td.offsetHeight - pardiv.scrollTop > pardiv.offsetHeight - sh)  {
          pardiv.scrollTop = pardiv.scrollTop + td.offsetHeight;
        }
      }
    end;
  end;
end;

procedure TCustomStringGrid.SetRowHeights(ARow: integer; const Value: integer);
var
  frh,rh: integer;
  fh, nh, nhvis, nhvisnoscroll: integer;
begin
  while FRowHeights.Count <= ARow do
    FRowHeights.Add(DefaultRowHeight);

  FRowHeights[ARow] := Value;

  if IsUpdating then
    Exit;

  if not Assigned(FGrid) then
    Exit;

  fh := FixedRowHeight;

  nh := NormalRowHeight;

  nhvis := Height - FixedRowHeight;

  nhvisnoscroll := nhvis - GetScrollBarHeight;

  if ARow < FixedRows then
  begin
    frh := FixedRowHeight;
    asm
      var tr = this.FGrid.rows[0];
      if (tr != undefined) {
        tr.style.height = frh + "px";
        var td = tr.cells[0];
        if (td != undefined) {
          var fc = td.firstChild;
          if (fc != undefined) {
            fc.style.height = frh + "px";
          }
          td = tr.cells[1];
          fc = td.firstChild;
          if (fc != undefined) {
            fc.style.height = frh + "px";
          }
          }
        }

      tr = this.FFixedColRow.rows[0];
      if (tr != undefined) {
         tr = tr.cells[0];
         if (tr != undefined) {
         tr.style.height = Value + "px";
         tr = this.FFixedRow.rows[ARow].cells[0];
         if (tr != undefined) {
         tr.style.height = Value + "px"; }
         }
      }
    end;
  end;


  if ARow >= FixedRows then
  begin
    ARow := ARow - FixedRows;

    rh := Value;
    if HasHorzScrollBar and HasVertScrollBar and (ARow = RowCount - 1) then
      rh := Value + FNormalCells.parentElement.scrollHeight;

    asm
      var tr = this.FFixedCol.rows[ARow];
      if (tr != undefined) {
      tr.style.height = Value + "px";
      var tr = this.FNormalCells.rows[ARow];

      if (tr != undefined) {
      tr.style.height = rh + "px"; }
      }
    end;
  end;

  asm
    var td = this.FGrid.rows[1].cells[0];
    if (td.firstChild) {
    td.firstChild.style.height = nhvisnoscroll + "px";
    td.firstChild.firstChild.style.height = nh + "px";
    }

    td = this.FGrid.rows[1].cells[1];
    if (td.firstChild) {
    td.firstChild.style.height = nhvis + "px";
    td.firstChild.firstChild.style.height = nh + "px";
    }
  end;
end;

procedure TCustomStringGrid.SetRows(Index: integer; const Value: TStrings);
var
  i: integer;
begin
  if (Index >= 0) and (Index < RowCount) then
  begin
    for i := 0 to ColCount - 1 do
    begin
      if i < Value.Count then
        Cells[i,Index] := Value[i];
    end;
  end
  else
    raise Exception.Create('Invalid row index');
end;

procedure TCustomStringGrid.SetSelection(const Value: TGridRect);
var
  c,r,rc,rr: integer;
  sel: boolean;
  left,right,top,bottom: integer;
  tr,td: TJSElement;

begin
  FSelection := Value;

  left := Value.Left;
  right := Value.Right;

  if Value.Left > Value.Right then
  begin
    left := Value.Right;
    right := Value.Left;
  end;

  top := Value.Top;
  bottom := Value.Bottom;

  if Value.Top > Value.Bottom then
  begin
    top := Value.Bottom;
    bottom := Value.Top;
  end;

  for r := FixedRows to RowCount - 1 do
  begin
    if goRowSelect in Options then
    begin
      rr := r - FixedRows;
      sel := (r >= top) and (r <= bottom);

      if sel then
      begin
        asm
          tr = this.FNormalCells.rows[rr];
        end;
        AddSelection(tr);
      end
      else
      begin
        asm
          tr = this.FNormalCells.rows[rr];
        end;
        RemoveSelection(tr);
      end;
    end
    else
    for c := FixedCols to ColCount - 1 do
    begin
      rc := c - FixedCols;
      rr := r - FixedRows;

      sel := (c >= left) and (c <= right) and (r >= top) and (r <= bottom);

      if sel then
      begin
        asm
           td = this.FNormalCells.rows[rr].cells[rc];
        end;
        if Assigned(td) then
          AddSelection(td);
      end
      else
      begin
        asm
          td = this.FNormalCells.rows[rr].cells[rc];
        end;
        if Assigned(td) then
          RemoveSelection(td);
      end;
    end;
  end;
end;

procedure TCustomStringGrid.SetSelectionColor(const Value: TColor);
begin
  if (FSelectionColor <> Value) then
  begin
    FSelectionColor := Value;
    UpdateElement;
  end;
end;

procedure TCustomStringGrid.SetSelectionTextColor(const Value: TColor);
begin
  if (FSelectionTextColor <> Value) then
  begin
    FSelectionTextColor := Value;
    UpdateElement;
  end;
end;

procedure TCustomStringGrid.SetSortIndex(const Value: integer);
var
  i: integer;
begin
  FSortIndex := Value;
  for i := 0 to ColCount - 1 do
  begin
    SelectSortIndicator(i, 0, i = FSortIndex);
  end;
end;

procedure TCustomStringGrid.SetSortIndicator(const ACol, ARow: integer;
  AIndicator: TGridSortIndicator);
var
  el: TJSElement;
  sid: string;

begin
  sid := Name + 'si'+ACol.ToString+'_'+ARow.ToString;

  el := document.getElementById(sid);

  if Assigned(el) then
  begin
    if AIndicator = siAscending then
    begin
      TJSHTMLElement(el).innerHTML := GlyphAscending;
      el['sort'] := 'up';
    end
    else
    begin
      TJSHTMLElement(el).innerHTML := GlyphDescending;
      el['sort'] := 'dn';
    end;
  end;

end;

procedure TCustomStringGrid.SetTopRow(const Value: integer);
var
  pixVal: integer;
begin
  pixVal := (Value - FixedRows) * DefaultRowHeight;
  if Assigned(FNormalCells) and (pixVal >= 0) then
  begin
    FNormalCells.parentElement.parentElement.scrollTop := pixVal;
    if FixedCols > 0 then
      FFixedCol.parentElement.parentElement.scrollTop := pixVal;
  end;
end;

procedure TCustomStringGrid.ShowEdit;
begin
  StartEdit('');
end;


procedure TCustomStringGrid.Sort(AColumn: integer;
  ADirection: TGridSortIndicator);
var
  colidx: integer;
  grid: TJSElement;
  asc: boolean;
begin
  grid := FNormalCells;
  asc := ADirection = siAscending;
  colidx := AColumn - FixedCols;

  asm
    const getCellValue = (tr, idx) => tr.children[idx].innerText || tr.children[idx].textContent;
    const comparer = (idx, asc) => (a, b) => ((v1, v2) =>
      v1 !== '' && v2 !== '' && !isNaN(v1) && !isNaN(v2) ? v1 - v2 : v1.toString().localeCompare(v2)
      )(getCellValue(asc ? a : b, idx), getCellValue(asc ? b : a, idx));

    const table = grid;
    Array.from(table.querySelectorAll('tr:nth-child(n+1)'))
        .sort(comparer(colidx, asc))
        .forEach(tr => table.appendChild(tr) );
  end;
end;

procedure TCustomStringGrid.StartEdit(ch: string);
var
  created: boolean;
  eh: TJSHTMLElement;
  s: string;
  dorange: boolean;
begin
   // https://developer.mozilla.org/en-US/docs/Learn/Forms/HTML5_input_types

   // default editor
  if WordWrap then
    FEditType := geMemo
  else
    FEditType := geText;

  GetCellEditor(Col,Row, FEditType);

  if FEditType = geNone then
    Exit;

  eh := ElementHandle;

  FEditRow := Row;
  FEditCol := Col;

  FEditMode := true;

  s := GetEditText(Col,Row);

  FOrigVal := s;

  if FEditType = geCombo then
  begin
    if not Assigned(FCombo) then
    begin
      FCombo := TWebComboBox.Create(Self);
      FCombo.ElementPosition := epRelative;
      FCombo.WidthStyle := ssPercent;
      FCombo.WidthPercent := 100;
      FCombo.HeightStyle := ssPercent;
      FCombo.HeightPercent := 100;
      FCombo.Parent := Self;
      FCombo.OnExit := HandleComboExit;
      FCombo.OnKeyPress := HandleComboKeyPress;
    end;

    FCombo.Items.Assign(FComboBoxItems);
    FCombo.Text := s;
    FSelectedCell.innerHTML := '';
    FSelectedCell.appendChild(FCombo.ElementHandle);
    FCombo.SetFocus;
    RemoveSelection(FSelectedCell);
    Exit;
  end;

  if FEditType = geMemo then
  begin
    if not Assigned(FMemo) then
    begin
      FMemo := TWebMemo.Create(Self);
      FMemo.ElementPosition := epRelative;
      FMemo.WidthStyle := ssPercent;
      FMemo.WidthPercent := 100;
      FMemo.BorderStyle := bsNone;
      FMemo.Parent := Self;
      FMemo.ElementHandle.style.setProperty('outline','none');
      FMemo.OnExit := HandleMemoExit;
      FMemo.OnKeyPress := HandleMemoKeyPress;
    end;

    FMemo.Height := RowHeights[FEditRow] - 3;
    FMemo.Lines.Text := s;
    FSelectedCell.innerHTML := '';
    TJSHTMLElement(FSelectedCell).style.setProperty('justify-content','top');
    TJSHTMLElement(FSelectedCell).style.setProperty('vertical-align','top');
    FSelectedCell.appendChild(FMemo.ElementHandle);
    FMemo.SetFocus;
    RemoveSelection(FSelectedCell);
    Exit;
  end;

  if FEditType = geCustom then
  begin
    FEditControl.ElementPosition := epRelative;
    FEditControl.WidthStyle := ssPercent;
    FEditControl.WidthPercent := 100;
    FEditControl.Parent := Self;
    FEditControl.OnExit := DoEditControlExit;

    SetEditControlValue(FEditCol, FEditRow, FEditControl, s);

    FSelectedCell.innerHTML := '';
    TJSHTMLElement(FSelectedCell).style.setProperty('justify-content','top');
    TJSHTMLElement(FSelectedCell).style.setProperty('vertical-align','top');
    FSelectedCell.appendChild(FEditControl.ElementHandle);
    RemoveSelection(FSelectedCell);
    Exit;
  end;


  if FEditType = geMask then
  begin
    if not Assigned(FMask) then
    begin
      FMask := TWebMaskEdit.Create(Self);
      FMask.ElementPosition := epRelative;
      FMask.WidthStyle := ssPercent;
      FMask.WidthPercent := 100;
      FMask.HeightStyle := ssPercent;
      FMask.HeightPercent := 100;
      FMask.Parent := Self;
      FMask.OnExit := HandleMaskExit;
      FMask.OnKeyPress := HandleMaskKeyPress;
    end;

    FMask.EditMask := EditMask;
    FMask.Text := s;
    FSelectedCell.innerHTML := '';
    FSelectedCell.appendChild(FMask.ElementHandle);
    FMask.SetFocus;
    FMask.SetSelection(0,Length(s));
    RemoveSelection(FSelectedCell);
    Exit;
  end;

  created := false;


  asm
    if (this.FEdit == undefined) {
      this.FEdit = document.createElement('INPUT');
      this.FEdit.style.setProperty('box-sizing','border-box');
      this.FEdit.style.setProperty('-webkit-box-sizing','border-box');
      this.FEdit.style.setProperty('-moz-box-sizing','border-box');
      this.FEdit.style.setProperty('outline', 'none');
      this.FEdit.style.setProperty('border-style', 'none');

      created = true;
    }
    this.FEdit.removeAttribute("type");
  end;

  case FEditType of
  geURL: FEdit['type'] := 'URL';
  geDate: FEdit['type'] := 'DATE';
  geTime: FEdit['type'] := 'TIME';
  geRange:
    begin
      FEdit['type'] := 'RANGE';
      FEdit['min'] := NumAttribute(RangeEdit.min);
      FEdit['max'] := NumAttribute(RangeEdit.max);
      FEdit['step'] := NumAttribute(RangeEdit.step);
    end;
  geEMail: FEdit['type'] := 'EMAIL';
  geNumber: FEdit['type'] := 'NUMBER';
  geTel: FEdit['type'] := 'TEL';
  geColor: FEdit['type'] := 'COLOR';
  end;

  dorange := FEditType = geText;

  asm
    this.FEdit.removeAttribute("min");
    this.FEdit.removeAttribute("max");
    this.FEdit.removeAttribute("step");

    if (ch == '') {
      this.FEdit.value = s;
      }
    else {
      this.FEdit.value = ch;
    }

    this.FEdit.style.width = "100%";
    this.FEdit.style.height = "100%";
    this.FEdit.style.fontFamily = eh.style.fontFamily;
    this.FEdit.style.fontSize = eh.style.fontSize;

    if (dorange) {
    this.FEdit.setSelectionRange(0, this.FEdit.value.length)
    }

    this.FSelectedCell.innerHTML = "";
    this.FSelectedCell.appendChild(this.FEdit);
  end;


  asm
    this.FEdit.focus();
  end;
  RemoveSelection(FSelectedCell);

  if created then
  begin
    FEdit.addEventListener('blur', FHandleEditBlurPtr);
    FEdit.addEventListener('keypress', FHandleEditKeypressPtr);
  end;
end;

function TCustomStringGrid.StopEdit: string;
var
  val: string;
  eh: TJSHTMLElement;
begin
  Result := '';

  if FEditMode then
  begin
    FEditMode := false;

    if FEditType = geCombo then
    begin
      val := FCombo.Text;
      eh := FCombo.ElementHandle;
    end
    else
    if FEditType = geMask then
    begin
      val := FMask.Text;
      eh := FMask.ElementHandle;
    end
    else
    begin
      val := (FEdit as TJSHTMLInputElement).value;
      eh := FEdit;
    end;

    ValidateCell(FEditCol, FEditRow, val);

    if FSelectedCell.contains(eh) then
      FSelectedCell.removeChild(eh);

    FSelectedCell.innerHTML := val;
    Result := val;

    UpdateCell(FEditCol, FEditRow, val);
    if not (goRowSelect in Options) then
    begin
      AddSelection(FSelectedCell);
    end;
  end;
end;

function TCustomStringGrid.StylePrefix: string;
var
  frm: TCustomForm;
begin
  Result := '';
  frm := GetParentForm(Self);
  if Assigned(frm) then
    Result := frm.ClassName;
end;

procedure TCustomStringGrid.TopLeftChanged;
begin
  if Assigned(OnTopLeftChanged) then
    OnTopLeftChanged(Self);
end;

procedure TCustomStringGrid.UnbindEvents;
begin
  inherited;

  if Assigned(FNormalCells) then
  begin
    FNormalCells.removeEventListener('mousedown',FHandleMouseDownPtr);
    FNormalCells.removeEventListener('mouseup',FHandleMouseUpPtr);
    FNormalCells.removeEventListener('mousemove',FHandleMouseMovePtr);
    FNormalCells.removeEventListener('click',FHandleClickPtr);
  end;

  if Assigned(FFixedCol) then
    FFixedCol.removeEventListener('click',FHandleFixedClickPtr);

  if Assigned(FFixedRow) then
    FFixedRow.removeEventListener('click',FHandleFixedClickPtr);

  if Assigned(FFixedColRow) then
    FFixedColRow.removeEventListener('click',FHandleFixedClickPtr);
end;

procedure TCustomStringGrid.UpdateCell(ACol, ARow: integer; var AValue: string);
begin

end;

procedure TCustomStringGrid.UpdateElement;
var
  clr: string;
begin
  inherited;

  if IsUpdating then
    Exit;

  if Assigned(ElementHandle) then
  begin
    if (Color <> clWindow) and (Color <> clNone) then
      ElementHandle.style.setProperty('background-color', ColorToHTML(Color));

    if Assigned(FGrid) then
    begin
      SetHTMLElementFont(TJSHTMLElement(FGrid), Font, not ((ElementClassName = '') and (ElementFont = efProperty)) );
    end;

    ElementHandle.style.setProperty('overflow', 'hidden');

    if (BorderStyle = bsSingle) and not (BorderColor = clNone) and (ElementClassName = '') and not IsLinked then
    begin
      ElementHandle.style.setProperty('border', '1px solid '+ ColorToHTML(BorderColor));
//      BorderWidth := 1;
    end
    else
    begin
      ElementHandle.style.setProperty('border', '');
//      BorderWidth := 0;
    end;

    RenderGrid;

    clr := ColorToHTML(Color);

    if Color <> clWindow then
    begin
      // background color stringgrid (DIV & TBODY)
      asm
        var bd = this.FNormalCells.tBodies[0];
        if (bd != undefined) {
          this.FNormalCells.tBodies[0].style.setProperty('background-color', clr);
          }
        this.FNormalCells.parentElement.parentElement.style.setProperty('background-color', clr);
      end;
    end;

    if FOrigID = '' then
    begin
      FOrigID := GetID;
      if FOrigID = '' then
        FOrigID := Name;
    end;

    AddInstanceStyle(StylePrefix + FOrigID, GetGridCSS(FOrigID + 'tbl'));

    UpdateGridSize;
    UpdateGridAlignment;
  end;
end;

procedure TCustomStringGrid.UpdateElementVisual;
var
  fixcol,fixtxtcol: string;
begin
  inherited;

  if IsUpdating then
    Exit;

  if Assigned(ElementHandle) and Assigned(FGrid) then
  begin
    fixcol := ColorToHTML(FFixedColor);
    fixtxtcol := ColorToHTML(FixedTextColor);

    if (FixedCols > 0) and (FixedRows > 0) then
    begin
      asm
        this.FFixedColRow.parentElement.style.setProperty('background-color',fixcol);
        this.FFixedColRow.parentElement.style.setProperty('color',fixtxtcol);
      end;
    end;

    if (FixedCols > 0) then
    begin
      asm
        this.FFixedCol.parentElement.style.setProperty('background-color',fixcol);
        this.FFixedCol.parentElement.style.setProperty('color',fixtxtcol);

        if (this.FFixedCol.parentElement.parentElement) {
          this.FFixedCol.parentElement.parentElement.style.setProperty('background-color',fixcol);
          this.FFixedCol.parentElement.parentElement.style.setProperty('color',fixtxtcol);
        }
      end;
    end;

    if (FixedRows > 0) then
    begin
      asm
        this.FFixedRow.parentElement.style.setProperty('background-color',fixcol);
        this.FFixedRow.parentElement.style.setProperty('color',fixtxtcol);
        this.FFixedRow.parentElement.parentElement.style.setProperty('background-color',fixcol);
        this.FFixedRow.parentElement.parentElement.style.setProperty('color',fixtxtcol);
        if (this.FFixedRow.parentElement.parentElement.parentElement) {
          this.FFixedRow.parentElement.parentElement.parentElement.style.setProperty('background-color',fixcol);
          this.FFixedRow.parentElement.parentElement.parentElement.style.setProperty('color',fixtxtcol);
        }
      end;
    end;
  end;

end;

procedure TCustomStringGrid.UpdateGridAlignment;
var
  i,j,k,l,cw,rh: integer;
  al: TAlignment;

begin
  if not Assigned(ElementHandle) then
    Exit;

  if IsUpdating then
    Exit;

  if (RowCount = 0) or (ColCount = 0) then
    Exit;

  if FColAlignments.Count = 0 then
    Exit;

  for i := 0 to FixedCols - 1 do
  begin
    al := ColAlignments[i];
    j := i; // avoid that the optimizer removes j variable

    if (FixedRows > 0) and (FixedCols > 0) then
    begin

      asm
        for (j = 0; j < this.FFixedColRow.rows.length; j ++){
         var tr = this.FFixedColRow.rows[j];
         if (tr) {
         var td = tr.cells[i];
         if (td) {
           this.SetAlignAttr(al, td);
           }
        }
        }
      end;
    end;

    if (FixedCols > 0) then
    begin
      asm
        for (j = 0; j < this.FFixedCol.rows.length; j ++){
         var tr = this.FFixedCol.rows[j];
         if (tr) {
           var td = tr.cells[i];
           if (td) {
           this.SetAlignAttr(al, td);
           }
         }
        }
      end;
    end;
  end;

  for i := FixedCols to ColCount - 1 do
  begin
    al := ColAlignments[i];

    k := i - FixedCols;
    j := i; // avoid that the optimizer removes j variable

    if (FixedRows > 0) then
    begin
      asm
        //if (this.FFixedRow.rows.length > 0) {
        for (j = 0; j < this.FFixedRow.rows.length; j++) {
          var tr = this.FFixedRow.rows[j];
          if (tr) {
          var td = tr.cells[k];
          if (td) {
            this.SetAlignAttr(al, td);
           }
          }
        }
      end;
    end;

    asm
      for (j = 0; j < this.FNormalCells.rows.length; j++) {
        var tr = this.FNormalCells.rows[j];
        if (tr) {
          var td = tr.cells[k];
          if (td) {
          this.SetAlignAttr(al, td);
          }
        }
      }
    end;
  end;
end;

procedure TCustomStringGrid.UpdateGridSize;
var
  i,j,k,l,cw,rh,frh: integer;

begin
  if not Assigned(ElementHandle) then
    Exit;

  if not Assigned(Parent) then
    Exit;

  if IsUpdating then
    Exit;

  if (RowCount = 0) or (ColCount = 0) then
    Exit;

  for i := 0 to FixedCols - 1 do
  begin
    cw := ColWidths[i] - CELLPADDING;

    if (FixedRows > 0) and (FixedCols > 0) then
    begin
      asm
        for (j = 0; j < this.FFixedColRow.rows.length; j ++){
         var tr = this.FFixedColRow.rows[j];
         if (tr) {
         var td = tr.cells[i];
         if (td) {
         td.style.width = cw + "px";
         td.style.maxWidth = cw + "px";
         }
         }
        }
      end;
    end;

    if (FixedCols > 0) then
    begin
      asm
        for (j = 0; j < this.FFixedCol.rows.length; j ++){
         var tr = this.FFixedCol.rows[j];
         if (tr) {
         var td = tr.cells[i];
         if (td) {
         td.style.width = cw + "px";
         td.style.maxWidth = cw + "px";
         }
         }
        }
      end;
    end;
  end;

  for i := 0 to FixedRows - 1 do
  begin
    rh := RowHeights[i];

    if FixedCols > 0  then
    begin
      asm
        var tr = this.FFixedColRow.rows[i];
        if (tr) {
          tr.style.height = rh + "px";
        }
      end;
    end;

    asm
      var tr = this.FFixedRow.rows[i];
      if (tr) {
        tr.style.height = rh + "px";
      }
    end;
  end;

  for i := FixedCols to ColCount - 1 do
  begin
    cw := ColWidths[i] - CELLPADDING;

    k := i - FixedCols;

    if (FixedRows > 0) then
    begin
      asm
        //if (this.FFixedRow.rows.length > 0) {
        for (i = 0; i < this.FFixedRow.rows.length; i++) {
          var tr = this.FFixedRow.rows[i];
          if (tr) {
          var td = tr.cells[k];
          if (td) {
          td.style.width = cw + "px";
          td.style.maxWidth = cw + "px";
          }
          }
        }
      end;
    end;

    asm
      if (this.FNormalCells.rows.length > 0) {
        var tr = this.FNormalCells.rows[0];
        if (tr) {
        var td = tr.cells[k];
        if (td) {
        td.style.width = cw + "px";
        td.style.maxWidth = cw + "px";
        }
        }
      }
    end;
  end;

  for i := FixedRows to RowCount - 1 do
  begin
    k := i - FixedRows;
    rh := RowHeights[i];

    for j := FixedCols to ColCount - 1 do
    begin
      l := j - FixedCols;
      cw := ColWidths[j] - CELLPADDING;

      frh := rh;

      if (i = RowCount - 1) and HasHorzScrollBar and HasVertScrollBar then
      begin
        frh := rh + 20;  //FNormalCells.parentElement.scrollheight;
      end;

      if FixedCols > 0 then
      begin
        asm
          var tr = this.FFixedCol.rows[k];
          if (tr) {
            tr.style.height = frh + "px";
          }
        end;

        if (i = RowCount - 1) and HasHorzScrollBar and HasVertScrollBar then
        begin
          asm
            var td = this.FFixedCol.rows[k].cells[0];
            if (td) {
              td.style.setProperty("vertical-align","top");
            }
          end;
        end;
      end;

      asm
        var tr = this.FNormalCells.rows[k];
        if (tr) {
        tr.style.height = rh + "px";

        td = tr.cells[l];
        if (td) {
        td.style.maxWidth = cw + "px";
        }
        }
      end;
    end;
  end;
end;


procedure TCustomStringGrid.ValidateCell(ACol, ARow: integer;
  var AValue: string);
begin
  if Assigned(OnValidateEdit) then
    OnValidateEdit(Self, ACol, ARow, AValue);
end;

{$HINTS ON}

function TCustomStringGrid.HandleMouseMove(Event: TJSMouseEvent): Boolean;
var
  rindex, cindex: integer;
  l, t: Single;
  ss: TShiftState;
begin
  rindex := -1;
  cindex := -1;

  asm
    if (Event.target.tagName.toLowerCase() == 'td')
    {
      cindex = Event.target.cellIndex;
      var tr = Event.target.parentElement;
      rindex = tr.rowIndex;
    }
  end;

  if (cindex >= 0) and (rindex >= 0) and (FStartCell.X >= 0) and (FStartCell.Y >= 0) then
    Selection := GridRect(FStartCell.X, FStartCell.Y, cindex + FixedCols, rindex + FixedRows);

  Result := true;

  XYToClient(Event.clientX, Event.clientY, l, t);

  ss := GetMouseEventShiftState(Event);

  MouseMove(ss, Trunc(l), Trunc(t));
end;

function TCustomStringGrid.HandleMouseUp(Event: TJSMouseEvent): Boolean;
var
  l, t: Single;
  ss: TShiftState;
  mb: TMouseButton;
begin
  asm
    TMSStringGridresetResize();
  end;

  FStartCell.X := -1;
  FStartCell.Y := -1;
  Result := false;

  Event.stopPropagation;
  Event.cancelBubble := true;

  XYToClient(Event.clientX, Event.clientY, l, t);

  ss := GetMouseEventShiftState(Event);
  mb := GetMouseEventButton(Event);

  MouseUp(mb, ss, Trunc(l), Trunc(t));

end;

{ TCustomGrid }

procedure TCustomGrid.Clear;
begin
//
end;

procedure TCustomGrid.ColCountChanged(delta: integer);
begin
end;

function TCustomGrid.ColOffset: integer;
begin
  Result := 0;
end;

function TCustomGrid.GetCells(ACol, ARow: integer): string;
begin
  Result := '';
end;

function TCustomGrid.GetCellsArray: TJSArray;
var
  i,j: integer;
  row: TJSArray;
  s:string;
begin
  Result := TJSArray.new;

  for i := RowOffset to RowCount - 1 do
  begin
    row := TJSArray.new;

    for j := ColOffset to ColCount - 1 do
    begin
      s := Cells[j,i];
      row.push(s);
    end;
    Result.push(row);
  end;
end;

procedure TCustomGrid.GridChanged;
begin
  //
end;

procedure TCustomGrid.RowCountChanged(delta: integer);
begin
end;

function TCustomGrid.RowOffset: integer;
begin
  Result := 0;
end;

procedure TCustomGrid.SetCells(ACol, ARow: integer; const Value: string);
begin
//
end;

procedure TCustomGrid.SetColCount(const Value: integer);
var
  delta: integer;
begin
  if (FColCount <> Value) and (Value >= 0) then
  begin
    delta := Value - FColCount;
    FColCount := Value;
    if Assigned(ElementHandle) then
    begin
      ColCountChanged(delta);
      GridChanged;
    end;
  end;
end;

procedure TCustomGrid.SetRowCount(const Value: integer);
var
  delta: integer;
begin
  if (FRowCount <> Value) and (Value >= 0) then
  begin
    delta := Value - FRowCount;
    FRowCount := Value;
    if Assigned(ElementHandle) then
    begin
      RowCountChanged(delta);
      GridChanged;
    end;
  end;
end;

procedure TCustomGrid.SetCellsArray(const Value: TJSArray);
var
  i,j: integer;
  ja: TJSArray;
  jv: JSValue;
begin
  if Value.length + RowOffset > RowCount  then
    RowCount := Value.length + RowOffset;

  for i := 0 to Value.length - 1 do
  begin
    ja := TJSArray(Value.elements[i]);
    if (ja is TJSArray) then
    begin
      if ja.length + ColOffset > ColCount then
        ColCount := ja.length + ColOffset;

      for j := 0 to ja.length - 1 do
      begin
        jv := ja.Elements[j];
        Cells[j + ColOffset,i + RowOffset] := js.toString(jv);
      end;
    end;
  end;
end;

{ TCustomTableControl }

{$HINTS OFF}
procedure TCustomTableControl.AddButton(ACol, ARow: integer; AText: string; AStyle: string);
var
  s, ct: string;
  el: TJSHTMLElement;
begin
  if (ARow >= 0) and (ACol >= 0) and not HasCheckBox(ACol,ARow) then
  begin
    ct := Cells[ACol, ARow];
    if ct <> '' then
      ct := '&nbsp;' + ct;

    s := '<BUTTON type="button" style="vertical-align:middle"';

    if AStyle <> '' then
      s := s + ' class="'+AStyle+'"';

    s := s + '>'+AText+'</BUTTON>';

    Cells[ACol,ARow] := s + ct;

    el := CellElements[ACol, ARow];
    BindCellButton(ACol, ARow, el);
  end;

end;

procedure TCustomTableControl.AddButtonColumn(ACol: integer; AText,
  AStyle: string);
var
  i,s: integer;
begin
  s := 0;
  if RowHeader then
    s := 1;

  for i := s to RowCount - 1 do
    AddButton(ACol, i, AText, AStyle);
end;

procedure TCustomTableControl.AddCheckBox(ACol, ARow: integer;
  Checked: boolean = false);
var
  s, ct: string;
  el: TJSHTMLElement;
begin
  if (ARow >= 0) and (ACol >= 0) and not HasCheckBox(ACol,ARow) then
  begin
    ct := Cells[ACol, ARow];
    if ct <> '' then
      ct := '&nbsp;' + ct;

    s := '<INPUT TYPE="CHECKBOX" style="vertical-align:middle"';
    if checked then
      s := s + ' CHECKED';
    s := s + '>';
    Cells[ACol,ARow] := s + ct;

    el := CellElements[ACol, ARow];
    BindCellCheckBox(ACol, ARow, el);
  end;
end;

procedure TCustomTableControl.AddCheckBoxColumn(ACol: integer;
  Checked: boolean = false);
var
  i,s: integer;
begin
  s := 0;
  if RowHeader then
    s := 1;

  for i := s to RowCount - 1 do
    AddCheckBox(ACol, i, Checked);
end;

procedure TCustomTableControl.AddProgress(ACol, ARow, APosition: integer;
  AStyle: string);
var
  prog: TJSElement;
begin
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    if not HasProgress(ACol,ARow) then
    begin
      prog := document.createElement('PROGRESS');
      prog['max'] := '100';
      prog['value'] := APosition.toString;
      TJSHTMLElement(prog).style.setProperty('width','100%');
      if AStyle <> '' then
        prog['class'] := AStyle;
      CellElements[ACol,ARow].insertBefore(prog, CellElements[ACol,ARow].firstChild);
    end;
  end;
end;

procedure TCustomTableControl.AddSortIndicator(const ACol, ARow: integer;
  AIndicator: TGridSortIndicator);
var
  s,sid: string;
  sp: integer;
begin
  s := Cells[ACol,ARow];
  sp := pos('<span', s);

  if sp > 0 then
    s := Copy(s, 1, sp - 1);

  sid := Name + 'si'+ACol.ToString+'_'+ARow.ToString;

  case AIndicator of
  siNone: Cells[ACol,ARow] := s;
  siAscending: Cells[ACol,ARow] := s + '<span style="cursor:pointer;float:right" id="' + sid + '" sort="up">'+GlyphAscending+'</span>';
  siDescending: Cells[ACol,ARow] := s + '<span style="cursor:pointer;float:right" id="'+ sid + '" sort="dn">'+GlyphDescending+'</span>';
  end;

end;

procedure TCustomTableControl.BindCellButton(ACol, ARow: integer;
  AElement: TJSHTMLElement);
var
  el: TJSHTMLElement;
begin
  el := TJSHTMLElement(AElement.firstChild);
  if Assigned(el) and (el.tagName = 'DIV') then
    el := TJSHTMLElement(el.firstChild);

  if Assigned(el) and (el.tagName = 'BUTTON') then
  begin
    el.addEventListener('click', FHandleButtonClickPtr);
  end;
end;

procedure TCustomTableControl.BindCellCheckBox(ACol, ARow: integer;
  AElement: TJSHTMLElement);
var
  el: TJSHTMLElement;
begin
  el := TJSHTMLElement(AElement.firstChild);

  if Assigned(el) and (el.tagName = 'DIV') then
    el := TJSHTMLElement(el.firstChild);

  if Assigned(el) and (el.tagName = 'INPUT') then
  begin
    if (lowercase(el.getAttribute('TYPE'))= 'checkbox') then
    begin
      el.addEventListener('click', FHandleCheckClickPtr);
    end;
  end;

end;

procedure TCustomTableControl.BindEvents;
begin
  inherited;
  RenderGridScript;
end;

procedure TCustomTableControl.Clear;
var
  i,j: integer;
begin
  for i := 0 to RowCount - 1 do
    for j := 0 to ColCount - 1 do
      begin
        Cells[j,i] := '';
      end;
end;

procedure TCustomTableControl.ClearData;
begin
  Clear;
end;

procedure TCustomTableControl.ClearMethodPointers;
begin
  inherited;
  FHandleCheckClickPtr := nil;
  FHandleButtonClickPtr := nil;
end;

procedure TCustomTableControl.ColCountChanged(delta: integer);
var
  c,fromrow, torow: integer;
  dtb,cc: string;

begin
  if IsUpdating or not Assigned(FTable) then
    Exit;

  cc := '';
  dtb := '';
  if (csDesigning in ComponentState) then
  begin
    dtb := '1px dashed gray';
    cc := '&nbsp;';
  end;

  fromrow := 0;
  torow := RenderRowCount;

  if RowHeader then
    torow := torow + 1;

  if Header.Visible then
  begin
    Header.FElement.setAttribute('colspan',ColCount.ToString);
    fromrow := 1;
    torow := torow + 1;
  end;

  if Footer.Visible then
  begin
    Footer.FElement.setAttribute('colspan',ColCount.ToString);
  end;

  if delta > 0 then
  begin
    asm
      var i = 0;
      var j = 0;
      var k = 0;
      var tr = undefined;
      var td = undefined;

      for(j = fromrow; j < torow; j++)
      {
        tr = this.FTable.rows[j];

        if (tr == undefined) { continue; }

        k = tr.cells.length;

         for(i = 0; i < delta; i++)
         {
           if ((j == fromrow) && (this.FRowHeader))
           {
             var th = document.createElement('th');
             th.setAttribute('scope','col');
             tr.appendChild(th);
             this.SetTableCell(i,j,th,cc);
           }
           else
           {
             if ((tr.cells.length == 0) && (this.FColHeader))
             {
               var th = document.createElement('th');
               th.setAttribute('scope','row');
               this.SetTableCell(i,j,th, cc);
               tr.appendChild(th);
             }
             else
             {
               td = tr.insertCell();
               td = tr.cells[k];
               this.SetTableCell(i,j,td, cc);
             }
           }
         }
      }
    end;

    if DragMode = dmAutomatic then
      EnableDrag;
  end;

  if delta < 0 then
  begin
    asm
      var i = 0;
      var j = 0;
      var k = 0;
      var tr = undefined;

      for(j = fromrow; j < torow; j++)
      {
        tr = this.FTable.rows[j];

        if (tr == undefined) { continue; }

        k = tr.cells.length;

        for(i = 0; i < -delta; i++)
        {
          if ((k - i - 1 < k) && (k - i - 1 >= 0))  {
            tr.deleteCell(k - i - 1);
        }
       }
      }
    end;
  end;

end;
{$HINTS ON}

function TCustomTableControl.ColOffset: integer;
begin
  if ColHeader then
    Result := 1
  else
    Result := 0;
end;

constructor TCustomTableControl.Create(AOwner: TComponent);
begin
  FHeader := nil;
  FFooter := nil;

  inherited;
  TabStop := false;
end;

function TCustomTableControl.CreateElement: TJSElement;
begin
  Result := document.createElement('DIV');
  FTable := nil;
end;

procedure TCustomTableControl.CreateInitialize;
begin
  inherited;
  FOptions := TTableControlOptions.Create(Self);

  FData := nil;
  FUnfiltered := nil;
  if (FData = nil) then
  begin
    if not Assigned(FHeader) then
      FHeader := TTableControlHeader.Create(Self);
    if not Assigned(FFooter) then
      FFooter := TTableControlHeader.Create(Self);

    FPaging := TTableControlPaging.Create(Self);
    FHiddenCols := TStringList.Create;
    FHiddenCols.Duplicates := dupIgnore;
  end;
  asm
    this.FData = [];
    this.FData.length = 11;
    var i;
    for(i=0;i<11;i++) {
      if (this.FData[i] == null) this.FData[i] = [];

      this.FData[i].length = 5;
    }
  end;

//  Color := clWindow;
 // TabStop := false;
  FColCount := 5;
  FRowCount := 10;
  FRowHeader := true;
  FColHeader := true;
  FRowIndex := -1;
  FSelectionColor := $FF8F5B;
  FSelectionTextColor := $FFFFFF;
  Enabled := true;

  if (csDesigning in ComponentState) then
  begin
    Width := 400;
    Height := 300;
  end;
end;

procedure TCustomTableControl.DataBeginUpdate;
begin
  //
end;

procedure TCustomTableControl.DataEndUpdate;
begin
  //
end;

procedure TCustomTableControl.DataInsertRow(AInsertPosition: Integer);
begin
  InsertRow(AInsertPosition);
end;

destructor TCustomTableControl.Destroy;
begin
  FHiddenCols.Free;
  FOptions.Free;
  FHeader.Free;
  FFooter.Free;
  FPaging.Free;
  inherited;
end;

procedure TCustomTableControl.DisableDrag;
  procedure RemoveDraggableAttribute(AElement: TJSElement);
  var
    I: Integer;
  begin
    for I := 0 to AElement.children.length - 1 do
    begin
      if TJSElement(AElement.children[I]).tagName = 'TD' then
      begin
        TJSElement(AElement.children[I]).removeAttribute('draggable');
        RemoveDraggableAttribute(TJSElement(AElement.children[I]));
      end
      else
        RemoveDraggableAttribute(TJSElement(AElement.children[I]));
    end;
  end;
begin
  if not Assigned(Container) then
    Exit;

  RemoveDraggableAttribute(Container);
end;

procedure TCustomTableControl.DoButtonClick(ACol, ARow: integer);
begin
  if Assigned(OnButtonClick) then
    OnButtonClick(Self, ACol, ARow);
end;

procedure TCustomTableControl.DoCheckClick(ACol, ARow: Longint;
  Checked: boolean);
begin
  if Assigned(OnCheckClick) then
    OnCheckClick(Self, ACol, ARow, Checked);
end;

function TCustomTableControl.DoHttpAbort(Event: TEventListenerEvent): boolean;
begin
  if Assigned(OnHttpRequestError) then
    OnHttpRequestError(Self);

  Result := true;
end;

function TCustomTableControl.DoHttpLoadCsv(Event: TEventListenerEvent): boolean;
var
  sl: TStringList;
  req: TJSXMLHttpRequest;
begin
  req := TJSXMLHttpRequest(Event.target);

  if (req.status >= 200) and (req.status <= 208) then
  begin
    sl := TStringList.Create;
    sl.Text := req.responseText;

    try
      LoadFromStringList(sl);
    finally
      sl.Free;
    end;

    if Assigned(OnHttpRequestSuccess) then
      OnHttpRequestSuccess(Self);
  end;

  Result := true;
end;

{$HINTS OFF}
function TCustomTableControl.DoHttpLoadJson(Event: TEventListenerEvent): boolean;
var
  J: TJSObject;
  req: TJSXMLHttpRequest;

begin
  req := TJSXMLHttpRequest(Event.target);

  if (req.status >= 200) and (req.status <= 208) then
  begin
    J := TJSJSON.parseObject(req.responseText);

    LoadFromJSON(J, FDataNode);

    if Assigned(OnHttpRequestSuccess) then
      OnHttpRequestSuccess(Self);
  end;

  Result := true;
end;

function TCustomTableControl.DoHttpLoadPageJson(Event: TEventListenerEvent): boolean;
var
  J: TJSObject;
  req: TJSXMLHttpRequest;

begin
  req := TJSXMLHttpRequest(Event.target);

  if (req.status >= 200) and (req.status <= 208) then
  begin
    J := TJSJSON.parseObject(req.responseText);

    LoadPageFromJSON(J, FDataNode);

    if Assigned(OnHttpRequestSuccess) then
      OnHttpRequestSuccess(Self);
  end;

  Result := true;
end;


procedure TCustomTableControl.DoHttpRequest(ARequest: TJSXMLHttpRequest);
var
  LRequestRec: TJSXMLHttpRequestRecord;
begin
  if Assigned(OnHttpRequest) then
  begin
    LRequestRec.req := ARequest;
    OnHttpRequest(Self, LRequestRec);
  end;
end;

procedure TCustomTableControl.DoSelectPage(AIndex: integer);
var
  Allow: boolean;
  DoDefault: boolean;

begin
  Allow := true;
  DoDefault := true;

  if Assigned(OnSelectPage) then
    OnSelectPage(Self, AIndex, Allow, DoDefault);

  if Allow then
    SelectPage(AIndex, DoDefault);
end;

procedure TCustomTableControl.DoSortClick(ACol, ARow: Longint;
  ASortIndicator: TGridSortIndicator);
var
 i: integer;
begin
  FSortIndex := ACol;

  for i := 0 to ColCount - 1 do
  begin
    SelectSortIndicator(i,ARow,i = ACol);
  end;

  FSortDirection := ASortIndicator;

  if Assigned(OnSortClick) then
    OnSortClick(Self, ACol, ARow, ASortIndicator);

  Sort(ACol, ASortIndicator);
end;

procedure TCustomTableControl.EnableColResize;
var
  TableName: string;
  ResizeEnabled: Boolean;
  ridx: integer;
begin
  TableName := Name;
  ResizeEnabled := Options.ResizeColumns;

  if not (csDesigning in ComponentState) then
  begin
    if Header.Visible then
      ridx := 1
    else
      ridx := 0;

    asm
      resizableGrid(TableName, ResizeEnabled, ridx);
    end;
  end;
end;

procedure TCustomTableControl.EnableDrag;
  procedure AddDraggableAttribute(AElement: TJSElement);
  var
    I: Integer;
  begin
    for I := 0 to AElement.children.length - 1 do
    begin
      if TJSElement(AElement.children[I]).tagName = 'TD' then
      begin
        TJSElement(AElement.children[I]).Attrs['draggable'] := 'true';
        AddDraggableAttribute(TJSElement(AElement.children[I]));
      end
      else
        AddDraggableAttribute(TJSElement(AElement.children[I]));
    end;
  end;
begin
  if not Assigned(Container) then
    Exit;

  AddDraggableAttribute(Container);
end;

function TCustomTableControl.FindCell(Condition: string; CaseSensitive: boolean = true; AllCells: boolean = false): TGridCoord;
var
  i,j: integer;
begin
  if ColHeader and not AllCells then
    i := 1
  else
    i := 0;

  if RowHeader then
    j := 1
  else
    j := 0;

  FFindCase := CaseSensitive;
  FFindCondition := Condition;

  Result := FindFrom(i,j,Condition, CaseSensitive, AllCells);
end;

function TCustomTableControl.FindFrom(ACol, ARow: integer; Condition: string; CaseSensitive: boolean = true; AllCells: boolean = false): TGridCoord;
var
  i,j,r,pg: integer;
  sr,sc: integer;
  cd: string;
  found: boolean;
begin
  sc := ACol;

  Result.X := -1;
  Result.Y := -1;

  for i := ARow to RowCount - 1 do
  begin
    for j := sc to ColCount - 1 do
    begin
      asm
        cd = this.FData[i][j];
        found = (cd != undefined);
      end;

      if found and MatchStr('*' + Condition + '*', cd, CaseSensitive) then
      begin
        Result.X := j;
        Result.Y := i;
        FFindCol := j;
        FFindRow := i;

        if Paging.Enabled then
        begin
          pg := (i - ARow) div Paging.Size;
          SelectPage(pg);
          FFindRow := i mod Paging.Size;
          if FFindRow < ARow then
            FFindRow := Paging.Size;
          Result.Y := FFindRow;
        end;

        ScrollRowInView(FFindRow);
        SelectCell(j,FFindRow);
        Exit;
      end;
    end;

    if ColHeader and not AllCells then
      sc := 1
    else
      sc := 0;
  end;
end;

function TCustomTableControl.FindNext: TGridCoord;
begin
  if FFindCol + 1 = RowCount then
  begin
    if ColHeader then
      FFindCol := 1
    else
      FFindCol := 0;
    FFindRow := FFindRow + 1;
  end
  else
    FFindCol := FFindCol + 1;

  Result := FindFrom(FFindCol, FFindRow, FFindCondition, FFindCase);
end;

{$HINTS ON}

function TCustomTableControl.GetButtonElement(ACol,
  ARow: integer): TJSHTMLElement;
var
  el: TJSElement;
begin
  Result := nil;

  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := CellElements[ACol,ARow];
    el := TJSElement(el.firstChild);
    if Assigned(el) and (el.tagName = 'DIV') then
      el := TJSElement(el.firstChild);

    if Assigned(el) and (el.tagName = 'BUTTON') then
    begin
      Result := TJSHTMLElement(el);
    end;
  end;
end;

procedure TCustomTableControl.GetCellChildren(ACol, ARow: integer;
  AField: TField; AValue: string; AElement: TJSHTMLElement);
var
  LElementRec: TJSHTMLElementRecord;
begin
  if Assigned(OnGetCellChildren) then
  begin
    LElementRec.element := AElement;
    OnGetCellChildren(Self, ACol, ARow, AField, AValue, LElementRec);
  end;
end;

procedure TCustomTableControl.GetCellClassName(ACol, ARow: integer;
  AField: TField; AValue: string; var AClassName: string);
begin
  if Assigned(OnGetCellClass) then
    OnGetCellClass(Self, ACol, ARow, AField, AValue, AClassName);
end;

procedure TCustomTableControl.GetCellData(ACol, ARow: integer; AField: TField;
  var AValue: string);
begin
  if Assigned(OnGetCellData) then
    OnGetCellData(Self, ACol, ARow, AField, AValue);
end;

function TCustomTableControl.GetCellElement(col, row: integer): TJSHTMLElement;
var
  res: TJSHTMLElement;
  rh,fr: integer;
begin
  res := nil;

  rh := 0;
  if Header.Visible then
    rh := 1;

  fr := 0;
  if ColHeader then
    fr := 1;

  if (col >= 0) and (row >= 0) and (col < ColCount) and (row <= RenderRowCount + rh + fr) and Assigned(FTable) then
  begin
    asm
      if ((this.FTable.rows.length > row + rh) && (this.FTable.rows[row + rh].cells.length > col)) {
        res = this.FTable.rows[row + rh].cells[col];
      }
    end;
  end;

  Result := res;
end;

function TCustomTableControl.GetCells(Col, Row: integer): string;
var
  res: string;
  hdr,trc: integer;
begin
  Result := '';

  hdr := 0;
  if Header.Visible then
    hdr := 1;

  trc := RenderRowCount;
  if RowHeader then
    inc(trc);

  if (col >= 0) and (row >= 0) and (col < ColCount) {and (row < trc)} and (hdr < 2) then
  begin
    asm
//      res = this.FData[row][col];
//      if (res == undefined) {
        var td = this.FTable.rows[row + hdr].cells[col];
        //res = td.innerHTML;

        res = td.textContent;
        if (td.childElementCount > 0) {
          if (td.firstChild.tagName == 'DIV')
            res = td.firstChild.textContent;
        }
//      }
    end;

    Result := res;
  end;
end;

function TCustomTableControl.GetCheckElement(ACol,
  ARow: integer): TJSHTMLElement;
var
  el: TJSElement;
  s: string;
begin
  Result := nil;

  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := TJSElement(CellElements[ACol,ARow].firstChild);

    if Assigned(el) and (el is TJSElement) and (uppercase(el.tagName) = 'DIV') then
      el := TJSElement(el.firstChild);

    if Assigned(el) and (el is TJSElement) then
    begin
      s := '';
      if el.hasAttribute('type') then
        s := el.getAttribute('type');

      if (s<>'') and (uppercase(s) = 'CHECKBOX') then
      begin
        Result := TJSHTMLElement(el);
      end;
    end;
  end;
end;

function TCustomTableControl.GetDataRowCount: Integer;
begin
  if RowHeader or (Paging.Enabled = false) then
    Result := RowCount - 1
  else
    Result := RowCount;
end;

procedure TCustomTableControl.GetMethodPointers;
begin
  inherited;
  FHandleCheckClickPtr := @HandleCheckBoxClick;
  FHandleButtonClickPtr := @HandleButtonClick;
end;

function TCustomTableControl.GetProgressElement(ACol,
  ARow: integer): TJSHTMLElement;
var
  el: TJSElement;
begin
  Result := nil;

  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := TJSElement(CellElements[ACol,ARow].firstChild);
    if Assigned(el) and (el is TJSElement) then
    begin
      if (uppercase(el.tagName) = 'PROGRESS') then
      begin
        Result := TJSHTMLElement(el);
      end;
    end;
  end;
end;

function TCustomTableControl.GetRowClassName(row: integer): TElementClassName;
var
  res: string;
begin
  res := '';

  if (row >= 0) and (row < RowCount) then
  begin
    asm
      var tr = this.FTable.rows[row];
      res = tr.getAttribute('class');
    end;
  end;

  Result := res;
end;

function TCustomTableControl.HandleButtonClick(Event: TJSEvent): Boolean;
var
  rindex, cindex: integer;
begin
  Result := true;
  rindex := -1;
  cindex := -1;

  asm
    var el = Event.target;
    var d = el.parentElement;
    if (d.tagName.toLowerCase() == 'div') {
      d = d.parentElement;
    }

    if (d.tagName.toLowerCase() == 'td') {
      cindex = d.cellIndex;
      var tr = d.parentElement;
      rindex = tr.rowIndex;
    }
  end;

  if Paging.Enabled then
  begin
    rindex := rindex + Paging.Index * Paging.Size;
  end;

  if (rindex <> -1) and (cindex <> -1) then
  begin
    DoButtonClick(cindex, rindex);
  end;
end;

function TCustomTableControl.HandleCheckBoxClick(Event: TJSEvent): Boolean;
var
  rindex, cindex: integer;
  chk: boolean;
begin
  Result := true;
  rindex := -1;
  cindex := -1;

  asm
    var el = Event.target;
    chk = el.checked;
    var d = el.parentElement;
    if (d.tagName.toLowerCase() == 'td')
    {
      cindex = d.cellIndex;
      var tr = d.parentElement;
      rindex = tr.rowIndex;
    }
  end;

  if Paging.Enabled then
  begin
    rindex := rindex + Paging.Index * Paging.Size;
  end;

  if (rindex <> -1) and (cindex <> -1) then
  begin
    DoCheckClick(cindex, rindex, chk);
  end;
end;

function TCustomTableControl.HandleDoClick(Event: TJSMouseEvent): Boolean;
var
  c,r: integer;
  tgt: TJSElement;
  sd: TGridSortIndicator;
  tag,s: string;

begin
  tgt := Event.targetElement;

  if Uppercase(tgt.tagName) = 'BUTTON' then
  begin
    HandleButtonClick(Event);
//    inherited;
    Exit;
  end;

  if Uppercase(tgt.tagName) = 'INPUT' then
  begin
    s := tgt['type'];
    if Assigned(s) and (uppercase(s) = 'CHECKBOX') then
    begin
      HandleCheckBoxClick(Event);
      inherited;
      Exit;
    end;
  end;


  sd := siNone;

  if tgt.tagName = 'SPAN' then
  begin
    if tgt['sort']= 'up' then
    begin
      sd := siAscending;
    end
    else
    if tgt['sort']= 'dn' then
    begin
      sd := siDescending;
    end;
  end;

  repeat
    asm
      tag = tgt.tagName.toLowerCase();
    end;
    if (tag <> 'td') and (tag <> 'th') then
      tgt := tgt.parentElement;
  until (tag = 'td') or (tag = 'th') or (tgt = nil);

  if (tag = 'td') or (tag = 'th') then
  begin
    asm
      c = tgt.cellIndex;
      var tr = tgt.parentElement;
      r = tr.rowIndex;
    end;

    if Header.Visible then
      dec(r);

//     // handle row selection
//     if (tag = 'td') and Enabled then
//     begin
//       RowIndex := r;
//     end;

    if Paging.Enabled then
    begin
      r := r + Paging.Index * Paging.Size;
    end;

    if Assigned(OnClickCell) and (sd = siNone) and Enabled then
      OnClickCell(Self, c, r);
  end;

  if sd = siAscending then
  begin
    DoSortClick(c,r,siDescending);
    SetSortIndicator(c,r, siDescending);
  end
  else
  if sd = siDescending then
  begin
    DoSortClick(c,r, siAscending);
    SetSortIndicator(c,r, siAscending);
  end;

  if Assigned(OnClick) then
    OnClick(Self);

  Result := true;
end;

function TCustomTableControl.HandleDoDblClick(Event: TJSMouseEvent): Boolean;
var
  c,r: integer;
  el: TJSElement;
begin
  el := Event.targetElement;

  if (el.tagName = 'TD') or (el.tagName = 'TH') then
  begin
    asm
      c = el.cellIndex;
      var tr = el.parentElement;
      r = tr.rowIndex;
    end;

    if Paging.Enabled then
    begin
      r := r + Paging.Index * Paging.Size;
    end;

    if Assigned(OnDblClickCell) then
      OnDblClickCell(Self, c, r);
  end;
  Result := true;
end;

function TCustomTableControl.HandleDoKeyPress(Event: TJSKeyBoardEvent): Boolean;
begin
  inherited;

  Result := true;
end;

{$HINTS OFF}
function TCustomTableControl.HandleDoMouseDown(Event: TJSMouseEvent): Boolean;
var
  r,maxr: integer;
  tgt,tr: TJSElement;
  sd: TGridSortIndicator;
  tag: string;

begin
  Result := true;

  if not Enabled then
    Exit;

  tgt := Event.targetElement;
  tag := '';
  sd := siNone;
  tr := nil;

  repeat
    asm
      tag = tgt.tagName.toLowerCase();
    end;
    if (tag <> 'td') and (tag <> 'th') then
      tgt := tgt.parentElement;
  until (tag = 'td') or (tag = 'th') or (tgt = nil);

  if (tag = 'td') or (tag = 'th') then
  begin
     asm
       tr = tgt.parentElement;
       r = tr.rowIndex;
     end;
     if Header.Visible then
       dec(r);

     if Paging.Enabled then
       maxr := Paging.Size
     else
       maxr := RowCount;

     if r > maxr then
       Exit;

     // handle row selection
     if (tag = 'td') or (tag = 'th') and Enabled then
     begin
       asm
         tag = tr.parentElement.tagName.toLowerCase();
       end;
       if tag <> 'thead' then  // can't select fixed col
         RowIndex := r;
     end;
  end;
end;

function TCustomTableControl.HandleDoMouseMove(Event: TJSMouseEvent): Boolean;
var
  IsAutoWidth: Boolean;
begin
  inherited;

  IsAutoWidth := (WidthStyle in [ssAuto]);
  asm
    autoWidth = IsAutoWidth;
    resizeColumn(Event);
  end;

  Result := True;
end;
{$HINTS ON}

function TCustomTableControl.HandleDoMouseUp(Event: TJSMouseEvent): Boolean;
begin
  inherited;

  asm
    resetResize();
  end;

  Result := True;
end;

function TCustomTableControl.HasCheckBox(ACol, ARow: integer): boolean;
var
  el: TJSElement;
begin
  Result := false;
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetCheckElement(ACol,ARow);
    Result := Assigned(el);
  end;
end;

function TCustomTableControl.HasProgress(const ACol, ARow: integer): boolean;
var
  el: TJSElement;
begin
  Result := false;
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetProgressElement(ACol,ARow);
    Result := Assigned(el);
  end;
end;

function TCustomTableControl.HasSortIndicator(const ACol,
  ARow: integer): boolean;
var
  el: TJSElement;
  sid: string;

begin
  sid := Name + 'si'+ACol.ToString+'_'+ARow.ToString;

  el := document.getElementById(sid);

  Result := Assigned(el);
end;

procedure TCustomTableControl.InitSample(AInitMethod: TInitMethod);
var
  i,j: integer;
  trc: integer;
begin
  trc := RenderRowCount;

  if RowHeader then
    inc(trc);

  for i := 0 to trc - 1 do
  begin
    for j := 0 to ColCount - 1 do
    begin
     case AInitMethod of
     imRandom:
       Cells[j,i] := IntToStr(Random(100));
     imLinear:
       Cells[j,i] := IntToStr(j)+':'+IntToStr(i);
     end;
    end;
  end;
end;

{$HINTS OFF}
procedure TCustomTableControl.HideColumn(const Index: integer);
var
  i,fr: integer;
  tr,td: TJSHTMLElement;

begin
  if (Index <0) or (Index > ColCount) then
    Exit;

  if Footer.Visible then
    fr := 1
  else
    fr := 0;

  asm
    for(i=0; i < this.FTable.rows.length - fr; i++)
    {
      tr = this.FTable.rows[i];
      td = tr.cells[Index];
      td.style.setProperty('display','none');
    }
  end;

  FHiddenCols.Add(Index.ToString);
end;

function TCustomTableControl.IsHiddenColumn(const Index: integer): boolean;
begin
  Result := FHiddenCols.IndexOf(Index.ToString) <> -1;
end;

procedure TCustomTableControl.UnHideColumn(const Index: integer);
var
  i,idx: integer;
  tr,td: TJSHTMLElement;
begin
  if (Index <0) or (Index > ColCount) then
    Exit;

  idx := FHiddenCols.IndexOf(Index.ToString);

  if idx = -1 then
    Exit;

  FHiddenCols.Delete(idx);

  asm
    for(i=0; i<this.FTable.rows.length;i++)
    {
      tr = this.FTable.rows[i];
      td = tr.cells[Index];
      td.style.removeProperty('display');
    }
  end;

end;

procedure TCustomTableControl.HideRow(const Index: integer);
begin
  if (Index <0) or (Index > RowCount) then
    Exit;

  if not Assigned(FTable) then
    Exit;

  asm
    var tr = this.FTable.rows[Index];
    tr.style.setProperty('display','none');
  end;
end;

procedure TCustomTableControl.UnHideRow(const Index: integer);
begin
  if (Index <0) or (Index > RowCount) then
    Exit;

  asm
    var tr = this.FTable.rows[Index];
    tr.style.removeProperty('display');
  end;
end;

procedure TCustomTableControl.InsertRow(Index: integer);
var
  i,cnt: integer;
  ch: boolean;
begin
  if (Index <0) or (Index > RowCount) then
    Exit;

  cnt := ColCount;
  ch := ColHeader;

  asm
    var tr = this.FTable.insertRow(Index);

    for(i = 0; i < cnt; i++)
    {
      if ((i == 0) && (ch)) {
        var th = document.createElement('th');
        th.setAttribute('scope','row');
        tr.appendChild(th);
        }
      else
      {
        tr.insertCell();
      }
    }
  end;
end;

procedure TCustomTableControl.Invalidate;
begin
  inherited;

  if not (csloading in ComponentState) then
    Exit;

  Rerendergrid;
  if Header.Visible then
    Header.Update(Paging.Size, RowCount, Paging.Index);

  if Footer.Visible then
    Footer.Update(Paging.Size, RowCount, Paging.Index);
end;

function TCustomTableControl.InVisiblePage(var ARow: integer): boolean;
begin
  if Paging.Enabled then
  begin
    Result := (ARow >= Paging.Size * Paging.Index) and (ARow < Paging.Size * (Paging.Index + 1));
    ARow := ARow - (Paging.Size * Paging.Index);
  end
  else
    Result := ARow <= RowCount;
end;

procedure TCustomTableControl.KeyDown(var Key: Word; Shift: TShiftState);
var
  fr: integer;
begin
  inherited;

  if not Enabled then
    Exit;

  fr := 0;
  if RowHeader  then
    fr := 1;

  if Key = VK_UP then
    RowIndex := Max(fr, RowIndex - 1);

  if Key = VK_DOWN then
    RowIndex := Min(RowCount - 1, RowIndex + 1);

  if Key = VK_HOME then
    RowIndex := fr;

  if Key = VK_END then
    RowIndex := RowCount - 1;

  if Key = VK_NEXT then
    RowIndex := Min(RowCount - 1, RowIndex + 4);

  if Key = VK_PRIOR then
    RowIndex := Max(fr, RowIndex - 4);
end;

{$HINTS ON}

procedure TCustomTableControl.LoadFromStringList(AStrings: TStrings);
var
  firstrow: integer;
  arow: TStringList;
  i,j,cr: integer;
  cd, cn: string;
  ce: TJSHTMLElement;

begin
  if RowHeader and not FLoadFixed then
    firstrow := 1
  else
    firstrow := 0;

  RowCount := AStrings.Count + firstrow;

  arow := TStringList.Create;

  try
    for i := 0 to AStrings.Count - 1 do
      begin
        arow.StrictDelimiter := true;
        arow.Delimiter := FDelimiter;
        arow.DelimitedText := AStrings.Strings[i];

        if arow.Count > ColCount then
          ColCount := arow.Count;

        for j := 0 to arow.Count - 1 do
        begin
          cd := arow.Strings[j];
          GetCellData(j, i + firstrow, nil, cd);

          Cells[j, i + firstrow] := cd;

          cr := i + firstrow;
          if InVisiblePage(cr) then
          begin
            ce := CellElements[j, cr];

            GetCellChildren(j, cr, nil, cd, ce);

            cn := '';
            GetCellClassName(j, cr, nil, cd, cn);
            if cn <> '' then
              ce.setAttribute('class',cn);
          end;
        end;
      end;
  finally
    arow.Free;
  end;

end;

procedure TCustomTableControl.LoadFromStrings(AStrings: TStrings; Delimiter: char = ';'; LoadFixed: boolean = false);
begin
  FDelimiter := Delimiter;
  FLoadFixed := LoadFixed;

  LoadFromStringList(AStrings);
end;

procedure TCustomTableControl.Loaded;
begin
  inherited;
end;

procedure TCustomTableControl.LoadFromCSV(const AURL: string; Delimiter: char = ';'; LoadFixed: boolean = false);
begin
  FDelimiter := Delimiter;
  FLoadFixed := LoadFixed;

  FReq := TJSXMLHttpRequest.new;
  FReq.addEventListener('load',@DoHttpLoadCsv);
  FReq.addEventListener('abort',@DoHttpAbort);
  FReq.open('GET', AURL);
  DoHttpRequest(FReq);
  FReq.send;
end;

function TCustomTableControl.LoadFromCSVAsync(const AURL: string; Delimiter: char = ';'; LoadFixed: boolean = false): TJSPromise;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)

      function DoLoad(Event: TEventListenerEvent): boolean;
      begin
        if DoHttpLoadCsv(Event) then
          ASuccess(TJSXMLHttpRequest(Event.target))
        else
          AFailed(TJSXMLHttpRequest(Event.target));
        Result := true;
      end;

      function DoError(Event: TEventListenerEvent): boolean;
      begin
        AFailed(TJSXMLHttpRequest(Event.target));
        Result := true;
      end;

    begin
      FDelimiter := Delimiter;
      FLoadFixed := LoadFixed;
      FReq := TJSXMLHttpRequest.new;
      FReq.addEventListener('load',@DoLoad);
      FReq.addEventListener('abort',@DoError);
      FReq.addEventListener('timeout',@DoError);
      FReq.addEventListener('error',@DoError);

      FReq.open('GET', AURL);
      DoHttpRequest(FReq);
      FReq.send;
    end);
end;

procedure TCustomTableControl.LoadFromJSON(const AURL: string; ADataNode: string = '');
begin
  FDataNode := ADataNode;
  FReq := TJSXMLHttpRequest.new;
  FReq.addEventListener('load',@DoHttpLoadJson);
  FReq.addEventListener('abort',@DoHttpAbort);
  FReq.open('GET', AURL);
  DoHttpRequest(FReq);
  FReq.send;
end;

procedure TCustomTableControl.LoadPageFromJSON(const AURL: string; ADataNode: string = '');
begin
  FDataNode := ADataNode;
  FReq := TJSXMLHttpRequest.new;
  FReq.addEventListener('load',@DoHttpLoadPageJson);
  FReq.addEventListener('abort',@DoHttpAbort);
  FReq.open('GET', AURL);
  DoHttpRequest(FReq);
  FReq.send;
end;

function TCustomTableControl.LoadFromJSONAsync(const AURL: string; ADataNode: string = ''): TJSPromise;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)

      function DoLoad(Event: TEventListenerEvent): boolean;
      begin
        if DoHttpLoadJson(Event) then
          ASuccess(TJSXMLHttpRequest(Event.target))
        else
          AFailed(TJSXMLHttpRequest(Event.target));
        Result := true;
      end;

      function DoError(Event: TEventListenerEvent): boolean;
      begin
        AFailed(TJSXMLHttpRequest(Event.target));
        Result := true;
      end;

    begin
      FDataNode := ADataNode;
      FReq := TJSXMLHttpRequest.new;
      FReq.addEventListener('load',@DoLoad);
      FReq.addEventListener('abort',@DoError);
      FReq.addEventListener('timeout',@DoError);
      FReq.addEventListener('error',@DoError);

      FReq.open('GET', AURL);
      DoHttpRequest(FReq);
      FReq.send;
    end);
end;

function TCustomTableControl.LoadPageFromJSONAsync(const AURL: string; ADataNode: string = ''): TJSPromise;
begin
  Result := TJSPromise.new(
    procedure(ASuccess, AFailed: TJSPromiseResolver)

      function DoLoad(Event: TEventListenerEvent): boolean;
      begin
        if DoHttpLoadPageJson(Event) then
          ASuccess(TJSXMLHttpRequest(Event.target))
        else
          AFailed(TJSXMLHttpRequest(Event.target));
        Result := true;
      end;

      function DoError(Event: TEventListenerEvent): boolean;
      begin
        AFailed(TJSXMLHttpRequest(Event.target));
        Result := true;
      end;

    begin
      FDataNode := ADataNode;
      FReq := TJSXMLHttpRequest.new;
      FReq.addEventListener('load',@DoLoad);
      FReq.addEventListener('abort',@DoError);
      FReq.addEventListener('timeout',@DoError);
      FReq.addEventListener('error',@DoError);

      FReq.open('GET', AURL);
      DoHttpRequest(FReq);
      FReq.send;
    end);
end;


{$HINTS OFF}

procedure TCustomTableControl.LoadFromJSON(AJSON: TJSObject; ADataNode: string = '');
var
  JO: JSValue;
  JA: TJSArray;
  r,c,cr,firstrow: integer;
  arow: string;
  sl: TStringList;
  cd,cn: string;
  ce: TJSHTMLElement;
  p: boolean;
begin
  if ADataNode <> '' then
    JA := TJSArray(AJSON.Properties[ADataNode])
  else
    JA := TJSArray(AJSON);

  if Assigned(JA) then
  begin
    p := Paging.Enabled;
    Paging.Enabled := false;

    sl := TStringList.Create;

    firstrow := 0;

    if RowHeader then
      firstrow := 1;

    RowCount := JA.length + firstrow;

    for r := 0 to JA.length - 1 do
    begin
      JO := JA.Elements[r];
      c := 0;
      arow := '';

      asm
        var x = undefined;
        for (x in JO) {
          arow += "|" + JO[x];
          c++;
        }
      end;

      Delete(arow, 1, 1);
      sl.StrictDelimiter := true;
      sl.Delimiter := '|';
      sl.DelimitedText := arow;
      if ColCount < sl.Count  then
        ColCount := sl.Count;

      for c := 0 to sl.Count - 1 do
      begin
        cd := sl.Strings[c];
        GetCellData(c, r + firstrow, nil, cd);

        Cells[c, r + firstrow] := cd;

        cr := r;

        if InVisiblePage(cr) then
        begin
          cr := cr + firstrow;
          ce := CellElements[c, cr];

          GetCellChildren(c, cr, nil, cd, ce);

          cn := '';
          GetCellClassName(c, cr, nil, cd, cn);
          if cn <> '' then
            ce.setAttribute('class',cn);
        end;
      end;

      Paging.Enabled := p;
    end;

    sl.Free;
  end;
end;

procedure TCustomTableControl.LoadPageFromJSON(AJSON: TJSObject; ADataNode: string = '');
var
  JO: JSValue;
  JA: TJSArray;
  r,c,cr,firstrow: integer;
  arow: string;
  sl: TStringList;
  cd,cn: string;
  ce: TJSHTMLElement;
  p: boolean;
begin
  if ADataNode <> '' then
    JA := TJSArray(AJSON.Properties[ADataNode])
  else
    JA := TJSArray(AJSON);

  if Assigned(JA) then
  begin
    sl := TStringList.Create;

    firstrow := Paging.Index * Paging.Size;

    if RowHeader then
      firstrow := firstrow + 1;

    for r := 0 to JA.length - 1 do
    begin
      JO := JA.Elements[r];
      c := 0;
      arow := '';

      asm
        var x = undefined;
        for (x in JO) {
          arow += "|" + JO[x];
          c++;
        }
      end;

      Delete(arow, 1, 1);
      sl.StrictDelimiter := true;
      sl.Delimiter := '|';
      sl.DelimitedText := arow;
      if ColCount < sl.Count  then
        ColCount := sl.Count;

      for c := 0 to sl.Count - 1 do
      begin
        cd := sl.Strings[c];
        GetCellData(c, r + firstrow, nil, cd);

        Cells[c, r + firstrow] := cd;

        cr := r;

        if InVisiblePage(cr) then
        begin
          cr := cr + firstrow;
          ce := CellElements[c, cr];

          GetCellChildren(c, cr, nil, cd, ce);

          cn := '';
          GetCellClassName(c, cr, nil, cd, cn);
          if cn <> '' then
            ce.setAttribute('class',cn);
        end;
      end;
    end;

    sl.Free;
  end;
end;


function TCustomTableControl.HasButton(ACol, ARow: integer): boolean;
var
  el: TJSElement;
begin
  Result := false;

  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetButtonElement(ACol,ARow);
    if Assigned(el) then
    begin
      Result := true;
    end;
  end;
end;

procedure TCustomTableControl.RemoveButton(ACol, ARow: integer);
var
  el: TJSElement;
begin
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetButtonElement(ACol,ARow);
    if Assigned(el) then
    begin
      el.removeEventListener('click',FHandleCheckClickPtr);
      asm
        el.remove();
      end;
    end;
  end;
end;

procedure TCustomTableControl.RemoveCheckBox(ACol, ARow: integer);
var
  el: TJSElement;
begin
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetCheckElement(ACol,ARow);
    if Assigned(el) then
    begin
      el.removeEventListener('click',FHandleCheckClickPtr);
      asm
        el.remove();
      end;
    end;
  end;
end;

procedure TCustomTableControl.RemoveFilter;
var
  r,rs,l: integer;
  tr: TJSHTMLElement;
begin
  if Assigned(FTable) then
  begin
    if ColHeader then
      rs := 1
    else
      rs := 0;

    if Paging.Enabled then
    begin
      if Assigned(FUnfiltered) then
      begin
        asm
          this.FData = Array.from(this.FUnfiltered);
        end;
        FUnfiltered := nil;
      end;
      asm
        l = this.FData.length;
      end;

      RowCount := l;
      SelectPage(Paging.ActivePage, true);
    end
    else
    begin
      for r := rs to RowCount - 1 do
      begin
        asm
          tr = this.FTable.rows[r];
        end;
        tr.style.removeProperty('display');
      end;
    end;
  end;
end;

procedure TCustomTableControl.RemoveProgress(const ACol, ARow: integer);
var
  el: TJSElement;
begin
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetProgressElement(ACol,ARow);
    asm
      el.remove();
    end;
  end;
end;

procedure TCustomTableControl.RemoveRow(Index: integer);
begin
  if (Index <0) or (Index >= RowCount) then
    Exit;

  asm
    var tr = this.FTable.deleteRow(Index);
  end;

end;

procedure TCustomTableControl.RemoveSortIndicator(const ACol, ARow: integer);
var
  el: TJSElement;
  sid: string;

begin
  sid := Name + 'si'+ACol.ToString+'_'+ARow.ToString;

  el := document.getElementById(sid);

  if Assigned(el) then
    el.parentElement.removeChild(el);
end;

procedure TCustomTableControl.RenderGrid;
var
  i: integer;
  table,tbody,th: TJSHTMLElement;
  frows, fcols, fhc,rc: integer;
  rh,ch: boolean;
  headclass,cc,headcaption: string;
  ridx: integer;

begin
  if not Assigned(FTable) then
  begin
    FTable := document.createElement('TABLE');
    ElementHandle.appendChild(FTable);

    SetHTMLElementFont(TJSHTMLElement(FTable), Font, not ((ElementClassName = '') and (ElementFont = efProperty)) );

    table := TJSHTMLElement(FTable);
    table.setAttribute('id', Name + 'Table');
    table.style.setProperty('vertical-align','middle');

    frows := RenderRowCount;

    fcols := ColCount;
    rh := RowHeader;
    ch := ColHeader;
    rc := RowCount;

    cc := '';
    ridx := 0;
    fhc := 0;

    if (ElementTableClassName = '') then
    begin
      table.style.setProperty('border-collapse','collapse');
      table.setAttribute('border','0');
      table.setAttribute('cellpadding','0');
      table.setAttribute('cellspacing','0');
      table.setAttribute('width','100%');
      table.setAttribute('table-layout','fixed');
      table.setAttribute('height','100%');
    end
    else
    begin
      table.setAttribute('class',ElementTableClassName);
    end;

    headclass := ElementHeaderClassName;

    asm
      while (table.firstChild) {
        table.removeChild(table.firstChild);
      }
    end;

    Header.FElement := nil;

    if Header.Visible then
    begin
      inc(ridx);
      headcaption := Header.Caption;

      asm
        var theader = table.createTHead();

        if (headclass != "")
        {
          theader.setAttribute("class", headclass);
        }

        var tr = theader.insertRow(0);
        th = document.createElement('th');
        tr.appendChild(th);
        th.setAttribute('colspan',fcols);
        th.setAttribute('vertical-align','middle');
        if (headcaption != '') {
          th.innerHTML = '<div style="float:left;margin-right:10px;vertical-align:middle">'+headcaption+'</div>'; }
      end;
      Header.FElement := th;
    end;


    asm
      var i = 0;
      var j = 0;

      if (rh)
      {
        fhc++;

        var theader = table.createTHead();

        if (headclass != "")
        {
          theader.setAttribute("class", headclass);
        }

        var tr = theader.insertRow(ridx);

        for(i=0; i < fcols; i++)
        {
          if (rc > 0) {
          cc = this.FData[0][i];
          if (cc == undefined) { cc = "";}
          }
          else
          {
            cc = "";
          }
          var th = document.createElement('th');
          th.setAttribute('scope','col');
          this.SetTableCell(i,0, th, cc);
          tr.appendChild(th);
        }
      }

      tbody = table.appendChild(document.createElement('tbody'));

      for(i=0; i < frows; i++)
      {
        if (i + fhc >= this.FData.length) { break; }

        var tr = tbody.insertRow(tbody.length);

        for(j=0; j < fcols; j++)
        {
          cc = this.FData[i+fhc][j];
          if (cc == undefined) { cc = "";}

          if ((j == 0) && (ch))
          {
            var th = document.createElement('th');
            th.setAttribute('scope','row');
            tr.appendChild(th);
            this.SetTableCell(j,i + fhc,th, cc);
          }
          else
          {
            tr.insertCell();
            var td = tr.cells[j];
            this.SetTableCell(j,i + fhc,td, cc);
          }
        }
      }
    end;

    FBody := tbody;

    Footer.FElement := nil;

    if Footer.Visible then
    begin
      inc(ridx);
      headcaption := Footer.Caption;
      asm
        var theader = table.createTFoot();

        if (headclass != "")
        {
          theader.setAttribute("class", headclass);
        }

        var tr = theader.insertRow(0);
        th = document.createElement('th');
        tr.appendChild(th);
        th.setAttribute('colspan',fcols);
//        th['colspan'] = fcols;
        th.innerHTML = headcaption;
      end;
      Footer.FElement := th;
    end;

    if DragMode = dmAutomatic then
      EnableDrag;
  end;

  if not Options.ResizeColumns then
  begin
    // update column widths
//    for i := 0 to ColCount - 1 do
//      ColWidths[i] := ColWidths[i];
  end;

  EnableColResize;

  if FHiddenCols.Count > 0 then
  begin
    for i := 0 to ColCount - 1 do
    begin
      if IsHiddenColumn(i) then
        HideColumn(i);
    end;
  end;
end;

procedure TCustomTableControl.RenderGridData;
var
  i,j: integer;
begin
  for i := 0 to ColCount - 1 do
    for j := 0 to RowCount - 1 do
      Cells[i,j] := Cells[i,j];
end;

procedure TCustomTableControl.RenderHeaderFooter;
begin
  if Header.Visible then
    Header.Update(Paging.Size, RowCount, Paging.Index);

  if Footer.Visible then
    Footer.Update(Paging.Size, RowCount, Paging.Index);
end;

procedure TCustomTableControl.RenderGridScript;
begin
  AddControlScriptSource(
  'var pageX,curCol,nxtCol,curColWidth,nxtColWidth,autoWidth,curTable,curTableWidth;'+
  'autoWidth = false;'+

  'function resizeColumn(e) {'+
  '   if (curCol) {'+
  '    var diffX = e.pageX - pageX;'+
  '		if ((nxtCol) && (!autoWidth)){'+
  '		 nxtCol.style.width = (nxtColWidth - diffX)+"px";'+
  '   } else {'+
//  '    curTable.style.width = (curTableWidth + diffX) + "px";'+
  '   }'+
  '   newColWidth = (curColWidth + diffX);'+
  '   if (newColWidth < 10)'+
  '    newColWidth = 10;'+
  '	  curCol.style.width = newColWidth + "px";'+
  '   }'+
  ' }'+

  'function resetResize() {'+
  '   curCol = undefined;'+
  '   nxtCol = undefined;'+
  '   pageX = undefined;'+
  '   nxtColWidth = undefined;'+
  '   curColWidth = undefined;'+
  '   curTable = undefined;'+
  '   curTableWidth = undefined;'+
  ' }'+

  'function resizableGrid(tablename, enabled,ridx) {'+
  ' var table = document.getElementById(tablename + "Table");'+
  ' if (!table) return;' +
  ' var row = table.getElementsByTagName("tr")[ridx],'+
  ' cols = row ? row.children : undefined;'+
  ' if (!cols) return;'+

  ' for (var i=0;i<cols.length;i++){'+
  '  var coldiv = document.getElementById(tablename + "Div" + i);'+

  '  if (coldiv)'+
  '    coldiv.remove();'+

  '  if (enabled) {'+
  '    var div = createDiv();'+
  '    cols[i].appendChild(div);'+
  '    cols[i].style.position = "relative";'+
  '    div.id = tablename + "Div" + i;'+
  '    setListeners(div);'+
  '  }'+
  ' }'+

  ' function setListeners(div){'+

  '  div.addEventListener("mousedown", function (e) {'+
  '   curCol = e.target.parentElement;'+
  '   nxtCol = curCol.nextElementSibling;'+
  '   pageX = e.pageX;'+

  '   curColWidth = curCol.offsetWidth;'+
  '   curTable = curCol.parentElement.parentElement.parentElement;'+
  '   curTableWidth = curTable.offsetWidth;'+
  '   if (nxtCol)'+
  '    nxtColWidth = nxtCol.offsetWidth;'+
  '  });'+

  '  div.addEventListener("mouseover", function (e) {'+
  '   e.target.style.borderRight = "2px solid #0000ff";'+
  '  });'+

  '  div.addEventListener("mouseout", function (e) {'+
  '   e.target.style.borderRight = "";'+
  '  });'+

  '  document.addEventListener("mousemove", function (e) {'+
  '   resizeColumn(e);'+
  '  });'+

  '  document.addEventListener("mouseup", function (e) {'+
  '   resetResize(e);'+
  '  });'+
  ' }'+

  ' function createDiv(){'+
  '  var div = document.createElement("div");'+
  '  div.style.top = 0;'+
  '  div.style.right = 0;'+
  '  div.style.width = "10px";'+
  '  div.style.position = "absolute";'+
  '  div.style.cursor = "col-resize";'+
  '  div.style.userSelect = "none";'+
  '  div.style.height = "100%";'+
  '  div.innerHTML = "&nbsp;";'+
//  '  div.style.border = "1px solid red";'+
  '  return div;'+
  ' }'+

  '};');
end;

function TCustomTableControl.RenderRowCount: integer;
begin
  if Paging.Enabled and (Paging.Size > 0) then
    Result := Paging.Size
  else
  begin
    if RowHeader then
      Result := RowCount - 1
    else
      Result := RowCount;
  end;
end;

procedure TCustomTableControl.ReRenderGrid;
begin
  if not Assigned(ElementHandle) or IsUpdating then
    Exit;

  if Assigned(FTable) then
    FTable.parentElement.removeChild(FTable);

  FTable := nil;
  RenderGrid;
  RenderHeaderFooter;
end;

procedure TCustomTableControl.RowCountChanged(delta: integer);
var
  r: integer;
  tbody: TJSElement;
begin
  if not Assigned(FTable) then
    Exit;

  tbody := FBody;

  if (delta > 0) then
  begin

    asm
      var i = 0;
      var j = 0;
      var tr = undefined;
      var l = tbody.rows.length;

      for(i = 0;i < delta; i++)
      {
         tr = tbody.insertRow(l);
         for(j = 0; j < this.FColCount; j++)
         {

          if ((j == 0) && (this.FColHeader))
          {
            var th = document.createElement('th');
            th.setAttribute('scope','row');
            tr.appendChild(th);
          }
          else
          {
            tr.insertCell();
          }
         }
      }
    end;

    if DragMode = dmAutomatic then
      EnableDrag;
  end;

  if delta < 0 then
  begin
    asm
      var i = 0;
      var l = this.FBody.rows.length;
      if (l < -delta) return;
      for(i = 0;i < -delta; i++) {
        this.FBody.deleteRow(l - i - 1);
      }
    end;
  end;
end;
{$HINTS ON}

function TCustomTableControl.RowOffset: integer;
begin
  if RowHeader then
    Result := 1
  else
    Result := 0;
end;

procedure TCustomTableControl.SaveToCSV(const AFileName: string;
  Delimiter: char; SaveFixed: boolean);
var
  sl: TStringList;
begin
  sl := TStringList.Create;
  try
    SaveToStrings(sl, Delimiter, SaveFixed);
    sl.SaveToFile(AFileName);
  finally
    sl.Free;
  end;
end;

procedure TCustomTableControl.SaveToStrings(AStrings: TStrings; Delimiter: char;
  SaveFixed: boolean);
var
  r,c: integer;
  fr,fc: integer;
  s,rowstr: string;
begin
  fr := 0;
  fc := 0;

  if not SaveFixed and ColHeader then
    fc := 1;

  if not SaveFixed and RowHeader then
    fr := 1;

  for r := fr to RowCount - 1 do
  begin
    rowstr := '';
    for c := fc to ColCount - 1 do
    begin
      s := Cells[c,r];
      s := stringreplace(s,'"','""',[rfReplaceAll]);

      if (pos(Delimiter,s) > 0) or (pos(#13, s) > 0) then
      begin
        s := '"' +  s +  '"';
      end;

      if s = '' then
        s := '""';

      if rowstr = '' then
        rowstr := s
      else
        rowstr := rowstr + Delimiter + s;
    end;
    AStrings.Add(rowstr);
  end;
end;

procedure TCustomTableControl.ScrollRowInView(ARow: integer);
begin
  if (ARow < RowCount) and Assigned(FTable) then
  begin
    asm
     this.FTable.rows[ARow].scrollIntoView({
       behavior: 'smooth',
       block: 'center'
       });
    end;
  end;
end;

procedure TCustomTableControl.SelectCell(ACol, ARow: integer);
var
  el: TJSElement;
begin
  if (ARow < RowCount) and (ACol < ColCount) then
  begin
    el := CellElements[ACol,ARow];
    if Assigned(el) then
      SelectElementContents(el);
  end;
end;

{$HINTS OFF}
procedure TCustomTableControl.SelectPage(AIndex: integer; DoDefault: boolean = true);
var
  i,j,delta,offset,hdr: integer;
  tr,td: TJSHTMLElement;
  s: string;

begin
  if Paging.Enabled then
  begin
    Paging.FActivePage := AIndex;

    RenderHeaderFooter;

    if DoDefault then
    begin
      if RowHeader then
        delta := 1
      else
        delta := 0;

      if Header.Visible then
        hdr := 1
      else
        hdr := 0;

      offset := AIndex * Paging.Size;

      for i := 0 to Paging.Size - 1 do
      begin
        asm
          tr = this.FTable.rows[i + delta + hdr];
        end;
        if Assigned(tr) then
        begin
          if (offset + i + delta >= RowCount) then
          begin
            asm
              tr.style.setProperty('display','none');
            end;
            continue;
          end;

          s := '';

          for j := 0 to ColCount - 1 do
          begin
            asm
              s = this.FData[offset + i + delta][j];
              if (s != undefined) {
                td = tr.cells[j];
                this.SetTableCell(j,i+delta,td,s);
              }
              tr.style.removeProperty('display');
            end;
          end;
        end;
      end;
    end;
  end;
end;
{$HINTS ON}

procedure TCustomTableControl.SelectSortIndicator(const ACol, ARow: integer;
  ASelect: boolean);
var
  el: TJSElement;
  sid: string;

begin
  sid := Name + 'si'+ACol.ToString+'_'+ARow.ToString;

  el := document.getElementById(sid);

  if Assigned(el) then
  begin
    if ElementSelectionClassName = '' then
    begin
      if ASelect then
        TJSHTMLElement(el).style.setProperty('color',ColorToHTML($FF8F5B))
      else
        TJSHTMLElement(el).style.removeProperty('color');
    end
    else
    begin
      if ASelect then
        TJSHTMLElement(el).classlist.Add(ElementSelectionClassName)
      else
        TJSHTMLElement(el).classlist.Remove(ElementSelectionClassName);
    end;
  end;
end;

procedure TCustomTableControl.SetCells(col, row: integer; const Value: string);
var
  trc: integer;
  td: TJSHTMLElement;
begin
  if not Assigned(FTable) then
    Exit;

  trc := RowCount;
  if RowHeader then
    inc(trc);

  if (col >= 0) and (row >= 0) and (col < ColCount) and (row <= trc) then
  begin
    if (row = 0) then
      EnableColResize;

    asm
      if (row < this.FData.length) {
        this.FData[row][col] = Value;
      }
    end;

    if RowHeader then
      row := row - 1;

    if InVisiblePage(row) or ((row = -1) and RowHeader) then
    begin
      if Header.Visible then
        inc(row);
      if RowHeader then
        row := row + 1;

      asm
        td = this.FTable.rows[row].cells[col];
      end;

      SetTableCell(col,row,td, Value);
    end;
  end;
end;

procedure TCustomTableControl.SetWordWrap(const AValue: boolean);
begin
  if FWordWrap <> AValue then
  begin
    FWordWrap := AValue;
    if Assigned(FTable) then
    begin
      if not (csloading in ComponentState) then
        ReRenderGrid;
    end;
  end;
end;

procedure TCustomTableControl.SetColCount(const Value: integer);
begin
  inherited;

  asm
    var i;
    for(i=0; i < this.FData.length; i++)
    {
      if (this.FData[i] == null) this.FData[i] = [];

      this.FData[i].length = Value;
    }
  end;
end;

procedure TCustomTableControl.SetColHeader(const Value: boolean);
begin
  if FColHeader <> Value then
  begin
    FColHeader := Value;
    if Assigned(FTable) then
    begin
      if not (csloading in ComponentState) then
        ReRenderGrid;
    end;
  end;
end;

procedure TCustomTableControl.SetDataColumnCount(AValue: Integer);
begin
  if RowHeader then
    ColCount := AValue + 1
  else
    ColCount := AValue;
end;

procedure TCustomTableControl.SetDataHeader(AColumn: Integer; AValue: string);
var
  d: integer;
begin
  if not ColHeader then
    Exit;

  d := 0;
  if RowHeader then
    d := 1;

  Cells[AColumn + d, 0] := AValue;
end;

procedure TCustomTableControl.SetDataRowCount(AValue: Integer);
begin
  if ColHeader then
    RowCount := AValue + 1
  else
    RowCount := AValue;
end;

procedure TCustomTableControl.SetDataValue(AColumn, ARow: Integer;
  AValue: string);
var
  dc,dr: integer;
begin
  dc := 0;
  dr := 0;

  if RowHeader then
    dc := 1;
  if ColHeader then
    dr := 1;

  Cells[AColumn + dc, ARow + dr] := AValue;
end;

procedure TCustomTableControl.SetElementHeaderClassName(
  const Value: TElementClassName);
var
  head: TJSHTMLElement;
begin
  if (FElementHeaderClassName <> Value) then
  begin
    FElementHeaderClassName := Value;
    if Assigned(FTable) then
    begin
      head := TJSHTMLElement(FTable.firstChild);
      if Assigned(head) and (head.tagName = 'THEAD') then
        head.setAttribute('class',ElementHeaderClassName);
    end;
  end;
end;

procedure TCustomTableControl.SetElementTableClassName(
  const Value: TElementClassName);
begin
  if (FElementTableClassName <> Value) then
  begin
    FElementTableClassName := Value;
    if Assigned(FTable) then
      FTable.setAttribute('class',ElementTableClassName);
  end;
end;

procedure TCustomTableControl.SetFilter(Column: integer; Condition: string; CaseSensitive: boolean = true);
var
  r,rs,hdr,l: integer;
  tr: TJSHTMLElement;
  s: string;
begin
  if Assigned(FTable) then
  begin
    if Condition = '' then
    begin
      RemoveFilter;
      Exit;
    end;

    hdr := 0;
    rs := 0;

    if RowHeader then
      rs := 1;

    if Header.Visible then
      hdr := 1;

    if Paging.Enabled then
    begin
      if not Assigned(FUnfiltered) then
      begin
        asm
          this.FUnfiltered  = Array.from(this.FData);
        end;
      end;

      for r := RowCount - 1 downto rs do
      begin
        asm
          s = this.FData[r][Column];
        end;

        if not MatchStr(Condition, s, CaseSensitive) and (hdr < 2) then
        begin
          asm
            this.FData.splice(r,1);
          end;
        end;
      end;

      asm
        l = this.FData.length;
      end;

      RowCount := l;

      SelectPage(Paging.ActivePage, true);
    end
    else
    begin

      for r := rs to RowCount - 1 do
      begin
        if not MatchStr(Condition, Cells[Column, r], CaseSensitive) and (hdr < 2) then
        begin
          asm
            tr = this.FTable.rows[r + hdr];
          end;
          tr.style.setProperty('display','none');
        end;
      end;
    end;
  end;
end;

procedure TCustomTableControl.SetFooter(const Value: TTableControlHeader);
begin
  FFooter.Assign(Value);
end;

procedure TCustomTableControl.SetHeader(const Value: TTableControlHeader);
begin
  FHeader.Assign(Value);
end;

procedure TCustomTableControl.SetOptions(const Value: TTableControlOptions);
begin
  FOptions.Assign(Value);
end;

procedure TCustomTableControl.SetPaging(const Value: TTableControlPaging);
begin
  FPaging.Assign(Value);
end;

procedure TCustomTableControl.SetProgress(ACol, ARow, APosition: integer);
var
  el: TJSElement;
begin
  if (ACol < ColCount) and (ARow < RowCount) then
  begin
    el := GetProgressElement(ACol,ARow);
    if Assigned(el) then
    begin
      el['value'] := APosition.ToString;
    end;
  end;
end;

procedure TCustomTableControl.SetRowClassName(row: integer; const Value: TElementClassName);
begin
  if (row >= 0) and (row < RowCount) then
  begin
    asm
      var tr = this.FTable.rows[row];
      tr.setAttribute('class',Value);
    end;
  end;
end;

{$HINTS OFF}
procedure TCustomTableControl.SetRowCount(const Value: integer);
var
  delta: integer;
  trc,trh,i: integer;

begin
  if (FRowCount <> Value) and (Value >= 0) then
  begin
    trc := Value;
//    if RowHeader then
//      inc(trc);
//    if Header.Visible then
//      inc(trc);
    asm
      this.FData.length = trc;
      var i;

      for(i = 0; i < this.FData.length; i++) {
        if (this.FData[i] == null) {
          this.FData[i] = [];
          this.FData[i].length = this.FColCount;
        }
      }
    end;
//    if Paging.Enabled then
//    begin
//      trc := 0;
//      if RowHeader then
//        inc(trc);
//
//      delta := Min(Paging.Size + trc, Value) - FRowCount
//    end
//    else
      delta := Value - FRowCount;

    if (FRowcount = 0) and RowHeader then
      delta := Value - 1;

    FRowCount := Value;
    RowCountChanged(delta);
    GridChanged;

    if Paging.Enabled then
    begin
      trc := 0;
      trh := 0;
      if RowHeader then
        inc(trc);
      if Header.Visible then
        inc(trh);

      for i := Paging.Size + trc + trh to RowCount - 1 + trh do
      begin
        HideRow(i);
      end;
    end;

    if (Header.Pager <> tpNone) then
      Header.Update(Paging.Size, RowCount, Paging.Index);

    if (Footer.Pager <> tpNone) then
      Footer.Update(Paging.Size, RowCount, Paging.Index);
  end;
end;
{$HINTS ON}

procedure TCustomTableControl.SetRowHeader(const Value: boolean);
begin
  if (FRowHeader <> Value) then
  begin
    FRowHeader := Value;
    if Assigned(FTable) then
    begin
      if not (csloading in ComponentState) then
        ReRenderGrid;
    end;
  end;
end;

procedure TCustomTableControl.SetRowIndex(const Value: integer);
var
  el: TJSElement;
begin
  if FRowIndex <> -1 then
  begin
    el := CellElements[0,FRowIndex];
    if Assigned(el) then
    begin
      if ElementRowSelectClassName = '' then
      begin
        TJSHTMLElement(el.parentElement).style.removeProperty('color');
        TJSHTMLElement(el.parentElement).style.removeProperty('background-color');
      end
      else
        el.parentElement.className := '';
    end;
  end;

  FRowIndex := Value;

  if (FRowIndex <> -1) then
  begin
    el := CellElements[0,FRowIndex];

    if Assigned(el) then
    begin
      console.log(el);
      if ElementRowSelectClassName = '' then
      begin
        if (SelectionTextColor <> clNone) then
          TJSHTMLElement(el.parentElement).style.setProperty('color', ColorToHTML(SelectionTextColor));
        if (SelectionColor <> clNone) then
          TJSHTMLElement(el.parentElement).style.setProperty('background-color',ColorToHTML(SelectionColor));
      end
      else
        TJSHTMLElement(el.parentElement).classList.add(ElementRowSelectClassName);
    end;
  end;
end;

procedure TCustomTableControl.SetSelectionColor(const Value: TColor);
begin
  if (FSelectionColor <> Value) then
  begin
    FSelectionColor := Value;
    UpdateElement;
  end;
end;

procedure TCustomTableControl.SetSelectionTextColor(const Value: TColor);
begin
  if (FSelectionTextColor <> Value) then
  begin
    FSelectionTextColor := Value;
    UpdateElement;
  end;
end;

procedure TCustomTableControl.SetSortIndex(const Value: integer);
begin
  FSortIndex := Value;
end;

procedure TCustomTableControl.SetSortIndicator(const ACol, ARow: integer;
  AIndicator: TGridSortIndicator);
var
  el: TJSElement;
  sid: string;

begin
  sid := Name + 'si'+ACol.ToString+'_'+ARow.ToString;

  el := document.getElementById(sid);

  if Assigned(el) then
  begin
    if AIndicator = siAscending then
    begin
      TJSHTMLElement(el).innerHTML := GlyphAscending;
      el['sort'] := 'up';
    end
    else
    begin
      TJSHTMLElement(el).innerHTML := GlyphDescending;
      el['sort'] := 'dn';
    end;
  end;
end;

procedure TCustomTableControl.SetColWidth(const ACol: integer; AWidth: integer);
var
  td,el: TJSHTMLElement;
  i: integer;
begin
  if (ACol < ColCount) and (ACol >= 0) then
  begin
    if AWidth > 0 then
    begin
      for i := 0 to RenderRowCount do
      begin
        td := nil;
        td := CellElements[ACol,i];
        if Assigned(td) then
        begin
          td.style.setProperty('width', AWidth.ToString+'px');
          td.style.setProperty('max-width', AWidth.ToString+'px');
          el := TJSHTMLElement(td.firstChild);
          if Assigned(el) and (el.tagName = 'DIV') then
          begin
            el.style.setProperty('width', AWidth.ToString+'px');
            el.style.setProperty('max-width', AWidth.ToString+'px');
          end;
        end;
      end;
    end;
  end;
end;

{$HINTS OFF}

function TCustomTableControl.GetColWidth(const ACol: integer): integer;
var
  td: TJSHTMLElement;
  w: integer;
begin
  Result := 0;

  if (ACol < ColCount) and (ACol >= 0) then
  begin
    td := CellElements[ACol,0];
    asm
      w = td.offsetWidth;
    end;

    Result := w;
  end;
end;

procedure TCustomTableControl.RenderGridBorders;
var
  i,j: integer;
  td: TJSHTMLElement;
  clr: string;
begin
  for i := 0 to ColCount - 1 do
    for j := 0 to RenderRowCount do
    begin
      td := CellElements[i,j];
      if Assigned(td) then
      begin
        if Options.CellBorders then
        begin
          clr := ColorToHTML(Options.CellBorderColor);
          asm
            td.style.borderRight = "1px solid " + clr;
            td.style.borderBottom = "1px solid " + clr;
          end;
        end
        else
        begin
          asm
            td.style.borderRight = "";
            td.style.borderBottom = "";
          end;
        end;
      end;
    end;
end;

procedure TCustomTableControl.LogData;
begin
  console.log(FData);
end;

procedure TCustomTableControl.InitCSSLibrary(ALibrary: TCSSLibrary);
begin
  if (ALibrary = cssBootstrap) then
  begin
    ElementFont := efCSS;

    ElementHeaderClassName := 'thead-light';
    ElementTableClassName := 'table table-striped table-bordered  table-hover';

    if not Assigned(FHeader) then
      FHeader := TTableControlHeader.Create(Self);

    FHeader.ButtonActiveElementClassName := 'btn btn-primary';
    FHeader.ButtonElementClassName := 'btn btn-light';
    FHeader.LinkActiveElementClassName := 'link-primary';
    FHeader.LinkElementClassName := 'link-secondary';
    FHeader.ListElementClassName := 'pagination';
    FHeader.ListLinkElementClassName := 'page-link';
    FHeader.ListItemElementClassName := 'page-item';
    FHeader.DropDownElementClassName := 'form-control';
    FHeader.InputElementClassName := 'form-control';

    if not Assigned(FFooter) then
      FFooter := TTableControlHeader.Create(Self);

    FFooter.ButtonActiveElementClassName := 'btn btn-primary';
    FFooter.ButtonElementClassName := 'btn btn-light';
    FFooter.LinkActiveElementClassName := 'link-primary';
    FFooter.LinkElementClassName := 'link-secondary';
    FFooter.ListElementClassName := 'pagination';
    FFooter.ListLinkElementClassName := 'page-link';
    FFooter.ListItemElementClassName := 'page-item';
    FFooter.DropDownElementClassName := 'form-control';
    FFooter.InputElementClassName := 'form-control';
  end;
end;

procedure TCustomTableControl.SetTableCell(ACol,AROw: integer; ACell: TJSHTMLElement;
  const Value: string);
var
  clr,td,cn: string;
  topcell: TJSHTMLElement;

  function IsEmail(s: string): boolean;
  var
    res: boolean;
  begin
    asm
      var re = /\S+@\S+\.\S+/;
      res = re.test(s);
    end;
    Result := res;
  end;

  function IsImgUrl(url: string): boolean;
  var
    res:boolean;
  begin
    asm
      res = /\.(jpg|jpeg|png|webp|avif|gif)$/.test(url)
    end;
    Result := res;
  end;

  function IsValidHttpUrl(s: string): boolean;
  begin
    asm
      try {
        url = new URL(s);
      } catch (_) {
        return false;
      }
      return url.protocol === "http:" || url.protocol === "https:";
    end;
  end;

  function IsNum(str: string): boolean;
  begin
    asm
      if (typeof str != "string") return false // we only process strings!
      return !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
        !isNaN(parseFloat(str)) // ...and ensure strings of whitespace fail
    end;
  end;

begin
  if (csDesigning in ComponentState) and (ElementTableClassName = '') and not Options.CellBorders then
  begin
    asm
      ACell.style.borderRight = "1px dashed gray";
      ACell.style.borderBottom = "1px dashed gray";
      ACell.innerHTML = "&nbsp;";
    end;
    Exit;
  end;

  if Options.AutoCellEmail and IsEmail(Value) then
    td := '<a href="mailto:'+Value+'">'+Value+'</a>'
  else
    if Options.AutoCellImage and IsImgURL(Value) then
    begin
      if Options.ImageWidth > 0 then
        td := '<img src="'+Value+'" width="'+ Options.ImageWidth.ToString+'px">'
      else
        td := '<img src="'+Value+'">';

      case Options.ImageAlign of
      taCenter: ACell['align'] := 'center';
      taRightJustify: ACell['align'] := 'right';
      end;

    end
    else
      if Options.AutoCellURL and IsValidHttpURL(Value) then
        td := '<a href="'+Value+'">'+Value+'</a>'
      else
      begin
        td := Value;
        if Options.AutoNumAlign and IsNum(Value) then
          ACell['align'] := 'right';
      end;

  if WordWrap then
    ACell.innerHTML := td
  else
    ACell.innerHTML := '<div style="overflow: hidden; text-overflow: ellipsis; white-space:nowrap;">'+td+'</div>';

//  ACell.innerHTML := td;

  if (ElementTableClassName = '') and Options.CellBorders then
  begin
    clr := ColorToHTML(Options.CellBorderColor);
    asm
      ACell.style.borderRight = "1px solid " + clr;
      ACell.style.borderBottom = "1px solid " + clr;
    end;
  end;

  cn := '';
  GetCellClassName(ACol,ARow, nil, Value, cn);
  if cn <> '' then
    ACell.setAttribute('class',cn);
end;


procedure TCustomTableControl.Sort(AColumn: integer;
  ADirection: TGridSortIndicator);
var
  colidx: integer;
  grid: TJSElement;
  asc,rh: boolean;
  fixrow: JSValue;
begin
  if not Assigned(FTable) then
    Exit;

  grid := TJSElement(FTable.children[1]); // get table TBODY

  asc := ADirection = siAscending;

//  if ColHeader then
//    colidx := AColumn - 1
//  else
    colidx := AColumn;

  if Paging.Enabled then
  begin
    rh := RowHeader;

    asm
      if (rh) fixrow = this.FData[0];

      const getCellValue = (tr, idx) => tr[idx];
      const comparer = (idx, asc) => (a, b) => ((v1, v2) =>
        v1 !== '' && v2 !== '' && !isNaN(v1) && !isNaN(v2) ? v1 - v2 : v1.toString().localeCompare(v2)
        )(getCellValue(asc ? a : b, idx), getCellValue(asc ? b : a, idx));

      var arr = Array.from(this.FData);
      if (rh) arr.shift();
      arr.sort(comparer(colidx, asc));
      if (rh) arr.splice(0,0,fixrow);
      this.FData = arr;
    end;

    SelectPage(Paging.ActivePage, true);
  end
  else
  begin

    asm
      const getCellValue = (tr, idx) => tr.children[idx].innerText || tr.children[idx].textContent;
      const comparer = (idx, asc) => (a, b) => ((v1, v2) =>
        v1 !== '' && v2 !== '' && !isNaN(v1) && !isNaN(v2) ? v1 - v2 : v1.toString().localeCompare(v2)
        )(getCellValue(asc ? a : b, idx), getCellValue(asc ? b : a, idx));

      const table = grid;

      Array.from(table.querySelectorAll('tr'))
          .sort(comparer(colidx, asc))
          .forEach(tr => table.appendChild(tr) );
    end;

  end;
end;
{$HINTS ON}

procedure TCustomTableControl.UpdateElement;
begin
  inherited;

  if IsUpdating then
    Exit;

  if Assigned(ElementHandle) then
  begin
    if Options.ScrollVertical then
      ElementHandle.style.setProperty('overflow-y','scroll')
    else
      ElementHandle.style.setProperty('height','');

    if (csDesigning in ComponentState) then
    begin
      ElementHandle.style.setProperty('width', IntToStr(Width) + 'px');
      ElementHandle.style.setProperty('height', IntToStr(Height) + 'px');
    end;

    // allow selection in labels
    ElementHandle.style.setProperty('user-select', '');

    // allow default caret on text
    if Cursor = crDefault then
      ElementHandle.style.setProperty('cursor', '');

    if (BorderStyle = bsSingle) and (BorderColor <> clNone) and (ElementClassName = '') and (ElementTableClassName = '') and not IsLinked then
      ElementHandle.style.setProperty('border', '1px ' + ColorToHTML(BorderColor)+' solid')
    else
      ElementHandle.style.setProperty('border', '');

    if (Color <> clNone) and (Color <> clWindow) then
      ElementHandle.style.setProperty('background-color', ColorToHTML(Color))
    else
      ElementHandle.style.setProperty('background-color', '');

    RenderGrid;
  end;
end;


{ TResponsiveGridItem }

constructor TResponsiveGridItem.Create(ACollection: TCollection);
begin
  inherited;
  FVisible := true;
end;

destructor TResponsiveGridItem.Destroy;
begin
  (Collection as TResponsiveGridItems).DoItemDelete(Index);
  inherited;
end;

{$HINTS OFF}
function TResponsiveGridItem.GetJSONElementValue(id: string): string;
var
  res: string;
  JO: JSValue;
begin
  JO := FJSONObject;
  asm
    res = JO[id];
  end;
  Result := res;
end;
{$HINTS ON}

procedure TResponsiveGridItem.SetHTML(const Value: string);
begin
  FHTML := Value;
  Changed(false);
end;

procedure TResponsiveGridItem.SetSelected(const Value: boolean);
var
  LStyleName: string;
begin
  FSelected := Value;

  LStyleName := (Collection as TResponsiveGridItems).Grid.GetStyleName;

  if FSelected then
    ElementHandle.classList.add(LStyleName+'_Selected')
  else
    ElementHandle.classList.remove(LStyleName+'_Selected');
end;

procedure TResponsiveGridItem.SetVisible(const Value: boolean);
begin
  if FVisible <> Value then
  begin
    FVisible := Value;
    if Assigned(ElementHandle) then
    begin
      if FVisible then
        ElementHandle.style.removeProperty('display')
      else
        ElementHandle.style.setProperty('display','none');
    end;
  end;
end;

{ TResponsiveGridItems }

function TResponsiveGridItems.Add: TResponsiveGridItem;
begin
  Result := TResponsiveGridItem(inherited Add);
  DoItemAdd(Result.Index);
end;

function TResponsiveGridItems.Add(HTML: string): TResponsiveGridItem;
begin
  Result := TResponsiveGridItem(inherited Add);
  Result.FHTML := HTML;
  DoItemAdd(Result.Index);
end;

procedure TResponsiveGridItems.Clear;
begin
  inherited Clear;
  DoItemClear;
end;

constructor TResponsiveGridItems.Create(AOwner: TComponent);
begin
  inherited Create(AOwner, TResponsiveGridItem);
  FGrid := TResponsiveGrid(AOwner);
end;

procedure TResponsiveGridItems.Delete(Index: integer);
begin
  inherited Delete(Index);
end;

procedure TResponsiveGridItems.DoItemAdd(Index: integer);
begin
  if Assigned(OnItemAdd) then
    OnItemAdd(Self, Index);
end;

procedure TResponsiveGridItems.DoItemClear;
begin
  if Assigned(OnItemClear) then
    OnItemClear(Self);
end;

procedure TResponsiveGridItems.DoItemDelete(Index: integer);
begin
  if Assigned(OnItemDelete) then
    OnItemDelete(Self, Index);
end;

procedure TResponsiveGridItems.DoItemInsert(Index: integer);
begin
  if Assigned(OnItemInsert) then
    OnItemInsert(Self, Index);
end;

procedure TResponsiveGridItems.DoItemUpdate(Index: integer);
begin
  if Assigned(OnItemUpdate) then
    OnItemUpdate(Self, Index);
end;

function TResponsiveGridItems.GetItem(Index: integer): TResponsiveGridItem;
begin
  Result := TResponsiveGridItem(inherited Items[Index]);
end;

function TResponsiveGridItems.Insert(Index: integer): TResponsiveGridItem;
begin
  Result := TResponsiveGridItem(inherited Insert(Index));
  DoItemInsert(Result.Index);
end;

procedure TResponsiveGridItems.SetItem(Index: integer;
  const Value: TResponsiveGridItem);
begin
  inherited Items[Index] := Value;
end;

procedure TResponsiveGridItems.Update(Item: TCollectionItem);
begin
  inherited;
  if Assigned(Item) then
    DoItemUpdate(Item.Index);
end;

{ TResponsiveGrid }

procedure TResponsiveGrid.BindElement;
begin
  inherited;
end;

constructor TResponsiveGrid.Create(AOwner: TComponent);
begin
  inherited;
end;

function TResponsiveGrid.CreateElement: TJSElement;
begin
  Result := document.createElement('DIV');
end;

procedure TResponsiveGrid.CreateInitialize;
begin
  inherited;

  FDesignTime := (csDesigning in ComponentState) and not
    ((csReading in Owner.ComponentState) or (csLoading in Owner.ComponentState));

  FListElement := nil;
  FItemIndex := -1;

  FOptions := TResponsiveGridOptions.Create;
  FOptions.OnChange := OptionsChanged;
  FItems := TResponsiveGridItems.Create(Self);
  FItems.PropName := 'Items';

  if FDesignTime then
  begin
    Items.Clear;
    Items.Add;
    Items.Add;
    Items.Add;
  end;

  FItems.OnItemAdd := HandleItemAdd;
  FItems.OnItemUpdate := HandleItemUpdate;
  FItems.OnItemDelete := HandleItemDelete;
  FItems.OnItemInsert := HandleItemInsert;
  FItems.OnItemClear := HandleItemClear;

  if (csDesigning in ComponentState) then
  begin
    Width := 400;
    Height := 300;
  end;

end;

procedure TResponsiveGrid.CreateControl;
begin
  inherited;
end;

destructor TResponsiveGrid.Destroy;
begin
  FOptions.Free;
  FItems.Free;
  inherited;
end;

function TResponsiveGrid.DoHttpAbort(Event: TEventListenerEvent): boolean;
begin
  if Assigned(OnHttpRequestError) then
    OnHttpRequestError(Self);
  Result := true;
end;

function TResponsiveGrid.DoHttpLoadCsv(Event: TEventListenerEvent): boolean;
var
  sl,arow,columns: TStringList;
  i,c: integer;
  itm: TResponsiveGridItem;
  ident,html, htmlvalue: string;
  req: TJSXMLHttpRequest;

begin
  req := TJSXMLHttpRequest(Event.target);

  if (req.status >= 200) and (req.status <= 208) then
  begin
    sl := TStringList.Create;
    sl.Text := req.responseText;

    arow := TStringList.Create;
    arow.StrictDelimiter := true;
    arow.Delimiter := FDelimiter;

    if sl.Count > 0 then
    begin
      columns := TStringList.Create;
      columns.StrictDelimiter := true;
      columns.Delimiter := FDelimiter;
      columns.DelimitedText := sl.Strings[0];

      for i := 0 to sl.Count - 1 do
      begin
        arow.DelimitedText := sl.Strings[i];

        if Options.ItemTemplate = '' then
        begin
          Items.Add.HTML := arow.CommaText;
        end
        else
        begin
          itm := Items.Add('&nbsp;');

          html := Options.ItemTemplate;

          DoItemGetTemplate(i, arow.CommaText ,html);

          ident := '(%ITEMINDEX%)';
          if pos(ident, html) > 0 then
          begin
            html := StringReplace(html, ident, inttostr(itm.Index), [rfReplaceAll]);
          end;

          for c := 0 to arow.Count - 1 do
          begin
            ident := '(%' + columns.Strings[c] + '%)';
            if Pos(ident, html) > 0 then
            begin
              htmlvalue := '';
              if c < arow.Count then
                htmlvalue := arow.Strings[c];

              DoItemGetFieldValue(itm.Index, columns.Strings[c], htmlvalue);
              html := StringReplace(html, ident, htmlvalue, [rfReplaceAll]);
            end;
          end;

          itm.HTML := html;

          DoItemCreated(itm.Index);
        end;
      end;

      columns.Free;
    end;

    arow.Free;
    sl.Free;
  end;

  Result := true;
end;

{$HINTS OFF}
function TResponsiveGrid.DoHttpLoadJson(Event: TEventListenerEvent): boolean;
var
  J: TJSObject;
  req: TJSXMLHttpRequest;

begin
  req := TJSXMLHttpRequest(Event.target);

  if (req.status >= 200) and (req.status <= 208) then
  begin
    Items.Clear;

    J := TJSJSON.parseObject(req.responseText);

    LoadFromJSON(J, FDataNode);

    if Assigned(OnHttpRequestSuccess) then
      OnHttpRequestSuccess(Self);
  end;

  Result := true;
end;

procedure TResponsiveGrid.DoHttpRequest(ARequest: TJSXMLHttpRequest);
var
  LRequestRec: TJSXMLHttpRequestRecord;
begin
  if Assigned(OnHttpRequest) then
  begin
    LRequestRec.req := ARequest;
    OnHttpRequest(Self, LRequestRec);
  end;
end;

{$HINTS ON}

procedure TResponsiveGrid.DoItemCreated(Index: integer);
begin
  if Assigned(OnItemCreated) then
    OnItemCreated(Self, Index);
end;

procedure TResponsiveGrid.DoItemGetFieldValue(Index: integer;
  AFieldName: string; var AValue: string);
begin
  if Assigned(OnItemGetFieldValue) then
    OnItemGetFieldValue(Self, Index, AFieldName, AValue);
end;

procedure TResponsiveGrid.DoItemGetTemplate(Index: integer; ItemData: string;
  var ATemplate: string);
begin
  if Assigned(OnItemGetTemplate) then
    OnItemGetTemplate(Self, Index, ItemData, ATemplate);
end;

procedure TResponsiveGrid.FontChanged;
begin
  inherited;
  UpdateElement;
end;

function TResponsiveGrid.GetStyleName: string;
begin
  if Assigned(Parent) then
    Result := Parent.Name + '_' + Name
  else
    Result := Name;
end;

function TResponsiveGrid.TargetIsParent(tgt,el: TJSElement): boolean;
var
  pe: TJSElement;
begin
  Result := false;
  pe := tgt;
  repeat
    if el = pe then
    begin
      Result := true;
      break;
    end;
    pe := pe.parentElement;

  until not Assigned(pe);
end;

function TResponsiveGrid.HandleDoClick(Event: TJSMouseEvent): Boolean;
var
  i: integer;
  el: TJSHTMLElement;
  LStyleName: string;
begin
  Result := inherited HandleDoClick(Event);

  LStyleName := GetStyleName;

  for i := 0 to Items.Count - 1 do
  begin
    el := TJSHTMLElement(ListElementHandle.children.Items[i]);
    if Assigned(el) then
    begin
      // search HTML DIV element in list that is clicked
      if TargetIsParent(Event.targetElement, el) then
      begin
        if Options.MultiSelect then
        begin
          if Items[i].Selected then
          begin
            el.classList.remove(LStyleName+'_Selected');
          end
          else
          begin
            el.classList.add(LStyleName+'_Selected');
          end;
          Items[i].FSelected := not Items[i].FSelected;
        end
        else
        begin
          FItemIndex := i;
          el.classList.add(LStyleName+'_Selected');
        end;
        HandleDoItemClick(i);
      end
      else
        if not Options.MultiSelect then
          el.classList.remove(LStyleName+'_Selected');
    end;
  end;
end;

function TResponsiveGrid.HandleDoDblClick(Event: TJSMouseEvent): Boolean;
var
  i: integer;
  el: TJSHTMLElement;
begin
  Result := inherited HandleDoClick(Event);

  for i := 0 to Items.Count - 1 do
  begin
    el := TJSHTMLElement(ListElementHandle.children.Items[i]);

    if TargetIsParent(Event.targetElement, el) then
    begin
      HandleDoItemDblClick(i);
    end;
  end;
end;

procedure TResponsiveGrid.HandleDoItemClick(Index: integer);
begin
  if Assigned(OnItemClick) then
    OnItemClick(Self, Index);
end;

procedure TResponsiveGrid.HandleDoItemDblClick(Index: integer);
begin
  if Assigned(OnItemDblClick) then
    OnItemDblClick(Self, Index);
end;

function TResponsiveGrid.HandleDoMouseEnter(Event: TJSMouseEvent): Boolean;
begin
  Result := inherited HandleDoMouseEnter(Event);
end;

procedure TResponsiveGrid.HandleItemAdd(Sender: TObject; Index: integer);
var
  el: TJSHTMLElement;
begin
  if Items.Items[Index].FHTML = '' then
    Items.Items[Index].FHTML := Options.ItemTemplate;

  el := TJSHTMLElement(document.createElement('DIV'));
  Items.Items[Index].FElementHandle := el;

  ListElementHandle.appendChild(el);

  // already load when it has no template data fields
  if pos('(%',Items.Items[Index].HTML) = 0  then
    el.innerHTML := Items.Items[Index].HTML;

  if Options.ItemClassName <> '' then
    el.setAttribute('class',Options.ItemClassName);
end;

procedure TResponsiveGrid.HandleItemClear(Sender: TObject);
var
  i: integer;
begin
  for i := ListElementHandle.childElementCount - 1 downto 0 do
  begin
    ListElementHandle.removeChild(ListElementHandle.children.items[0]);
  end;
end;

procedure TResponsiveGrid.HandleItemDelete(Sender: TObject; Index: integer);
begin
  if Assigned(ListElementHandle) then
  begin
    ListElementHandle.removeChild(ListElementHandle.children.Items[Index]);
  end;
end;

procedure TResponsiveGrid.HandleItemInsert(Sender: TObject; Index: integer);
var
  el: TJSHTMLElement;
begin
  Items.Items[Index].FHTML := Options.ItemTemplate;

  el := TJSHTMLElement(document.createElement('DIV'));
  Items.Items[Index].FElementHandle := el;

  if Items.Count <= 1 then
    ListElementHandle.appendChild(el)
  else
    ListElementHandle.insertBefore(el, ListElementHandle.children.Items[Index]);

  el.innerHTML := Items.Items[Index].HTML;
  if Options.ItemClassName <> '' then
    el.setAttribute('class',Options.ItemClassName);
end;

procedure TResponsiveGrid.HandleItemUpdate(Sender: TObject; Index: integer);
var
  el: TJSHTMLElement;
begin
  if Assigned(ListElementHandle) then
  begin
    el := TJSHTMLElement(ListElementHandle.children.Items[Index]);
    if not Assigned(el) then
    begin
      HandleItemInsert(Self, Index);
    end
    else
      el.innerHTML := Items.Items[Index].HTML;
  end;
end;

procedure TResponsiveGrid.InitCSSLibrary(ALibrary: TCSSLibrary);
begin
  if (ALibrary = cssBootstrap) then
  begin
    ElementFont := efCSS;
  end;
end;

function TResponsiveGrid.ItemByTag(ATag: integer): TResponsiveGridItem;
var
  i: integer;
begin
  Result := nil;

  for I := 0 to Items.Count - 1 do
  begin
    if Items[I].Tag = ATag then
    begin
      Result := Items[I];
      Break;
    end;
  end;
end;

function TResponsiveGrid.ListElementHandle: TJSElement;
begin
  if not Assigned(FListElement) then
  begin
    FListElement := TJSHTMLElement(document.createElement('DIV'));
    FListElement['width'] := '100%';
    ElementHandle.appendChild(FListElement);
    Result := FListElement;
  end
  else
    Result := FListElement;
end;

procedure TResponsiveGrid.Loaded;
begin
  inherited;
  RenderGrid;
end;

procedure TResponsiveGrid.LoadFromCSV(const AURL: string; Delimiter: char = ';');
begin
  FDelimiter := Delimiter;

  FReq := TJSXMLHttpRequest.new;
  FReq.addEventListener('load',@DoHttpLoadCsv);
  FReq.addEventListener('abort',@DoHttpAbort);
  FReq.open('GET', AURL);
  DoHttpRequest(FReq);
  FReq.send;
end;

procedure TResponsiveGrid.LoadFromJSON(const AURL: string; ADataNode: string);
begin
  FDataNode := ADataNode;
  FReq := TJSXMLHttpRequest.new;
  FReq.addEventListener('load',@DoHttpLoadJson);
  FReq.addEventListener('abort',@DoHttpAbort);
  FReq.open('GET', AURL);
  DoHttpRequest(FReq);
  FReq.send;
end;

procedure TResponsiveGrid.LoadFromJSON(ASJON: TJSObject; ADataNode: string = '');
var
  JO: JSValue;
  JA: TJSArray;
  r,c: integer;
  arow,ident,html, htmlvalue: string;
  sl: TStringList;
  itm: TResponsiveGridItem;

begin
  if ADataNode <> '' then
    JA := TJSArray(ASJON.Properties[ADataNode])
  else
    JA := TJSArray(ASJON);

  if Assigned(JA) then
  begin
    sl := TStringList.Create;

    //RowCount := JA.length +  FixedRows;

    for r := 0 to JA.length - 1 do
    begin
      // array element
      JO := JA.Elements[r];

      c := 0;
      arow := '';

      asm
        var x = undefined;
        for (x in JO) {
          arow += "|" + x + "=" + JO[x];
          c++;
        }
      end;

      Delete(arow, 1, 1);
      sl.StrictDelimiter := true;
      sl.Delimiter := '|';
      sl.DelimitedText := arow;

      itm := Items.Add;
      itm.JSONElement := JO;

      if Options.ItemTemplate = '' then
      begin
        itm.HTML := sl.CommaText;
      end
      else
      begin
        html := Options.ItemTemplate;

        DoItemGetTemplate(r, sl.CommaText ,html);

        ident := '(%ITEMINDEX%)';
        if pos(ident, html) > 0 then
        begin
          html := StringReplace(html, ident, inttostr(itm.Index), [rfReplaceAll]);
        end;

        for c := 0 to sl.Count - 1 do
        begin
          ident := '(%' + sl.Names[c] + '%)';

          if Pos(ident, html) > 0 then
          begin
            htmlvalue := sl.Values[sl.Names[c]];
            DoItemGetFieldValue(itm.Index, sl.Names[c], htmlvalue);
            html := StringReplace(html, ident, htmlvalue, [rfReplaceAll]);
          end;
        end;

        itm.HTML := html;

        DoItemCreated(itm.Index);
      end;
    end;

    sl.Free;
  end;
end;

procedure TResponsiveGrid.OptionsChanged(Sender: TObject);
begin
  UpdateElement;
end;

procedure TResponsiveGrid.RenderGrid;
var
  i: integer;
  el: TJSHTMLElement;
begin
  if (csDestroying in ComponentState) then
    Exit;

  if not Assigned(ListElementHandle) then
    Exit;

  // clear previous items
  while ListElementHandle.childElementCount > 0 do
  begin
    ListElementHandle.removeChild(ListElementHandle.firstChild);
  end;

  // refresh items
  for i := 0 to Items.Count - 1 do
  begin
    el := TJSHTMLElement(document.createElement('DIV'));
    el.style.setProperty('position', 'relative');
    Items[i].FElementHandle := el;
    ListElementHandle.appendChild(el);
    el.innerHTML := Items[i].HTML;
    if Options.ItemClassName <> '' then
      el.setAttribute('class',Options.ItemClassName);
  end;
end;

procedure TResponsiveGrid.SetItemIndex(const Value: integer);
var
  el: TJSHTMLElement;
  i: integer;
  LStyleName: string;
begin
  FItemIndex := Value;

  if (csLoading in ComponentState) then
    Exit;

  LStyleName := GetStyleName;

  for i := 0 to Items.Count - 1 do
  begin
    el := TJSHTMLElement(ListElementHandle.children.Items[i]);
    if (i = FItemIndex) then
    begin
      el.setAttribute('class',Options.ItemClassName + ' ' + LStyleName+'_Selected');
    end
    else
      el.setAttribute('class',Options.ItemClassName);
  end;
end;

procedure TResponsiveGrid.SetItems(const Value: TResponsiveGridItems);
begin
  FItems.Assign(Value);
end;

procedure TResponsiveGrid.SetOptions(const Value: TResponsiveGridOptions);
begin
  FOptions.Assign(Value);
end;

procedure TResponsiveGrid.UpdateElement;
const
  cssimportant = ' !important; ';
var
  s, fs: string;
  ITMWIDTHMIN: string;
  ITMGAP: string;
  ITMHEIGHT: string;
  itemstyle, itemstylehover, itemstyleselected: string;
  LStyleName: string;
  LTemplate: string;
begin
  inherited;

  if IsUpdating then
    Exit;

  if (csLoading in ComponentState) then
    Exit;

  if Assigned(ElementHandle) then
  begin
    ITMWIDTHMIN := IntToStr(Options.ItemWidthMin);
    ITMHEIGHT := IntToStr(Options.ItemHeight);
    ITMGAP := IntToStr(Options.ItemGap);

    LStyleName := GetStyleName;

    ListElementHandle.setAttribute('class', LStyleName);

    fs := '';

    if (ElementClassName = '') and (ElementFont = efProperty) then
      fs := CSSFont(Self.Font);

    itemstyle := '';
    itemstylehover := '';
    itemstyleselected := '';

    if Options.ItemColor <> clNone then
    begin
      itemstyle := 'background-color:'+ColorToHTML(Options.ItemColor)+';';
    end;

    if (csDesigning in ComponentState) and (Options.ItemBorderColor = clNone) then
        itemstyle := itemstyle + 'border: 1px dotted silver;'
    else if (Options.ItemBorderColor <> clNone) then
      itemstyle := itemstyle + 'border: 1px solid ' + ColorToHTML(Options.ItemBorderColor)+';';

    if Options.ItemPadding <> 0 then
      itemstyle := itemstyle +  'padding:' + IntToStr(Options.ItemPadding)+'px;';

    if Options.ItemHoverColor <> clNone then
      itemstylehover := 'background-color:' + ColorToHTML(Options.ItemHoverColor) + cssimportant;

    if Options.ItemHoverBorderColor <> clNone then
      itemstylehover := itemstylehover + 'border: 1px solid '+ColorToHTML(Options.ItemHoverBorderColor) + cssimportant;

    if Options.ItemSelectedColor <> clNone then
      itemstyleselected := 'background-color:' + ColorToHTML(Options.ItemSelectedColor) + cssimportant;

    if Options.ItemSelectedBorderColor <> clNone then
      itemstyleselected := itemstyleselected + 'border: 1px solid '+ColorToHTML(Options.ItemSelectedBorderColor)+';';

    if Options.ItemSelectedTextColor <> clNone then
      itemstyleselected := itemstyleselected + 'color:'+ColorToHTML(Options.ItemSelectedTextColor)+';';

    if Options.ScrollVertical then
      ElementHandle.style.removeProperty('overflow-y')
    else
      ElementHandle.style.setProperty('overflow-y','hidden');

    if Options.Order = toColumn then
      LTemplate := 'grid-template-columns'
    else
    begin
      LTemplate := 'grid-auto-flow: column; height: ' + Height.ToString + 'px; grid-template-rows';
      ITMWIDTHMIN := IntToStr(Options.ItemHeight);
      ElementHandle.style.setProperty('overflow-x','auto');
    end;

    s := '.'+LStyleName+' {'+
       'display: grid; ' + LTemplate + ': repeat(auto-fill, minmax('+ ITMWIDTHMIN +'px, 1fr));'+
       ' grid-gap: '+ ITMGAP + 'px;'+
       '}' + #13
       + '.'+LStyleName+' > div { ' + itemstyle +'height: '+ ITMHEIGHT + 'px; ' + fs +' }'+#13
       + '.'+LStyleName+' > div:hover {' + itemstylehover + ' '+ fs + ' }'+#13
       + '.'+LStyleName+'_Selected {' + itemstyleselected + 'height: '+ ITMHEIGHT + 'px;' + fs + '}'+#13;

    AddInstanceStyle(LStyleName+'_style', s);
  end;

  RenderGrid;
end;

{ TResponsiveGridOptions }

procedure TResponsiveGridOptions.Assign(Source: TPersistent);
begin
  if (Source is TResponsiveGridOptions) then
  begin
    FItemWidthMin := (Source as TResponsiveGridOptions).ItemWidthMin;
    FItemHeight := (Source as TResponsiveGridOptions).ItemHeight;
    FMultiSelect := (Source as TResponsiveGridOptions).MultiSelect;
    FOrder := (Source as TResponsiveGridOptions).Order;
  end;
end;

procedure TResponsiveGridOptions.Changed;
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

constructor TResponsiveGridOptions.Create;
begin
  inherited;
  FItemWidthMin := 100;
  FItemHeight := 100;
  FItemGap := 10;
  FScrollVertical := true;
  FItemColor := clNone;
  FItemBorderColor := clNone;
  FItemHoverColor := clNone;
  FItemHoverBorderColor := clNone;
  FItemSelectedColor := clNone;
  FItemSelectedBorderColor := clNone;
  FItemSelectedTextColor := clNone;
  FOrder := toColumn;
end;

procedure TResponsiveGridOptions.SetItemBorderColor(const Value: TColor);
begin
  if (FItemBorderColor <> Value) then
  begin
    FItemBorderColor := Value;
    Changed;
  end;
end;

procedure TResponsiveGridOptions.SetItemColor(const Value: TColor);
begin
  if (FItemColor <> Value) then
  begin
    FItemColor := Value;
    Changed;
  end;
end;

procedure TResponsiveGridOptions.SetItemGap(const Value: integer);
begin
  if (FItemGap <> Value) then
  begin
    FItemGap := Value;
    Changed;
  end;
end;

procedure TResponsiveGridOptions.SetItemHeight(const Value: integer);
begin
  if (FItemHeight <> Value) then
  begin
    FItemHeight := Value;
    Changed;
  end;
end;

procedure TResponsiveGridOptions.SetItemHoverBorderColor(const Value: TColor);
begin
  if (FItemHoverBorderColor <> Value) then
  begin
    FItemHoverBorderColor := Value;
    Changed;
  end;
end;

procedure TResponsiveGridOptions.SetItemHoverColor(const Value: TColor);
begin
  if (FItemHoverColor <> Value) then
  begin
    FItemHoverColor := Value;
    Changed;
  end;
end;

procedure TResponsiveGridOptions.SetItemPadding(const Value: integer);
begin
  if (FItemPadding <> Value) then
  begin
    FItemPadding := Value;
    Changed;
  end;
end;

procedure TResponsiveGridOptions.SetItemSelectedBorderColor(
  const Value: TColor);
begin
  if FItemSelectedBorderColor <> Value then
  begin
    FItemSelectedBorderColor := Value;
    Changed;
  end;
end;

procedure TResponsiveGridOptions.SetItemSelectedColor(const Value: TColor);
begin
  if (FItemSelectedColor <> Value) then
  begin
    FItemSelectedColor := Value;
    Changed;
  end;
end;

procedure TResponsiveGridOptions.SetItemSelectedTextColor(const Value: TColor);
begin
  if (FItemSelectedTextColor <> Value) then
  begin
    FItemSelectedTextColor := Value;
    Changed;
  end;
end;

procedure TResponsiveGridOptions.SetItemTemplate(const Value: string);
begin
  if (FItemTemplate <> Value) then
  begin
    FItemTemplate := Value;
    Changed;
  end;
end;

procedure TResponsiveGridOptions.SetItemWidthMin(const Value: integer);
begin
  if (FItemWidthMin <> Value) then
  begin
    FItemWidthMin := Value;
    Changed;
  end;
end;

procedure TResponsiveGridOptions.SetScrollVertical(const Value: boolean);
begin
  if (FScrollVertical <> Value) then
  begin
    FScrollVertical := Value;
    Changed;
  end;
end;

{$HINTS ON}

{ TTableControlOptions }

procedure TTableControlOptions.Assign(Source: TPersistent);
begin
  if (Source is TTableControlOptions) then
  begin
    FScrollVertical := (Source as TTableControlOptions).ScrollVertical;
    FResizeColumns := (Source as TTableControlOptions).ResizeColumns;
    FAutoCellURL := (Source as TTableControlOptions).AutoCellURL;
    FAutoCellEmail := (Source as TTableControlOptions).AutoCellEmail;
    FAutoCellImage := (Source as TTableControlOptions).AutoCellImage;
    FAutoNumAlign := (Source as TTableControlOptions).AutoNumAlign;
    FCellBorders := (Source as TTableControlOptions).CellBorders;
    FCellBorderColor := (Source as TTableControlOptions).CellBorderColor;
    FImageWidth := (Source as TTableControlOptions).ImageWidth;
    FImageAlign := (Source as TTableControlOptions).ImageAlign;
  end;
end;

constructor TTableControlOptions.Create(AOwner: TCustomTableControl);
begin
  inherited;
  FOwner := AOwner;
  FCellBorderColor := clSilver;
  FImageAlign := taLeftJustify;
  FImageWidth := 0;
end;

procedure TTableControlOptions.SetCellBorderColor(const Value: TColor);
begin
  FCellBorderColor := Value;
  FOwner.RenderGridBorders;
end;

procedure TTableControlOptions.SetCellBorders(const Value: boolean);
begin
  FCellBorders := Value;
  FOwner.RenderGridBorders;
end;

procedure TTableControlOptions.SetResizeColumns(const Value: boolean);
begin
  FResizeColumns := Value;
  FOwner.EnableColResize;
end;

procedure TTableControlOptions.SetScrollVertical(const Value: boolean);
begin
  FScrollVertical := Value;
  FOwner.UpdateElement;
end;

{ TGridRange }

procedure TGridRange.Assign(Source: TPersistent);
begin
  if (Source is TGridRange) then
  begin
    FMax := (Source as TGridRange).Max;
    FMin := (Source as TGridRange).Min;
    FStep := (Source as TGridRange).Step;
  end;
end;

constructor TGridRange.Create;
begin
  inherited;
  FMin := 0;
  FMax := 100;
  FStep := 1;
end;

{ TTableControlHeader }

procedure TTableControlHeader.Assign(Source: TPersistent);
begin
  if (Source is TTableControlHeader) then
  begin
    FCaption := (Source as TTableControlHeader).Caption;
    FVisible := (Source as TTableControlHeader).Visible;
    FPager := (Source as TTableControlHeader).Pager;
    FPagerAlign := (Source as TTableControlHeader).PagerAlign;
    FButtonActiveElementClassName := (Source as TTableControlHeader).ButtonActiveElementClassName;
    FButtonElementClassName := (Source as TTableControlHeader).ButtonElementClassName;
    FLinkActiveElementClassName := (Source as TTableControlHeader).LinkActiveElementClassName;
    FLinkElementClassName := (Source as TTableControlHeader).LinkElementClassName;
    FDropDownElementClassName := (Source as TTableControlHeader).DropDownElementClassName;
    FInputElementClassName := (Source as TTableControlHeader).InputElementClassName;
    FFilterColumn := (Source as TTableControlHeader).FilterColumn;
    FFilter := (Source as TTableControlHeader).Filter;
    FSearch := (Source as TTableControlHeader).Search;
    FListElementClassName := (Source as TTableControlHeader).ListElementClassName;
    FListItemElementClassName := (Source as TTableControlHeader).ListItemElementClassName;
    FListLinkElementClassName := (Source as TTableControlHeader).ListLinkElementClassName;
  end;
end;

procedure TTableControlHeader.Changed;
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

constructor TTableControlHeader.Create(AOwner: TCustomTableControl);
begin
  inherited Create;
  FOwner := AOwner;
  FPagerElement := nil;
  FPagerAlign := paRight;
  FPageEventPtr := @HandlePagerClick;
  FSearchEventPtr := @HandleSearchClick;
  FFilterEventPtr := @HandleFilterClick;
end;

function TTableControlHeader.HandleFilterClick(Event: TJSInputEvent): Boolean;
var
  cond: string;
begin
  asm
    cond = this.FFilterInput.value;
  end;
  if (cond = '') or (cond <> FFilterValue) then
    FOwner.RemoveFilter;

  if (cond <> '') then
  begin
    FFilterValue := cond;
    FOwner.SetFilter(FilterColumn, FFilterValue);
  end;
  Result := true;
end;

function TTableControlHeader.HandlePagerClick(Event: TJSInputEvent): Boolean;
var
  idx: integer;
  s: string;
begin
  Event.preventDefault;
  Event.stopPropagation;

  Result := true;

  if Pager = tpDropDown then
  begin
    asm
      idx = this.FPagerElement.selectedIndex;
    end;
  end
  else
  if Pager = tpPrevNext then
  begin
    s := TJSElement(Event.target).getAttribute('data');
    idx := StrToInt(s);

    case idx of
    0: if FOwner.Paging.ActivePage > 0 then
         idx := 0
       else
         Exit;
    1: if FOwner.Paging.ActivePage > 0 then
         idx := FOwner.Paging.ActivePage - 1
       else
         Exit;
    2: if FOwner.Paging.ActivePage < FOwner.Paging.PageCount - 1 then
         idx := FOwner.Paging.ActivePage + 1
       else
         Exit;
    3: if FOwner.Paging.ActivePage < FOwner.Paging.PageCount - 1 then
         idx := FOwner.Paging.PageCount - 1
       else
         Exit;
    end;
  end
  else
  begin
    s := TJSElement(Event.target).getAttribute('data');
    idx := StrToInt(s);
  end;

  FOwner.DoSelectPage(idx);
end;

function TTableControlHeader.HandleSearchClick(Event: TJSInputEvent): Boolean;
var
  cond: string;
begin
  asm
    cond = this.FSearchInput.value;
  end;

  if (cond <> '') and (cond <> FSearchValue) then
  begin
    FSearchValue := cond;
    FOwner.FindCell(FSearchValue);
  end
  else
    FOwner.FindNext;
//FindCell(Condition: string; CaseSensitive: boolean = true; AllCells: boolean = false): TGridCoord;

  Result := true;
end;

procedure TTableControlHeader.SetCaption(const Value: string);
begin
  if FCaption <> Value then
  begin
    FCaption := Value;
    FOwner.RenderGrid;
  end;
end;

procedure TTableControlHeader.SetPager(const Value: TTablePager);
begin
  if (FPager <> Value) then
  begin
    FPager := Value;
    Update(FOwner.Paging.Size, FOwner.RowCount, FOwner.Paging.Index);
  end;
end;

procedure TTableControlHeader.SetPagerAlign(const Value: TTablePagerAlign);
begin
  if (FPagerAlign <> Value) then
  begin
    FPagerAlign := Value;
    FOwner.ReRenderGrid;
  end;
end;

procedure TTableControlHeader.SetVisible(const Value: boolean);
begin
  if FVisible <> Value then
  begin
    FVisible := Value;
    FOwner.ReRenderGrid;
  end;
end;

procedure TTableControlHeader.Update(PageSize, RowCount, ActivePage: integer);
var
  opt: TJSHTMLOptionElement;
  lnk,itm,btn: TJSHTMLElement;
  i,numpages: integer;
  trc: integer;
begin
  if not Assigned(FElement) then
    Exit;

  if Assigned(FSearchElement) then
  begin
    FSearchElement.parentElement.removeChild(FSearchElement);
    FSearchElement := nil;
  end;
  if Assigned(FFilterElement) then
  begin
    FFilterElement.parentElement.removeChild(FFilterElement);
    FFilterElement := nil;
  end;

  if Search then
  begin
    FSearchElement := TJSHTMLElement(document.createElement('DIV'));
    FSearchElement.style.setProperty('float','left');
    FSearchInput := TJSHTMLElement(document.createElement('INPUT'));
    if InputElementClassName <> '' then
      FSearchInput['class'] := InputElementClassName;
    FSearchInput.style.setProperty('display','inline');
    FSearchInput.style.setProperty('width','auto');

    btn := TJSHTMLElement(document.createElement('IMG'));
    btn['src'] := ICON_SEARCH;
    btn.style.setProperty('vertical-align','middle');
    btn.style.setProperty('margin-right','10px');
    FSearchElement.appendChild(FSearchInput);
    FSearchElement.appendChild(btn);
    FElement.appendChild(FSearchElement);
    btn.addEventListener('click',FSearchEventPtr);
    TJSHTMLInputElement(FSearchInput).value := FSearchValue;
  end;

  if Filter then
  begin
    FFilterElement := TJSHTMLElement(document.createElement('DIV'));
    FFilterElement.style.setProperty('float','left');
    FFilterInput := TJSHTMLElement(document.createElement('INPUT'));
    if InputElementClassName <> '' then
      FFilterInput['class'] := InputElementClassName;
    FFilterInput.style.setProperty('display','inline');
    FFilterInput.style.setProperty('width','auto');

    btn := TJSHTMLElement(document.createElement('IMG'));
    btn['src'] := ICON_FILTER;
    btn.style.setProperty('vertical-align','middle');
    btn.style.setProperty('margin-right','10px');
    FFilterElement.appendChild(FFilterInput);
    FFilterElement.appendChild(btn);
    FElement.appendChild(FFilterElement);
    btn.addEventListener('click',FFilterEventPtr);
    TJSHTMLInputElement(FFilterInput).value := FFilterValue;
  end;

  trc := 0;
  if FOwner.RowHeader then
    inc(trc);

  numpages := (RowCount - trc) div PageSize;

  if (RowCount - trc) mod PageSize > 0 then
    inc(numpages);

  FOwner.Paging.FPageCount := numpages;

  if Assigned(FPagerElement) then
  begin
    FPagerElement.parentElement.removeChild(FPagerElement);
    FPagerElement := nil;
  end;

  if not FOwner.Paging.Enabled then
    Exit;

  if FPager = tpDropDown then
  begin
    FPagerElement := TJSHTMLElement(document.createElement('SELECT'));

    if DropDownElementClassName <> '' then
      FPagerElement['class'] := DropDownElementClassName;

    FPagerElement.addEventListener('change',FPageEventPtr);

    for i := 0 to numpages - 1 do
    begin
      opt := TJSHTMLOptionElement(document.createElement('option'));
      opt.text := (i + 1).ToString;
      opt['data'] := i.ToString;
      if i = ActivePage then
        opt['selected'] := 'selected';

      TJSHTMLSelectElement(FPagerElement).add(opt);
    end;

    case FPagerAlign of
    paRight: FPagerElement.style.setProperty('float','right');
    paLeft: FPagerElement.style.setProperty('float','left');
    paCenter:
      begin
        FPagerElement.style.removeProperty('float');
        FPagerElement.style.setProperty('max-width','content');
        FPagerElement.style.setProperty('margin-left','auto');
        FPagerElement.style.setProperty('margin-right','auto');
      end;
    end;

    FPagerElement.style.setProperty('display','inline');
    FPagerElement.style.setProperty('width','auto');
    FElement.appendChild(FPagerElement);
  end;

  if FPager = tpLink then
  begin
    FPagerElement := TJSHTMLElement(document.createElement('DIV'));

    for i := 0 to numpages - 1 do
    begin
      lnk := TJSHTMLOptionElement(document.createElement('A'));
      lnk.innerHTML := (i + 1).ToString;
      lnk['href'] := '#';
      if LinkElementClassName <> '' then
      begin

        if (i = ActivePage) and (LinkActiveElementClassName <> '') then
          lnk['class'] := LinkActiveElementClassName
        else
          lnk['class'] := LinkElementClassName;
      end;
      lnk.style.setProperty('margin-right','4px');
      lnk.style.setProperty('display','inline');
      lnk['data'] := i.ToString;

      lnk.addEventListener('click',FPageEventPtr);
      FPagerElement.appendChild(lnk);
    end;

    case FPagerAlign of
    paRight: FPagerElement.style.setProperty('float','right');
    paLeft: FPagerElement.style.setProperty('float','left');
    paCenter:
      begin
        FPagerElement.style.removeProperty('float');
        FPagerElement.style.setProperty('max-width','content');
        FPagerElement.style.setProperty('margin-left','auto');
        FPagerElement.style.setProperty('margin-right','auto');
      end;
    end;
    FElement.appendChild(FPagerElement);
  end;

  if FPager = tpButton then
  begin
    FPagerElement := TJSHTMLElement(document.createElement('DIV'));

    for i := 0 to numpages - 1 do
    begin
      lnk := TJSHTMLOptionElement(document.createElement('BUTTON'));
      lnk.innerHTML := (i + 1).ToString;
      if ButtonElementClassName <> '' then
      begin
        if (i = ActivePage) and (ButtonActiveElementClassName <> '') then
          lnk['class'] := ButtonActiveElementClassName
        else
          lnk['class'] := ButtonElementClassName;
      end;
      lnk['data'] := i.ToString;
      lnk.addEventListener('click',FPageEventPtr);
      lnk.style.setProperty('margin-right','4px');
      FPagerElement.appendChild(lnk);
    end;

    case FPagerAlign of
    paRight: FPagerElement.style.setProperty('float','right');
    paLeft: FPagerElement.style.setProperty('float','left');
    paCenter:
      begin
        FPagerElement.style.removeProperty('float');
        FPagerElement.style.setProperty('max-width','content');
        FPagerElement.style.setProperty('margin-left','auto');
        FPagerElement.style.setProperty('margin-right','auto');
      end;
    end;

    FElement.appendChild(FPagerElement);
  end;

  if FPager = tpPrevNext then
  begin
    FPagerElement := TJSHTMLElement(document.createElement('DIV'));

    lnk := TJSHTMLOptionElement(document.createElement('BUTTON'));
    lnk.innerHTML := '&#xab;';
    lnk['data'] := '0';
      if ButtonElementClassName <> '' then
        lnk['class'] := ButtonElementClassName;
    lnk.addEventListener('click',FPageEventPtr);
    lnk.style.setProperty('margin-right','4px');

    if FOwner.Paging.ActivePage = 0 then
      lnk.setAttribute('disabled','');

    FPagerElement.appendChild(lnk);

    lnk := TJSHTMLOptionElement(document.createElement('BUTTON'));
    lnk.innerHTML := '&#x2039';
    lnk['data'] := '1';
      if ButtonElementClassName <> '' then
        lnk['class'] := ButtonElementClassName;
    lnk.addEventListener('click',FPageEventPtr);
    lnk.style.setProperty('margin-right','4px');

    if FOwner.Paging.ActivePage = 0 then
      lnk.setAttribute('disabled','');

    FPagerElement.appendChild(lnk);

    lnk := TJSHTMLOptionElement(document.createElement('BUTTON'));
    lnk.innerHTML := '&#x203A;';
    lnk['data'] := '2';
      if ButtonElementClassName <> '' then
        lnk['class'] := ButtonElementClassName;
    lnk.addEventListener('click',FPageEventPtr);
    lnk.style.setProperty('margin-right','4px');

    if FOwner.Paging.ActivePage = FOwner.Paging.PageCount then
      lnk.setAttribute('disabled','');

    FPagerElement.appendChild(lnk);

    lnk := TJSHTMLOptionElement(document.createElement('BUTTON'));
    lnk.innerHTML := '&#xbb;';
    lnk['data'] := '3';
    lnk.addEventListener('click',FPageEventPtr);
      if ButtonElementClassName <> '' then
        lnk['class'] := ButtonElementClassName;
    lnk.style.setProperty('margin-right','4px');
    FPagerElement.appendChild(lnk);
    case FPagerAlign of
    paRight: FPagerElement.style.setProperty('float','right');
    paLeft: FPagerElement.style.setProperty('float','left');
    paCenter:
      begin
        FPagerElement.style.removeProperty('float');
        FPagerElement.style.setProperty('max-width','content');
        FPagerElement.style.setProperty('margin-left','auto');
        FPagerElement.style.setProperty('margin-right','auto');
      end;
    end;


    if FOwner.Paging.ActivePage = FOwner.Paging.PageCount then
      lnk.setAttribute('disabled','');

    FElement.appendChild(FPagerElement);
  end;


  if FPager = tpList then
  begin
    FPagerElement := TJSHTMLElement(document.createElement('UL'));

    if ListElementClassName <> '' then
      FPagerElement['class'] := ListElementClassName;

    FPagerElement.style.setProperty('margin','0px');

    for i := 0 to numpages - 1 do
    begin
      itm := TJSHTMLOptionElement(document.createElement('LI'));
      if ListItemElementClassName <> '' then
      begin
        if i = ActivePage then
          itm['class'] := ListItemElementClassName + ' active'
        else
          itm['class'] := ListItemElementClassName;
      end;

      lnk := TJSHTMLOptionElement(document.createElement('A'));
      lnk.innerHTML := (i + 1).ToString;
      lnk['href'] := '#';
      lnk['data'] := i.ToString;
      lnk.addEventListener('click',FPageEventPtr);

      if ListLinkElementClassName <> '' then
        lnk['class'] := ListLinkElementClassName;

      itm.appendChild(lnk);

      FPagerElement.appendChild(itm);
    end;

    case FPagerAlign of
    paRight: FPagerElement.style.setProperty('float','right');
    paLeft: FPagerElement.style.setProperty('float','left');
    paCenter:
      begin
        FPagerElement.style.removeProperty('float');
        FPagerElement.style.setProperty('max-width','content');
        FPagerElement.style.setProperty('margin-left','auto');
        FPagerElement.style.setProperty('margin-right','auto');
      end;
    end;

    FElement.appendChild(FPagerElement);
  end;
end;

{ TTableControlPaging }

procedure TTableControlPaging.Assign(Source: TPersistent);
begin
  if (Source is TTableControlPaging) then
  begin
    FEnabled := (Source as TTableControlPaging).Enabled;
    FSize := (Source as TTableControlPaging).Size;
    FIndex := (Source as TTableControlPaging).Index;
  end;
end;

constructor TTableControlPaging.Create(AOwner: TCustomTableControl);
begin
  inherited Create;
  FOwner := AOwner;
  FSize := 10;
  FIndex := 0;
end;

procedure TTableControlPaging.SetEnabled(const Value: boolean);
var
  rh: integer;
begin
  if FEnabled <> Value then
  begin
    FEnabled := Value;

    rh := 0;

    if FOwner.RowHeader then
      rh := 1;

    if FEnabled then
      FOwner.RowCountChanged(FSize + rh - FOwner.RowCount)
    else
      FOwner.RowCountChanged(FOwner.RowCount - FSize - rh);

    FOwner.ReRenderGrid;
//    FOwner.GridChanged;
  end;
end;

procedure TTableControlPaging.SetIndex(const Value: integer);
begin
  if (FIndex <> Value) and (Value >= 0) then
  begin
    if FOwner.Paging.Size <> 0 then
      if not (Value < FOwner.RowCount div FOwner.Paging.Size) then
        Exit;

    FActivePage := Value;
    FIndex := Value;
    FOwner.DoSelectPage(FIndex);
  end;
end;

procedure TTableControlPaging.SetSize(const Value: integer);
var
  FOldSize: integer;
begin
  if (FSize <> Value) then
  begin
    FOldSize := FSize;
    FSize := Value;
    FOwner.RowCountChanged(FSize - FOldSize);
    FOwner.GridChanged;
  end;
end;

end.
